import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
// Sistema de diseño de la suite Dimo: los tokens de color, el gradiente de marca
// y las piezas comunes. Este portal usaba estilos propios y se veía distinto al
// resto de apps del grupo; ahora comparte lenguaje visual con ellas.
import { C, GRAD } from '../ui/theme.js';
import { EmptyState } from '../ui/ui.jsx';

// ── Formas del sistema de diseño ──────────────────────────────────────────
// Solo presentación: mismos datos, misma disposición. Radios 10 en botones y
// campos, 12 en tarjetas, 999 en píldoras (igual que Auto Leads y Email Leads).
const pildora = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
  padding: '3px 10px', borderRadius: 999, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px',
  color, background: `${color}18`, border: `1px solid ${color}44`,
});
const botonPrimario = {
  background: C.brand, color: '#fff', border: 'none', borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
  boxShadow: '0 3px 10px rgba(0,81,135,.28)',
};
const botonSecundario = {
  background: '#fff', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
};
const tarjeta = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12 };
const campo = {
  border: `1.5px solid ${C.line}`, borderRadius: 10, background: '#fff',
  fontSize: '13.5px', fontFamily: 'inherit', padding: '9px 12px', color: C.ink, boxSizing: 'border-box',
};
// Tabla: cabecera gris clara en versalitas y filas separadas por una línea fina.
const celdaTitulo = {
  padding: '9px 12px', textAlign: 'left', background: '#f7f8fa', color: C.muted,
  fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase',
  borderBottom: `1px solid ${C.line}`,
};
const SEPARADOR_FILA = '1px solid #eaecef';
// Etiqueta pequeña de las tarjetas de cifra (misma forma que StatCard).
const etiquetaCifra = { fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px', color: C.muted, textTransform: 'uppercase' };
// Fondo y tarjeta de los modales, iguales en toda la suite.
const velo = { position: 'fixed', inset: 0, background: 'rgba(15,28,43,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 };
const tarjetaModal = { background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(15,42,70,0.35)', overflow: 'hidden' };

// ==================== SUPABASE CONFIG ====================
const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

// Cliente Supabase simple
const supabase = {
  from: (table) => ({
    select: async (columns = '*') => {
      const url = `${SUPABASE_URL}/rest/v1/${table}?select=${columns}`;
      try {
        const response = await fetch(url, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    update: (record) => ({
      eq: async (column, value) => {
        const url = `${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}`;
        try {
          const response = await fetch(url, {
            method: 'PATCH',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(record)
          });
          if (!response.ok) throw new Error(`Error ${response.status}`);
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { data: null, error };
        }
      }
    })
  })
};

// ==================== SUPABASE AUTH ====================
const supabaseAuth = {
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error_description || 'Credenciales incorrectas' };
      }
      
      const data = await response.json();
      localStorage.setItem('sb-access-token', data.access_token);
      localStorage.setItem('sb-refresh-token', data.refresh_token);
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  signOut: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (token) {
      try {
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (e) {}
    }
    localStorage.removeItem('sb-access-token');
    localStorage.removeItem('sb-refresh-token');
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (!token) return null;
    
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }
};

// Buscar comercial por auth_user_id
const findComercialByAuthId = async (authUserId) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/comerciales?auth_user_id=eq.${authUserId}&select=*`;
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.length > 0 ? data[0] : null;
  } catch {
    return null;
  }
};

// Estados y colores
// Colores de los estados, tomados de la paleta de la suite: un solo verde
// (Vendido), un solo ámbar (Citado) y un solo rojo (Cancelado). Los estados y
// su recorrido no cambian, solo con qué color se pintan.
const ESTADOS = {
  'Pendiente': { color: C.gris, textColor: '#fff', icon: '⏳' },
  'Contactado': { color: '#1a9cd4', textColor: '#fff', icon: '📲' },
  'Atendido': { color: C.brand, textColor: '#fff', icon: '📞' },
  'Citado': { color: C.naranja, textColor: '#fff', icon: '📅' },
  'Ofertado': { color: C.morado, textColor: '#fff', icon: '💰' },
  'Duplicado': { color: C.faint, textColor: '#fff', icon: '🔁' },
  'Cancelado': { color: C.rojo, textColor: '#fff', icon: '❌' },
  'Vendido': { color: C.verde, textColor: '#fff', icon: '✅' }
};

// Motivos de no compra — la MISMA lista que usa Recepción (panel-recepcion,
// MOTIVOS_CANCELACION), petición Adriana 13/09: "solo las opciones de descarte
// que tengo yo, así en los informes salen siempre las mismas y no miles".
const MOTIVOS_NO_COMPRA = [
  'Pospone compra',
  'Compra en competencia',
  'Imposible contactar',
  'Tras contactar, ya no contesta',
  'Por precio',
  'Por ubicación',
  'No tenemos lo que busca',
  'No ha pedido información',
  'Financiera denegada',
  'Vender su coche',
  'Compra otra marca dentro del grupo'
];
// Al editar un lead antiguo cuyo motivo ya no está en la lista, se conserva como
// opción para no perderlo al guardar.
const motivosConActual = (actual) => (actual && !MOTIVOS_NO_COMPRA.includes(actual) ? [actual, ...MOTIVOS_NO_COMPRA] : MOTIVOS_NO_COMPRA);

// ── Dimo Phone: click-to-call desde el CRM ──────────────────
// En el ordenador, "Llamar" deja la marcación en dimo_llamadas_pendientes y la
// app Dimo Phone del móvil vinculado (abierta) la recoge en ~10 s y llama.
// En el propio móvil se sigue usando tel: (marcación nativa directa).
let dimoUsuarioActual = null; // { email, nombre } — se actualiza al iniciar sesión
const dimoSetUsuario = (comercial) => {
  dimoUsuarioActual = comercial?.email
    ? { email: String(comercial.email).trim().toLowerCase(), nombre: `${comercial.nombre || ''} ${comercial.apellidos || ''}`.trim() }
    : null;
};
const dimoToastCall = (texto, ok = true) => {
  try {
    const d = document.createElement('div');
    d.textContent = texto;
    d.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:99999;background:' + (ok ? '#1f2a37' : C.rojo) + ';color:#fff;padding:12px 18px;border-radius:10px;font-size:14px;box-shadow:0 8px 24px rgba(0,0,0,0.3);max-width:340px;';
    document.body.appendChild(d);
    setTimeout(() => d.remove(), 4500);
  } catch (e) { /* noop */ }
};
const esMovil = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
async function dimoLlamarDesdeCrm(telefono, lead = null) {
  const numero = String(telefono || '').trim();
  if (!numero) return;
  // En el móvil: marcación nativa de toda la vida
  if (esMovil() || !dimoUsuarioActual?.email) {
    window.location.href = `tel:${numero}`;
    return;
  }
  const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/dimo_phone_dispositivos?estado=eq.vinculado&user_email=eq.${encodeURIComponent(dimoUsuarioActual.email)}&order=vinculado_en.desc&select=token`, { headers });
    const disps = r.ok ? await r.json() : [];
    if (!Array.isArray(disps) || !disps.length) {
      // Sin móvil vinculado: comportamiento anterior
      dimoToastCall('⚠️ No tienes un móvil vinculado en Dimo Phone — abre el enlace tel: del número o vincúlalo desde 📱 Dimo Phone', false);
      window.location.href = `tel:${numero}`;
      return;
    }
    await fetch(`${SUPABASE_URL}/rest/v1/dimo_llamadas_pendientes`, {
      method: 'POST',
      headers: { ...headers, Prefer: 'return=minimal' },
      // Una marcación por CADA móvil vinculado: si el usuario se vinculó más de
      // una vez, la app puede llevar cualquiera de los tokens y la llamada llega
      // igual (las no atendidas caducan solas).
      body: JSON.stringify(disps.map(d => ({
        token: d.token,
        user_email: dimoUsuarioActual.email,
        numero,
        lead_id: lead?.id ?? null,
        lead_nombre: lead ? ([lead.nombre, lead.apellidos].filter(Boolean).join(' ') || lead.razonSocial || lead.razon_social || null) : null,
        solicitado_por: dimoUsuarioActual.nombre || null,
      }))),
    });
    dimoToastCall('📲 Llamada enviada a tu móvil — con Dimo Phone abierta se marcará en unos segundos');
  } catch (e) {
    window.location.href = `tel:${numero}`;
  }
}

// Da formato legible a comentarios largos pegados de portales/WhatsApp que
// llegan en una sola linea (parte antes de cada [HH:MM, fecha], URL o " - ")
const formatearComentarioLargo = (texto) => {
  const t = String(texto || '');
  if (t.includes('\n') || t.length < 120) return t;
  return t
    .replace(/\s+(?=\[\d{1,2}:\d{2},)/g, '\n\n')
    .replace(/\s+(?=https?:\/\/)/g, '\n\n')
    .replace(/\s+-\s+(?=[A-Z\u00c1\u00c9\u00cd\u00d3\u00da\u00d1\u00bf])/g, '\n\n- ')
    .trim();
};


const MARCAS = ['Peugeot', 'Citroën', 'Opel', 'Fiat', 'Jeep', 'Abarth', 'Leapmotor'];

// Orígenes disponibles para crear leads
const ORIGENES = [
  'Visita Espontánea',
  'Llamada Entrante',
  'Referido',
  'Evento/Feria',
  'Redes Sociales',
  'Conocido/Familiar',
  'Cliente Anterior',
  'Otro'
];

// Tipos de lead
const TIPOS_LEAD = ['Lead Válido', 'Lead', 'Oportunidad'];

// Tipos de cliente
const TIPOS_CLIENTE = ['Particular', 'Empresa', 'Autónomo'];

// Tipos de venta
const TIPOS_VENTA = ['VN', 'VO', 'B2B', 'KM0', 'Gerencia'];

// ==================== FETCH DATA ====================
// Función para cargar leads desde Supabase
const fetchLeads = async () => {
  try {
    // PAGE_SIZE bajado de 1000 a 500: con la tabla creciendo y SELECT * (jsonb
    // historial/comentarios), 1000 filas saturaban statement_timeout=3s del rol
    // anon en redes lentas. El server ya tiene timeout=8s ahora, pero 500 deja
    // margen extra y mas resiliencia.
    // Avanzamos `offset` por filas REALES recibidas (no por PAGE_SIZE): si una
    // pagina llega parcial por timeout o cap interno, seguimos paginando sin
    // perder filas y sin duplicar.
    const PAGE_SIZE = 500;
    let allData = [];
    let offset = 0;
    while (true) {
      const url = `${SUPABASE_URL}/rest/v1/leads?select=*&order=fecha_creacion.desc&limit=${PAGE_SIZE}&offset=${offset}`;
      // Retry hasta 3 veces por página: si statement_timeout u otro error transitorio
      // corta la página, reintenta con backoff exponencial. Antes el primer fallo
      // hacía return null y el comercial veía 0 leads (caso Jaime 2026-05-07).
      let data = null;
      let lastError = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await fetch(url, {
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) { lastError = new Error(`HTTP ${response.status}`); }
          else { data = await response.json(); lastError = null; break; }
        } catch (e) { lastError = e; }
        await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
      }
      if (lastError) {
        if (process.env.NODE_ENV === 'development') console.warn('[fetchLeads] página', offset, 'falló tras 3 intentos:', lastError);
        // Devolver lo que se haya conseguido en lugar de null para que el UI no se quede vacío
        return allData.length > 0 ? allData : null;
      }
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      offset += data.length;
      if (offset > 100000) break;
    }
    return allData;
  } catch (error) {
    // Error silencioso en producción
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching leads:', error);
    }
    return null;
  }
};

// Función para cargar comerciales desde Supabase
const fetchComerciales = async () => {
  try {
    const { data, error } = await supabase.from('comerciales').select('*');
    if (error) throw error;
    return (data || []).filter(c => c.activo);
  } catch (error) {
    // Error silencioso en producción
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching comerciales:', error);
    }
    return null;
  }
};

// ==================== VALIDACIÓN DE CONTRASEÑA ====================
// Política: mínimo 8 caracteres, al menos 1 mayúscula, 1 minúscula y 1 número
const validatePassword = (password) => {
  const errors = [];
  
  if (!password || password.length < 8) {
    errors.push('Mínimo 8 caracteres');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Al menos 1 mayúscula');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Al menos 1 minúscula');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Al menos 1 número');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors,
    message: errors.length > 0 ? `La contraseña debe tener: ${errors.join(', ')}` : null
  };
};

// ==================== SISTEMA DE LOGGING SEGURO ====================
const secureLog = {
  dev: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[DEV]', ...args);
    }
  },
  error: (context, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[ERROR: ${context}]`, error);
    }
  },
  warn: (context, message) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[WARN: ${context}]`, message);
    }
  }
};

// ==================== FUNCIÓN DE AUDITORÍA ====================
// Registrar acciones críticas en auditoria_logs
const registrarAuditoria = async (action, tableName = null, recordId = null, details = null) => {
  secureLog.dev('registrarAuditoria llamada:', action);
  
  try {
    const token = localStorage.getItem('sb-access-token');
    if (!token) {
      secureLog.warn('registrarAuditoria', 'No hay token');
      return;
    }
    
    // Obtener usuario DIRECTAMENTE con fetch
    const userResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!userResponse.ok) {
      secureLog.error('registrarAuditoria', 'Error obteniendo usuario');
      return;
    }
    
    const user = await userResponse.json();
    
    // Preparar datos de auditoría
    const auditData = {
      user_id: user.id,
      user_email: user.email,
      action,
      table_name: tableName,
      record_id: recordId ? String(recordId) : null,
      details: details || null,
      user_agent: navigator.userAgent || null
    };
    
    // Insertar auditoría
    const insertResponse = await fetch(`${SUPABASE_URL}/rest/v1/auditoria_logs`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(auditData)
    });
    
    if (!insertResponse.ok) {
      secureLog.error('registrarAuditoria', 'Error insertando auditoría');
    }
    
  } catch (error) {
    secureLog.error('registrarAuditoria', error);
  }
};

// Función para verificar si existe un lead duplicado (mismo móvil + misma marca)
const checkDuplicateLead = async (movil, marca) => {
  secureLog.dev('checkDuplicateLead - Buscando duplicados:', { movil, marca });
  
  try {
    const url = `${SUPABASE_URL}/rest/v1/leads?movil=eq.${encodeURIComponent(movil)}&marca=eq.${encodeURIComponent(marca)}&select=id,nombre,apellidos,vendedor_nombre,estado_cita,asistencia_cita,fecha_creacion`;
    
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const data = await response.json();
    
    if (data && data.length > 0) {
      secureLog.dev('Duplicado encontrado');
      return { exists: true, lead: data[0] };
    }
    
    return { exists: false, lead: null };
  } catch (error) {
    secureLog.error('checkDuplicateLead', error);
    return { exists: false, lead: null };
  }
};

// Función para verificar si existe un lead duplicado (mismo email + marca)
const checkDuplicateByEmail = async (email, marca) => {
  secureLog.dev('checkDuplicateByEmail - Buscando duplicados:', { email, marca });
  
  if (!email || !marca) {
    return { exists: false, lead: null };
  }
  
  try {
    const url = `${SUPABASE_URL}/rest/v1/leads?email=eq.${encodeURIComponent(email)}&marca=eq.${encodeURIComponent(marca)}&select=id,nombre,apellidos,vendedor_nombre,estado_cita,asistencia_cita,fecha_creacion,email`;
    
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const data = await response.json();
    
    if (data && data.length > 0) {
      secureLog.dev('Duplicado por email encontrado');
      return { exists: true, lead: data[0] };
    }
    
    return { exists: false, lead: null };
  } catch (error) {
    secureLog.error('checkDuplicateByEmail', error);
    return { exists: false, lead: null };
  }
};

// === Agenda T4.2: helpers de snooze / clasificación ===
// Inicio del día (00:00) de una fecha cualquiera.
const inicioDia = (dt) => { const x = new Date(dt); x.setHours(0, 0, 0, 0); return x; };
// Un lead "Pendiente" agendado para un día futuro sale de la lista de trabajo
// (pendientes de hoy) hasta esa fecha. Sin fecha agendada, fecha de hoy o
// fecha pasada => sigue contando como pendiente de hoy.
const estaAgendadoFuturo = (lead) => {
  if (!lead || !lead.citaAgendada) return false;
  if ((lead.estado || 'Pendiente') !== 'Pendiente') return false;
  return inicioDia(lead.citaAgendada).getTime() > inicioDia(new Date()).getTime();
};

// ── Tareas (lead_tareas) — la misma tabla que usa el Panel Recepción de Adriana ──
const RECEPCION_NOMBRE_CRM = 'Adriana';
const tareasHeaders = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };

// Paginado de 1000 en 1000: PostgREST corta cada respuesta en 1000 filas y con
// un tope fijo las tareas de hoy y las futuras se quedaban fuera de la pantalla.
const getTareasDeComercial = async (nombre) => {
  const todas = [];
  for (let page = 0; page < 10; page++) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/lead_tareas?asignada_a=eq.${encodeURIComponent(nombre)}&order=fecha.asc.nullslast,hora.asc.nullslast,created_at.desc&limit=1000&offset=${page * 1000}&select=*`, { headers: tareasHeaders });
    if (!r.ok) break;
    const lote = await r.json();
    todas.push(...lote);
    if (lote.length < 1000) break;
  }
  return todas;
};

const crearTareaCrm = async ({ leadId, titulo, fecha, hora, asignadaA, quien }) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/lead_tareas`, {
    method: 'POST',
    headers: { ...tareasHeaders, Prefer: 'return=representation' },
    body: JSON.stringify({ lead_id: leadId || null, titulo, fecha: fecha || null, hora: hora || null, asignada_a: asignadaA, creada_por: quien }),
  });
  if (!r.ok) throw new Error(`crear tarea → ${r.status}`);
  const filas = await r.json();
  return filas[0];
};

// Sube una imagen pegada en un comentario al bucket lead-archivos y
// devuelve su URL publica (mismo bucket que usa el Panel de Adriana).
const subirImagenComentarioCrm = async (leadId, file) => {
  const ext = (file.type || '').includes('png') ? 'png' : (file.type || '').includes('gif') ? 'gif' : 'jpg';
  const base = `captura-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.${ext}`;
  const path = `lead-${leadId}/comentarios/${Date.now()}-${base}`;
  const r = await fetch(`${SUPABASE_URL}/storage/v1/object/lead-archivos/${path}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': file.type || 'image/png', 'x-upsert': 'true' },
    body: file,
  });
  if (!r.ok) throw new Error(`Subida de imagen → ${r.status}`);
  return `${SUPABASE_URL}/storage/v1/object/public/lead-archivos/${path}`;
};

const actualizarTareaCrm = async (id, updates) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/lead_tareas?id=eq.${id}`, {
    method: 'PATCH',
    headers: { ...tareasHeaders, Prefer: 'return=representation' },
    body: JSON.stringify({ ...updates, updated_at: new Date().toISOString() }),
  });
  if (!r.ok) throw new Error(`actualizar tarea → ${r.status}`);
};

// ── Menciones @nombre en comentarios → tarea automática para esa persona ──
// "@Adriana este cliente pide factura" crea una tarea en el panel de Adriana;
// "@Brian llámalo tú" se la crea a Brian en su CRM.
const crmSinAcentos = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Quién es "@Carlos Duran" / "@carlos" entre las personas activas.
// Petición Adriana 07/10: con dos Carlos (Duran y de Gracia), «@Carlos Duran» se
// lo llevaba Carlos de Gracia porque bastaba con que coincidiera el nombre de
// pila. Ahora: 1) nombre completo exacto; 2) la persona empieza por lo
// mencionado («@Carlos Dur»); 3) solo por nombre de pila si hay UNA sola
// persona con ese nombre — si hay varias y no se pone el apellido, no se asigna
// a nadie antes que a la persona equivocada.
const resolverMencionCrm = (raw, personas) => {
  const palabras = crmSinAcentos(raw).split(/\s+/).filter(Boolean);
  if (!palabras.length) return null;
  const norm = personas.map(p => ({ p, n: crmSinAcentos(p), pila: crmSinAcentos(p).split(' ')[0] }));
  for (let k = Math.min(2, palabras.length); k >= 1; k--) {
    const m = palabras.slice(0, k).join(' ');
    const exacta = norm.find(x => x.n === m);
    if (exacta) return exacta.p;
    const empieza = norm.filter(x => x.n.startsWith(m));
    if (empieza.length === 1) return empieza[0].p;
    if (k === 1) {
      const porPila = norm.filter(x => x.pila === m);
      if (porPila.length === 1) return porPila[0].p;
    }
  }
  return null;
};

const procesarMencionesCrm = async (lead, texto, quien) => {
  const matches = String(texto || '').match(/@([\p{L}]+(?:\s+[\p{L}]+)?)/gu);
  if (!matches || !matches.length) return [];
  let personas = [];
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?activo=eq.true&select=nombre`, { headers: tareasHeaders });
    personas = r.ok ? (await r.json()).map(c => c.nombre).filter(Boolean) : [];
  } catch (e) { personas = []; }
  if (!personas.includes(RECEPCION_NOMBRE_CRM)) personas.push(RECEPCION_NOMBRE_CRM);
  const creadas = [];
  const avisadas = new Set();
  for (const raw of matches) {
    const persona = resolverMencionCrm(raw.slice(1), personas);
    if (!persona || persona === quien || avisadas.has(persona)) continue;
    avisadas.add(persona);
    try {
      const t = await crearTareaCrm({
        leadId: lead?.id || null,
        titulo: `💬 Mención de ${quien}: «${String(texto).trim().slice(0, 140)}»`,
        asignadaA: persona, quien,
      });
      creadas.push({ persona, tarea: t });
    } catch (e) { /* una mención fallida nunca bloquea el comentario */ }
  }
  return creadas;
};

// Función para actualizar lead en Supabase
const updateLeadInSupabase = async (leadId, updates) => {
  try {
    // Convertir nombres de campos al formato de Supabase
    const supabaseUpdates = {
      estado_cita: updates.estado,
      motivo_no_compra: updates.motivoNoCompra,
      primera_llamada: updates.primeraLlamada,
      ultima_llamada: updates.ultimaLlamada,
      fecha_agendada: updates.citaAgendada,
      ...(updates.asistenciaCita !== undefined ? { asistencia_cita: updates.asistenciaCita } : {}),
      tipo_agenda: updates.tipoAgenda,
      fecha_modificacion: new Date().toISOString(),
      observaciones: updates.observaciones,
      historial: updates.historial,
      comentarios: updates.comentarios,
      no_contesta: updates.noContesta,
      completada: updates.completada,
      pospuesta: updates.pospuesta,
      // Necesarios para el traspaso de lead a otro comercial; sin ellos en la
      // whitelist el PATCH no cambiaba propietario/centro (solo escribía historial).
      vendedor_nombre: updates.vendedor_nombre,
      centro: updates.centro,
      // Edición de datos del cliente desde la ficha (FichaLeadCrm). Solo se
      // envían si el caller los proporciona (undefined se elimina abajo).
      nombre: updates.nombre,
      apellidos: updates.apellidos,
      razon_social: updates.razonSocial,
      telefono: updates.telefono,
      movil: updates.movil,
      email: updates.email,
      cp: updates.cp,
      provincia: updates.provincia,
      marca: updates.marca,
      modelo: updates.modelo,
      motor: updates.motor,
      combustible: updates.combustible,
      matricula: updates.matricula,
      kilometros: updates.kilometros,
      tipo_venta: updates.tipoVenta
    };

    // Eliminar campos undefined (no enviar lo que el caller no proporcionó —
    // antes usábamos `|| 0` y `|| false` y eso reseteaba campos al hacer un
    // cambio rápido de estado).
    Object.keys(supabaseUpdates).forEach(key => {
      if (supabaseUpdates[key] === undefined) delete supabaseUpdates[key];
    });

    const { data, error } = await supabase.from('leads').update(supabaseUpdates).eq('id', leadId);

    if (error) throw error;
    // Con Prefer: return=representation, data === [] significa que no se
    // actualizó ninguna fila (id no existe o RLS lo bloqueó). Tratamos eso
    // como fallo para que el caller no haga optimistic update fantasma.
    if (Array.isArray(data) && data.length === 0) {
      return { success: false, error: 'No row updated (id desconocido o RLS)' };
    }
    return { success: true, data };
  } catch (error) {
    secureLog.error('updateLeadInSupabase', error);
    return { success: false, error: error.message };
  }
};

// Función para insertar un nuevo lead en Supabase
const insertLead = async (lead) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/leads`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(lead)
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    secureLog.error('insertLead', error);
    return { data: null, error };
  }
};

// ==================== VALIDACIÓN Y SANITIZACIÓN ====================
// Función para sanitizar texto (prevenir XSS y caracteres problemáticos)
const sanitizeInput = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Caracteres de control
    .replace(/[<>]/g, '') // Prevenir inyección HTML básica
    .replace(/[""'']/g, '"') // Comillas tipográficas
    .replace(/[—–]/g, '-') // Guiones especiales
    .replace(/…/g, '...') // Puntos suspensivos
    .trim();
};

// Validadores
const validators = {
  email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
  phone: (v) => !v || /^[0-9\s\-\+\(\)]{9,20}$/.test(v),
  notEmpty: (v) => v && v.trim().length > 0,
  minLength: (min) => (v) => !v || v.length >= min,
  maxLength: (max) => (v) => !v || v.length <= max
};

// Validar datos de lead antes de enviar
const validateLeadData = (data) => {
  const errors = [];
  
  if (data.email && !validators.email(data.email)) {
    errors.push('Email no válido');
  }
  if (data.movil && !validators.phone(data.movil)) {
    errors.push('Teléfono móvil no válido');
  }
  if (data.telefono && !validators.phone(data.telefono)) {
    errors.push('Teléfono no válido');
  }
  
  return errors.length > 0 ? errors : null;
};

const parseDate = (dateStr) => {
  if (!dateStr || dateStr.trim() === '') return null;
  const str = dateStr.toString().trim();
  
  // Formato DD/MM/YYYY HH:MM:SS
  if (str.includes('/')) {
    const parts = str.split(' ');
    const dateParts = parts[0].split('/');
    if (dateParts.length !== 3) return null;
    const day = dateParts[0].padStart(2, '0');
    const month = dateParts[1].padStart(2, '0');
    const year = dateParts[2].length === 2 ? '20' + dateParts[2] : dateParts[2];
    let timeStr = '00:00:00';
    if (parts[1]) {
      const timeParts = parts[1].split(':');
      timeStr = `${(timeParts[0] || '0').padStart(2, '0')}:${(timeParts[1] || '0').padStart(2, '0')}:${(timeParts[2] || '0').padStart(2, '0')}`;
    }
    const date = new Date(`${year}-${month}-${day}T${timeStr}`);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Intenta formato ISO
  const date = new Date(str);
  return isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
};

// Transformar datos de Supabase al formato del CRM

// Fecha de la última asignación o traspaso que consta en el historial del lead
// («Asignado por Recepción … a X», «Traspasado por Recepción de A a B»,
// «Traspaso de A a B», «Lead traspasado desde …», AutoLeads, etc.).
const fechaUltimaAsignacion = (historial) => {
  if (!Array.isArray(historial)) return null;
  let mejor = null;
  for (const h of historial) {
    const acc = String(h?.accion || '');
    if (!/asignad|traspas/i.test(acc)) continue;
    const f = h?.fecha ? new Date(h.fecha) : null;
    if (f && !isNaN(f) && (!mejor || f > mejor)) mejor = f;
  }
  return mejor;
};
const transformSupabaseLeads = (rows) => {
  if (!rows || rows.length === 0) return [];
  
  return rows.map((row, index) => {
    const fechaTraspaso = fechaUltimaAsignacion(row.historial);
    const fechaCreacionD = row.fecha_creacion ? new Date(row.fecha_creacion) : null;
    return {
      id: row.id,
      rowIndex: index + 2,
      // Cuándo entró el lead en la cartera de ESTE comercial: la última
      // asignación / traspaso del historial, o la creación si nunca cambió de
      // manos (petición Adriana 07/10: los leads que se le asignan a Miguel
      // salían abajo del todo porque se ordenaba por fecha de creación).
      fechaTraspaso,
      fechaEntrada: fechaTraspaso && (!fechaCreacionD || fechaTraspaso > fechaCreacionD) ? fechaTraspaso : fechaCreacionD,
      leadId: row.lead_id || `lead-${row.id}`,
      tipo: row.lead_tipo || 'Lead',
      origen: row.origen || '',
      detalleOrigen: row.detalle_origen || 'Orgánico',
      campana: row.campana || '',
      estado: row.estado_cita || 'Pendiente',
      motivoNoCompra: row.motivo_no_compra || '',
      usuarioAlta: row.usuario_alta || '',
      fechaCreacion: row.fecha_creacion ? new Date(row.fecha_creacion) : null,
      primeraLlamada: row.primera_llamada ? new Date(row.primera_llamada) : null,
      ultimaLlamada: row.ultima_llamada ? new Date(row.ultima_llamada) : null,
      citaAgendada: row.fecha_agendada ? new Date(row.fecha_agendada) : null,
      asistenciaCita: row.asistencia_cita || null,
      tipoAgenda: row.tipo_agenda || null,
      fechaModificacion: row.fecha_modificacion ? new Date(row.fecha_modificacion) : null,
      vendedor: row.vendedor_nombre || '',
      centro: row.centro || '',
      nombre: row.nombre || '',
      apellidos: row.apellidos || '',
      razonSocial: row.razon_social || '',
      tipoCliente: row.tipo_cliente || 'Particular',
      telefono: row.telefono || '',
      movil: row.movil || '',
      email: row.email || '',
      cp: row.cp || '',
      provincia: row.provincia || '',
      marca: row.marca || '',
      modelo: row.modelo || '',
      motor: row.motor || '',
      combustible: row.combustible || '',
      kilometros: row.kilometros || '',
      matricula: row.matricula || '',
      tipoVenta: row.tipo_venta || 'VN',
      utmSource: row.utm_source || '',
      utmMedium: row.utm_medium || '',
      utmCampaign: row.utm_campaign || '',
      completada: row.completada || false,
      noContesta: row.no_contesta || 0,
      pospuesta: row.pospuesta || false,
      observaciones: row.observaciones || '',
      enlaceOrigen: row.enlace_origen || '',
      historial: row.historial || [],
      comentarios: row.comentarios || []
    };
  });
};


// ==================== HOOK DETECCIÓN MÓVIL ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Estados para móvil con colores originales (sin iconos)
// Misma paleta que en escritorio, para que el móvil no cuente otra historia.
const ESTADOS_MOBILE = {
  'Pendiente': { color: '#fff', bg: C.gris },
  'Contactado': { color: '#fff', bg: '#1a9cd4' },
  'Atendido': { color: '#fff', bg: C.brand },
  'Citado': { color: '#fff', bg: C.naranja },
  'Ofertado': { color: '#fff', bg: C.morado },
  'Duplicado': { color: '#fff', bg: C.faint },
  'Cancelado': { color: '#fff', bg: C.rojo },
  'Vendido': { color: '#fff', bg: C.verde }
};

const ESTADOS_ORDEN_MOBILE = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado', 'Duplicado', 'Cancelado', 'Vendido'];

// Formatear fecha relativa
const formatRelativeDate = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  const isToday = now.toDateString() === d.toDateString();
  if (isToday) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const formatTimeMobile = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

// ==================== COMPONENTES MÓVILES ====================

// Header Móvil
const MobileHeader = ({ comercial, onLogout, onRefresh, isLoading, onOpenDimoPhone }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  return (
    <>
      <header style={{
        background: '#ffffff',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid #e7e9ec'
      }}>
        <img src="/logotipo.png" alt="Dimoauto" style={{ height: '22px', objectFit: 'contain', display: 'block' }} onError={(e) => e.target.style.display = 'none'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button 
            onClick={onRefresh}
            disabled={isLoading}
            className="fg-nav"
            style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span style={{ fontSize: '18px' }}>{isLoading ? '⏳' : '🔄'}</span>
          </button>
          <button onClick={() => setShowMenu(!showMenu)} className="fg-nav" style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer' }}>
            <span style={{ fontSize: '18px' }}>👤</span>
          </button>
        </div>
      </header>
      
      {showMenu && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,28,43,0.65)', zIndex: 200 }} onClick={() => setShowMenu(false)}>
          <div style={{ position: 'absolute', top: '60px', right: '16px', background: 'white', borderRadius: 12, padding: '8px', minWidth: '220px', boxShadow: '0 20px 60px rgba(15,42,70,0.35)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.line}` }}>
              <div style={{ fontWeight: 800, color: C.ink, fontSize: '14px' }}>{comercial?.nombre}</div>
              <div style={{ fontSize: '12.5px', color: C.muted }}>{comercial?.centro}</div>
            </div>
            {onOpenDimoPhone && (
              <button onClick={() => { setShowMenu(false); onOpenDimoPhone(); }} className="fg-nav" style={{ width: '100%', padding: '11px 14px', background: 'none', border: 'none', borderRadius: 10, textAlign: 'left', cursor: 'pointer', color: C.brand, fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: SEPARADOR_FILA }}>
                📱 Dimo Phone
              </button>
            )}
            <button onClick={onLogout} className="fg-nav" style={{ width: '100%', padding: '11px 14px', background: 'none', border: 'none', borderRadius: 10, textAlign: 'left', cursor: 'pointer', color: C.rojo, fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🚪 Cerrar Sesión
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Barra de filtro móvil
const MobileFilterBar = ({ filter, setFilter, searchTerm, setSearchTerm }) => (
  <div style={{ background: GRAD, padding: '12px 16px' }}>
    <div style={{ marginBottom: '12px' }}>
      <select 
        value={filter} 
        onChange={(e) => setFilter(e.target.value)}
        style={{ ...campo, width: '100%', padding: '11px 14px', border: 'none', fontWeight: 700 }}
      >
        <option value="todos">Todos los estados</option>
        {ESTADOS_ORDEN_MOBILE.map(estado => (
          <option key={estado} value={estado}>{estado}</option>
        ))}
      </select>
    </div>
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9aa4af', fontSize: '16px' }}>🔍</span>
      <input
        type="text"
        placeholder="Buscar cliente, teléfono o vehículo"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ ...campo, width: '100%', padding: '11px 12px 11px 42px', border: 'none' }}
      />
    </div>
  </div>
);

// Tarjeta de Lead Móvil
// ==================== ADJUNTOS DEL LEAD (subidos desde Recepción) ====================
const AdjuntosLead = ({ leadId }) => {
  const [archivos, setArchivos] = useState([]);
  const [visor, setVisor] = useState(null);

  useEffect(() => {
    if (!leadId) return;
    let vivo = true;
    fetch(`${SUPABASE_URL}/rest/v1/lead_archivos?lead_id=eq.${leadId}&order=created_at.desc&select=*`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(data => { if (vivo) setArchivos(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [leadId]);

  if (!archivos.length) return null;
  const esImg = (a) => (a.mime || '').startsWith('image/');

  return (
    <div style={{ marginTop: '16px', background: '#f4f5f7', borderRadius: 10, padding: '16px', border: '1px solid #e7e9ec' }}>
      <h4 style={{ margin: '0 0 12px 0', color: '#005187', fontSize: '13px', fontWeight: 600 }}>📎 Archivos adjuntos ({archivos.length})</h4>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {archivos.map(a => (
          <div key={a.id} style={{ width: '86px' }}>
            <div
              onClick={() => esImg(a) ? setVisor(a) : window.open(a.url, '_blank')}
              title={a.nombre}
              style={{ width: '86px', height: '68px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {esImg(a)
                ? <img src={a.url} alt={a.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '26px' }}>{(a.mime || '').includes('pdf') ? '📄' : '📁'}</span>}
            </div>
            <div style={{ fontSize: '10px', color: '#8a929c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '3px' }}>{a.nombre}</div>
          </div>
        ))}
      </div>
      {visor && (
        <div onClick={() => setVisor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px', cursor: 'zoom-out' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={visor.url} alt={visor.nombre} style={{ maxWidth: '90vw', maxHeight: '84vh', borderRadius: 10 }} />
            <div style={{ color: '#fff', fontSize: '13px', marginTop: '8px' }}>
              {visor.nombre} · <a href={visor.url} target="_blank" rel="noreferrer" style={{ color: '#1a9cd4' }}>Abrir original ↗</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== FICHA DE LEAD (misma estructura que Recepción/Adriana) ====================
// Sustituye a LeadDetailPanel / LeadDetailModal en escritorio. Trabaja sobre las
// mismas tablas (leads, lead_archivos) → sincronizada con el panel de Recepción.

// Fotos de catálogo (proyecto Marketing — vehicle_images, la misma de App Campañas)
const FICHA_MARKETING_URL = 'https://vgraqyqwrdgsqdkpltou.supabase.co';
const FICHA_MARKETING_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZncmFxeXF3cmRnc3Fka3BsdG91Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMjgyMjIsImV4cCI6MjA4MjYwNDIyMn0.PQ9BV4f3s_wOoR4YKkeP0C5yW1pLXF-6CD7174Dvebc';
const fichaFotosCache = {};
const fichaGetFoto = async (marca, modelo) => {
  const m = String(marca || '').trim().toLowerCase();
  const mod = String(modelo || '').trim().toLowerCase();
  if (!m || !mod) return null;
  if (!(m in fichaFotosCache)) {
    try {
      const r = await fetch(`${FICHA_MARKETING_URL}/rest/v1/vehicle_images?brand_name=ilike.${encodeURIComponent(m)}&select=model,image_url,is_principal`, {
        headers: { apikey: FICHA_MARKETING_KEY, Authorization: `Bearer ${FICHA_MARKETING_KEY}` },
      });
      fichaFotosCache[m] = r.ok ? await r.json() : [];
    } catch { fichaFotosCache[m] = []; }
  }
  const cand = (fichaFotosCache[m] || [])
    .filter(f => { const fm = String(f.model || '').trim().toLowerCase(); return fm && (mod.includes(fm) || fm.includes(mod)); })
    .sort((a, b) => ((b.is_principal ? 1 : 0) - (a.is_principal ? 1 : 0)) || (String(b.model).length - String(a.model).length));
  return cand[0]?.image_url || null;
};

// Chips de estado de la ficha: mismos colores que ESTADOS, en versión tenue.
const FICHA_CHIP = {
  Pendiente: { color: C.gris, bg: `${C.gris}18` },
  Contactado: { color: '#1a9cd4', bg: '#1a9cd418' },
  Atendido:  { color: C.brand, bg: `${C.brand}18` },
  Citado:    { color: C.naranja, bg: `${C.naranja}18` },
  Ofertado:  { color: C.morado, bg: `${C.morado}18` },
  Cancelado: { color: C.rojo, bg: `${C.rojo}18` },
  Vendido:   { color: C.verde, bg: `${C.verde}18` },
};

const fichaFmt = (f) => f ? new Date(f).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fichaEsImg = (a) => (a.mime || '').startsWith('image/');

// ── Dimo Phone: llamadas GSM del lead registradas por la app del comercial ──
const DIMO_TIPO = {
  entrante:  { icon: '↙', label: 'Entrante',  color: C.verde, bg: `${C.verde}14` },
  saliente:  { icon: '↗', label: 'Saliente',  color: C.brandDark, bg: '#dceaf4' },
  perdida:   { icon: '✕', label: 'Perdida',   color: C.rojo, bg: `${C.rojo}14` },
  rechazada: { icon: '⊘', label: 'Rechazada', color: C.naranja, bg: `${C.naranja}14` },
  bloqueada: { icon: '⛔', label: 'Bloqueada', color: '#8a929c', bg: '#f4f5f7' },
  buzon:     { icon: '📩', label: 'Buzón',     color: C.morado, bg: `${C.morado}14` },
};
const dimoDur = (s) => {
  const seg = Number(s) || 0;
  if (seg <= 0) return '—';
  const m = Math.floor(seg / 60), r = seg % 60;
  return m > 0 ? `${m} min ${String(r).padStart(2, '0')} s` : `${r} s`;
};

// Las llamadas se buscan por la ficha Y por los teléfonos del cliente: así salen
// también las que el cliente hace (entrantes) o las que se marcan a mano en el
// móvil, que la BD no liga a ninguna ficha (lead_id vacío). Igual que en
// Recepción. Petición Adriana 13/09: "creo que no se están registrando las llamadas".
const DimoLlamadasCard = ({ leadId, telefonos = [], compacta = false }) => {
  const [llamadas, setLlamadas] = useState([]);
  const [nombres, setNombres] = useState({});
  const colas = [...new Set((telefonos || []).map(t => String(t || '').replace(/\D/g, '').slice(-9)).filter(t => t.length === 9))];
  const claveTel = colas.join(',');

  useEffect(() => {
    if (!leadId && !claveTel) { setLlamadas([]); return undefined; }
    let vivo = true;
    const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` };
    const cond = [
      ...(leadId ? [`lead_id.eq.${leadId}`] : []),
      ...colas.map(t => `numero.ilike.${encodeURIComponent(`*${t}`)}`),
    ];
    fetch(`${SUPABASE_URL}/rest/v1/dimo_llamadas?or=(${cond.join(',')})&order=fecha.desc&limit=200&select=*`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        if (!vivo) return;
        const lista = Array.isArray(d) ? d : [];
        setLlamadas(lista);
        const emails = [...new Set(lista.map(l => (l.user_email || '').toLowerCase()).filter(Boolean))];
        if (!emails.length) return;
        fetch(`${SUPABASE_URL}/rest/v1/comerciales?email=in.(${emails.map(encodeURIComponent).join(',')})&select=email,nombre,apellidos`, { headers })
          .then(r => r.ok ? r.json() : [])
          .then(cs => {
            if (vivo && Array.isArray(cs)) {
              setNombres(Object.fromEntries(cs.map(c => [String(c.email || '').toLowerCase(), `${c.nombre || ''} ${c.apellidos || ''}`.trim()])));
            }
          }).catch(() => {});
      }).catch(() => {});
    return () => { vivo = false; };
  // eslint-disable-next-line
  }, [leadId, claveTel]);

  if (!llamadas.length) return null;
  const tarjeta = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: compacta ? '12px 14px' : '14px 16px' };
  const tituloTarjeta = { fontSize: 10.5, fontWeight: 800, color: C.brand, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 };
  return (
    <div className="fg-card" style={tarjeta}>
      <div style={tituloTarjeta}>📱 Llamadas Dimo Phone ({llamadas.length})</div>
      <div style={{ display: 'grid', gap: 6, maxHeight: compacta ? 200 : 260, overflowY: 'auto' }}>
        {llamadas.map(ll => {
          const t = DIMO_TIPO[ll.tipo] || { icon: '📞', label: ll.tipo || 'Llamada', color: '#8a929c', bg: '#f4f5f7' };
          const quien = nombres[(ll.user_email || '').toLowerCase()] || ll.user_email || '—';
          return (
            <div key={ll.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f4f5f7', borderRadius: 10, padding: '8px 12px', borderLeft: `4px solid ${t.color}` }}>
              <span style={{ width: 26, height: 26, borderRadius: '50%', background: t.bg, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{t.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#1f2a37' }}>
                  <b>{t.label}</b>
                  <span style={{ color: '#8a929c' }}> · {dimoDur(ll.duracion_seg)} · {ll.numero || ''}</span>
                </div>
                <div style={{ fontSize: 11, color: '#8a929c', marginTop: 1 }}>{fichaFmt(ll.fecha)} — {quien}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
const fichaEntradaHistorial = (accion, usuario, estado) => ({ fecha: new Date().toISOString(), accion, usuario, estado });

// Último cambio de estado de un lead (a partir del historial): cuándo, quién y por qué.
const ultimoCambioEstadoCrm = (lead) => {
  const hist = Array.isArray(lead?.historial) ? lead.historial : [];
  const actual = lead?.estado || lead?.estado_cita || 'Pendiente';
  for (let i = hist.length - 1; i >= 0; i--) {
    if (hist[i] && hist[i].estado === actual) {
      return { fecha: hist[i].fecha, usuario: hist[i].usuario || null, motivo: hist[i].motivo || null };
    }
  }
  return null;
};
const tooltipEstadoCrm = (lead) => {
  const info = ultimoCambioEstadoCrm(lead);
  const est = lead?.estado || 'Pendiente';
  return info
    ? `Estado "${est}" desde el ${fichaFmt(info.fecha)}${info.usuario ? `\nCambiado por ${info.usuario}` : ''}${info.motivo ? `\nMotivo: ${info.motivo}` : ''}`
    : `Estado: ${est} (sin registro de cuándo se cambió)`;
};

const FichaLeadCrm = ({ lead, onClose, onUpdate, comercialNombre, lista = null, onNavegar = null, onTareaCreada = null }) => {
  const [adjuntos, setAdjuntos] = useState([]);
  const [visor, setVisor] = useState(null);
  const [foto, setFoto] = useState(null);
  const [accion, setAccion] = useState(null); // 'cita' | 'llamada' | 'seguimiento' | 'comentario' | 'tarea'
  const [inCita, setInCita] = useState('');
  const [inSeguimiento, setInSeguimiento] = useState('');
  // Tarea programada con fecha y hora (petición Adriana 26/08): queda ligada
  // al cliente y le sale al comercial en «Mis tareas» y en su Agenda.
  const [inTarea, setInTarea] = useState({ titulo: '', fecha: '', hora: '' });
  const [inComentario, setInComentario] = useState('');
  const [respondiendoA, setRespondiendoA] = useState(null); // comentario al que se responde
  const [editandoCom, setEditandoCom] = useState(null); // comentario en edición { fecha, texto }
  const guardarComentarioEditado = async () => {
    if (!editandoCom || !editandoCom.texto.trim()) return;
    const lista = Array.isArray(lead.comentarios) ? lead.comentarios : [];
    const comentarios = lista.map(c => c.fecha === editandoCom.fecha
      ? { ...c, texto: editandoCom.texto.trim(), editado_fecha: new Date().toISOString(), editado_por: comercialNombre }
      : c);
    const result = await updateLeadInSupabase(lead.id, { comentarios });
    if (result.success) { onUpdate(lead.id, { comentarios }); setEditandoCom(null); }
  };
  const [inLlamada, setInLlamada] = useState({ resultado: 'Contactado', nota: '' });
  const [motivoPendiente, setMotivoPendiente] = useState(null); // motivo al cancelar
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);

  const telefonoLead = lead.movil || lead.telefono || '';
  const nombreCompleto = lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Sin nombre';

  useEffect(() => {
    if (!lead?.id) return;
    fetch(`${SUPABASE_URL}/rest/v1/lead_archivos?lead_id=eq.${lead.id}&order=created_at.desc&select=*`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    }).then(r => r.ok ? r.json() : []).then(d => setAdjuntos(Array.isArray(d) ? d : [])).catch(() => {});
    setAccion(null); setEditando(false); setMotivoPendiente(null); setRespondiendoA(null); setEditandoCom(null);
  }, [lead?.id]);

  useEffect(() => {
    setFoto(null);
    fichaGetFoto(lead?.marca, lead?.modelo).then(setFoto).catch(() => {});
  }, [lead?.marca, lead?.modelo]);

  const persistir = async (updates, accionHistorial) => {
    const full = {
      ...updates,
      ...(accionHistorial ? {
        historial: [...(Array.isArray(lead.historial) ? lead.historial : []), fichaEntradaHistorial(accionHistorial, comercialNombre, updates.estado || lead.estado)],
      } : {}),
    };
    const result = await updateLeadInSupabase(lead.id, full);
    if (result.success) onUpdate(lead.id, full);
    else alert('No se pudo guardar. Inténtalo de nuevo.');
    return result.success;
  };

  // ── Estado ────────────────────────────────────────────────
  const cambiarEstado = async (est) => {
    if (est === lead.estado) return;
    if (est === 'Citado' && !lead.citaAgendada) { setAccion('cita'); return; }
    if (est === 'Cancelado') { setMotivoPendiente(MOTIVOS_NO_COMPRA[0]); return; }
    setGuardando(true);
    const now = new Date().toISOString();
    await persistir({
      estado: est,
      ...(lead.primeraLlamada ? {} : { primeraLlamada: now }),
      ultimaLlamada: now,
    }, `Cambio de estado a ${est}`);
    setGuardando(false);
  };

  const confirmarCancelacion = async () => {
    setGuardando(true);
    await persistir({ estado: 'Cancelado', motivoNoCompra: motivoPendiente }, `Cancelado — ${motivoPendiente}`);
    setMotivoPendiente(null);
    setGuardando(false);
  };

  // ── Cita: modificar y confirmar asistencia ────────────────
  // (petición de Adriana 07/08: el comercial tiene que poder cambiar la cita
  //  y decir si el cliente ha venido o no)
  const aInputLocal = (valor) => {
    if (!valor) return '';
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const hayCita = !!lead.citaAgendada && lead.tipoAgenda !== 'seguimiento' && lead.tipoAgenda !== 'llamada';
  const abrirPanelCita = () => {
    setAccion(a => {
      if (a === 'cita') return null;
      setInCita(aInputLocal(hayCita ? lead.citaAgendada : ''));
      return 'cita';
    });
  };
  const marcarAsistencia = async (valor) => {
    const v = lead.asistenciaCita === valor ? null : valor;
    setGuardando(true);
    await persistir({ asistenciaCita: v },
      v ? `📅 Cita — el cliente ${v === 'Ha venido' ? 'HA VENIDO' : 'NO ha venido'}` : '📅 Asistencia a la cita borrada');
    setGuardando(false);
  };

  // ── Acciones ──────────────────────────────────────────────
  const ejecutarAccion = async () => {
    setGuardando(true);
    try {
      const now = new Date().toISOString();
      if (accion === 'cita') {
        if (!inCita) { setGuardando(false); return; }
        // Si ya había cita, esto es una MODIFICACIÓN: se respeta el estado
        // cuando el lead ya ha avanzado (Ofertado / Vendido) y se limpia la
        // asistencia, porque la cita anterior ya no es la que vale.
        const modificando = hayCita;
        const mantieneEstado = ['Ofertado', 'Vendido'].includes(lead.estado);
        const cuando = new Date(inCita).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        await persistir({
          estado: mantieneEstado ? lead.estado : 'Citado',
          citaAgendada: new Date(inCita).toISOString(),
          tipoAgenda: 'cita',
          ...(modificando ? { asistenciaCita: null } : {}),
          ...(lead.primeraLlamada ? {} : { primeraLlamada: now }),
          ultimaLlamada: now,
        }, modificando
          ? `📅 Cita modificada — ahora es el ${cuando}`
          : `📅 Cita agendada para ${cuando}`);
        setInCita('');
      } else if (accion === 'seguimiento') {
        if (!inSeguimiento) { setGuardando(false); return; }
        await persistir({
          citaAgendada: new Date(inSeguimiento + 'T09:00').toISOString(), tipoAgenda: 'seguimiento',
        }, `🚩 Seguimiento programado para el ${new Date(inSeguimiento + 'T00:00').toLocaleDateString('es-ES')}`);
        setInSeguimiento('');
      } else if (accion === 'tarea') {
        if (!inTarea.titulo.trim() || !inTarea.fecha) { setGuardando(false); alert('Escribe qué hay que hacer y elige el día.'); return; }
        await crearTareaCrm({ leadId: lead.id, titulo: inTarea.titulo.trim(), fecha: inTarea.fecha, hora: inTarea.hora || null, asignadaA: comercialNombre, quien: comercialNombre });
        const cuandoT = `${new Date(inTarea.fecha + 'T00:00').toLocaleDateString('es-ES')}${inTarea.hora ? ` a las ${inTarea.hora}` : ''}`;
        await persistir({}, `☑️ Tarea programada para el ${cuandoT}: ${inTarea.titulo.trim()}`);
        setInTarea({ titulo: '', fecha: '', hora: '' });
        onTareaCreada?.();
      } else if (accion === 'llamada') {
        await persistir({
          ...(lead.primeraLlamada ? {} : { primeraLlamada: now }),
          ultimaLlamada: now,
        }, `📞 Llamada registrada — ${inLlamada.resultado}${inLlamada.nota.trim() ? `: ${inLlamada.nota.trim()}` : ''}`);
        setInLlamada({ resultado: 'Contactado', nota: '' });
      } else if (accion === 'comentario') {
        if (!inComentario.trim()) { setGuardando(false); return; }
        // Si es una respuesta, se guarda con respuesta_a = fecha del comentario
        // original (mismo formato que el Panel Recepción de Adriana).
        const nuevo = {
          fecha: now, usuario: comercialNombre, texto: inComentario.trim(),
          ...(respondiendoA ? { respuesta_a: respondiendoA.fecha } : {}),
        };
        const comentarios = [...(Array.isArray(lead.comentarios) ? lead.comentarios : []), nuevo];
        const result = await updateLeadInSupabase(lead.id, { comentarios });
        if (result.success) {
          onUpdate(lead.id, { comentarios });
          // @menciones: crear tarea automática a la persona mencionada
          try {
            const menciones = await procesarMencionesCrm(lead, inComentario, comercialNombre);
            if (menciones.length) alert(`✅ Tarea creada para ${menciones.map(m => m.persona).join(', ')} — le saldrá en su perfil.`);
          } catch (e) { /* noop */ }
        }
        setInComentario('');
        setRespondiendoA(null);
      }
      setAccion(null);
    } finally { setGuardando(false); }
  };

  const handleWhatsAppFicha = () => {
    if (!telefonoLead) return;
    const phone = telefonoLead.replace(/\D/g, '');
    const fullPhone = phone.startsWith('34') ? phone : `34${phone}`;
    const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
    const mensaje = `Hola ${lead.nombre || 'cliente'}! soy ${comercialNombre || 'tu asesor'} del Grupo Dimoauto. Veo que estás interesado en el ${vehiculo || 'vehículo'}`;
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(mensaje)}`, '_blank');
    // Solo se registra como enviado si de verdad se pudo enviar (hay clientes sin WhatsApp)
    setTimeout(() => {
      const enviado = window.confirm('¿Se ha podido enviar el WhatsApp?\n\nAceptar = registrar como ENVIADO\nCancelar = el número NO tiene WhatsApp (queda registrado el aviso)');
      persistir({}, enviado
        ? `💬 WhatsApp enviado a ${telefonoLead} — «${mensaje.slice(0, 120)}${mensaje.length > 120 ? '…' : ''}»`
        : `⚠️ WhatsApp NO enviado a ${telefonoLead} — el número no tiene WhatsApp`);
    }, 400);
  };

  // ── Edición de datos del cliente ──────────────────────────
  const empezarEdicion = () => {
    setForm({
      nombre: lead.nombre || '', apellidos: lead.apellidos || '', razonSocial: lead.razonSocial || '',
      telefono: lead.telefono || '', movil: lead.movil || '', email: lead.email || '',
      cp: lead.cp || '', provincia: lead.provincia || '',
      marca: lead.marca || '', modelo: lead.modelo || '', matricula: lead.matricula || '',
      kilometros: lead.kilometros ?? '', tipoVenta: lead.tipoVenta || '',
    });
    setEditando(true);
  };
  const guardarEdicion = async () => {
    setGuardando(true);
    const campos = { ...form };
    campos.kilometros = campos.kilometros === '' ? null : parseInt(campos.kilometros, 10);
    if (Number.isNaN(campos.kilometros)) campos.kilometros = null;
    const ok = await persistir(campos, '✏️ Datos del cliente editados');
    if (ok) setEditando(false);
    setGuardando(false);
  };

  // ── Adjuntos ──────────────────────────────────────────────
  const subirAdjunto = async (files) => {
    if (!files?.length) return;
    setSubiendo(true);
    try {
      for (const f of files) {
        const limpio = f.name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `lead-${lead.id}/${Date.now()}-${limpio}`;
        const up = await fetch(`${SUPABASE_URL}/storage/v1/object/lead-archivos/${path}`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': f.type || 'application/octet-stream', 'x-upsert': 'true' },
          body: f,
        });
        if (!up.ok) throw new Error(`subida ${up.status}`);
        const url = `${SUPABASE_URL}/storage/v1/object/public/lead-archivos/${path}`;
        const ins = await fetch(`${SUPABASE_URL}/rest/v1/lead_archivos`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', Prefer: 'return=representation' },
          body: JSON.stringify({ lead_id: lead.id, nombre: f.name, path, url, mime: f.type || null, tamano: f.size || null, subido_por: comercialNombre }),
        });
        const filas = ins.ok ? await ins.json() : [];
        if (filas[0]) setAdjuntos(prev => [filas[0], ...prev]);
      }
    } catch { alert('No se pudo subir el archivo.'); }
    finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // ── Timeline ──────────────────────────────────────────────
  const timeline = useMemo(() => {
    const items = [];
    (Array.isArray(lead.historial) ? lead.historial : []).forEach(h => {
      const acc = String(h.accion || '');
      const tipo = acc.includes('📞') ? 'llamada' : acc.includes('Cita') || acc.includes('cita') ? 'cita' : acc.includes('🚩') ? 'seguimiento' : acc.includes('Traspas') || acc.includes('Asignado') ? 'traspaso' : 'historial';
      items.push({ fecha: h.fecha, tipo, texto: acc, quien: h.usuario });
    });
    const listaCom = Array.isArray(lead.comentarios) ? lead.comentarios : [];
    listaCom.forEach(c => {
      const padre = c.respuesta_a ? listaCom.find(p => p.fecha === c.respuesta_a) : null;
      items.push({ fecha: c.fecha, tipo: 'comentario', texto: c.texto, quien: c.usuario, comentario: c, padre });
    });
    adjuntos.forEach(a => items.push({ fecha: a.created_at, tipo: 'adjunto', texto: `📎 ${a.nombre}`, quien: a.subido_por, adjunto: a }));
    return items.filter(i => i.fecha).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [lead, adjuntos]);

  const ICONO = { llamada: '📞', cita: '📅', seguimiento: '🚩', traspaso: '⇄', historial: '📜', comentario: '🗒️', adjunto: '📎' };
  // Colores del timeline: todos de la paleta de la suite.
  const COLOR = { llamada: C.verde, cita: C.naranja, seguimiento: C.rojo, traspaso: C.brand, historial: C.muted, comentario: '#1a9cd4', adjunto: C.morado };

  // Formas de la ficha, alineadas con el sistema: tarjeta radio 12, campos y
  // botones radio 10.
  const tarjeta = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px' };
  const tituloTarjeta = { fontSize: 10.5, fontWeight: 800, color: C.brand, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 };
  const inp = { padding: '8px 11px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#fff', fontSize: 13.5, fontFamily: 'inherit', color: C.ink, boxSizing: 'border-box' };
  const btnAccion = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 6px', borderRadius: 10, border: `1px solid ${C.line}`, background: '#fff', fontSize: 12.5, fontWeight: 800, color: C.ink, cursor: 'pointer' };
  const estActual = lead.estado || 'Pendiente';

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 1400, overflowY: 'auto' }}>
      {/* Cabecera */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 18px', background: '#fff', borderBottom: `1px solid ${C.line}` }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nombreCompleto}
          <span style={{ fontWeight: 600, color: '#1a9cd4' }}> — {[lead.marca, lead.modelo].filter(Boolean).join(' ')}</span>
          <span style={{ marginLeft: 10, padding: '3px 10px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, color: (FICHA_CHIP[estActual] || {}).color, background: (FICHA_CHIP[estActual] || {}).bg, border: `1px solid ${(FICHA_CHIP[estActual] || {}).color || C.line}44` }}>{estActual}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Flechas para pasar al lead anterior / siguiente sin cerrar la ficha */}
          {Array.isArray(lista) && lista.length > 1 && onNavegar && (() => {
            const idx = lista.findIndex(l => l.id === lead.id);
            const ant = idx > 0 ? lista[idx - 1] : null;
            const sig = idx >= 0 && idx < lista.length - 1 ? lista[idx + 1] : null;
            return (
              <>
                <span style={{ fontSize: 12, color: C.muted, marginRight: 2 }}>{idx >= 0 ? `${idx + 1} / ${lista.length}` : ''}</span>
                <button onClick={() => ant && onNavegar(ant)} disabled={!ant} title="Lead anterior" className="fg-nav"
                  style={{ ...botonSecundario, padding: '6px 13px', fontSize: 14, color: ant ? C.brand : C.gris, cursor: ant ? 'pointer' : 'default' }}>←</button>
                <button onClick={() => sig && onNavegar(sig)} disabled={!sig} title="Lead siguiente" className="fg-nav"
                  style={{ ...botonSecundario, padding: '6px 13px', fontSize: 14, color: sig ? C.brand : C.gris, cursor: sig ? 'pointer' : 'default' }}>→</button>
              </>
            );
          })()}
          <button onClick={onClose} className="fg-nav" style={{ ...botonSecundario, padding: '7px 16px', flexShrink: 0 }}>✕ Cerrar</button>
        </div>
      </div>

      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 5fr) minmax(380px, 6fr)', gap: 14, alignItems: 'start' }}>
          {/* ══ IZQUIERDA ══ */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="fg-card" style={tarjeta}>
              <div style={tituloTarjeta}>🚗 Vehículo de interés</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div onClick={() => foto && setVisor({ url: foto, nombre: `${lead.marca || ''} ${lead.modelo || ''}`.trim(), mime: 'image/x' })}
                  style={{ width: 140, height: 96, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #e7e9ec', background: '#f4f5f7', cursor: foto ? 'zoom-in' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {foto
                    ? <img src={foto} alt="vehículo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ textAlign: 'center', color: C.gris }}><div style={{ fontSize: 30 }}>🚗</div><div style={{ fontSize: 10 }}>Sin foto</div></div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2a37', marginBottom: 4 }}>
                    {[lead.marca, lead.modelo].filter(Boolean).join(' ') || 'Sin vehículo'}
                  </div>
                  <div style={{ fontSize: 13, color: '#2b333d' }}>
                    {[
                      lead.tipoVenta ? (lead.tipoVenta === 'VN' ? 'VN (nuevo)' : lead.tipoVenta === 'VO' ? 'VO (ocasión)' : lead.tipoVenta) : null,
                      lead.combustible, lead.motor,
                      lead.kilometros ? `${Number(lead.kilometros).toLocaleString('es-ES')} km` : null,
                      lead.matricula,
                    ].filter(Boolean).join(' | ') || '—'}
                  </div>
                  {lead.enlaceOrigen && (
                    <a href={lead.enlaceOrigen} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, color: '#005187', fontWeight: 600 }}>
                      Ver anuncio original ↗
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="fg-card" style={tarjeta}>
              <div style={tituloTarjeta}>▾ Origen (source)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>ORIGEN</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.origen || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>DETALLE</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.detalleOrigen || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>CENTRO</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.centro || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>ENTRADA</div><div style={{ fontSize: 13, color: '#1f2a37' }}>{fichaFmt(lead.fechaCreacion)}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>1ª LLAMADA</div><div style={{ fontSize: 13, color: '#1f2a37' }}>{lead.primeraLlamada ? fichaFmt(lead.primeraLlamada) : 'Pendiente'}</div></div>
                <div>
                  <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>CITA / AGENDA</div>
                  <div style={{ fontSize: 13, color: '#1f2a37' }}>
                    {lead.citaAgendada ? `${lead.tipoAgenda === 'seguimiento' ? '🚩 ' : lead.tipoAgenda === 'llamada' ? '📞 ' : '📅 '}${fichaFmt(lead.citaAgendada)}` : '—'}
                    {hayCita && (
                      <button onClick={abrirPanelCita} title="Modificar la fecha y hora de esta cita"
                        style={{ marginLeft: 6, border: `1px solid ${C.naranja}44`, background: `${C.naranja}14`, color: C.naranja, borderRadius: 10, padding: '1px 7px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer' }}>✏️ Modificar</button>
                    )}
                    {lead.asistenciaCita && <span style={{ marginLeft: 6, background: lead.asistenciaCita === 'Ha venido' ? `${C.verde}14` : `${C.rojo}14`, color: lead.asistenciaCita === 'Ha venido' ? C.verde : C.rojo, borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{lead.asistenciaCita === 'Ha venido' ? '✅ Ha venido' : '❌ No ha venido'}</span>}
                  </div>
                  {hayCita && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, color: '#8a929c', fontWeight: 600, marginBottom: 3 }}>¿HA VENIDO EL CLIENTE?</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => marcarAsistencia('Ha venido')} disabled={guardando}
                          style={{ padding: '4px 9px', borderRadius: 10, border: `1px solid ${C.verde}55`, background: lead.asistenciaCita === 'Ha venido' ? `${C.verde}14` : '#fff', color: C.verde, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>✅ Ha venido</button>
                        <button onClick={() => marcarAsistencia('No ha venido')} disabled={guardando}
                          style={{ padding: '4px 9px', borderRadius: 10, border: `1px solid ${C.rojo}55`, background: lead.asistenciaCita === 'No ha venido' ? `${C.rojo}14` : '#fff', color: C.rojo, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>❌ No ha venido</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="fg-card" style={tarjeta}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ ...tituloTarjeta, marginBottom: 0 }}>📎 Adjuntos ({adjuntos.length})</div>
                <button onClick={() => fileRef.current?.click()} disabled={subiendo}
                  style={{ padding: '5px 12px', borderRadius: 10, border: '1px dashed #1a9cd4', background: '#fff', color: '#005187', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: subiendo ? 0.6 : 1 }}>
                  {subiendo ? 'Subiendo…' : '+ Subir'}
                </button>
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={e => subirAdjunto([...e.target.files])} style={{ display: 'none' }} />
              </div>
              {adjuntos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {adjuntos.map(a => (
                    <div key={a.id} style={{ width: 70 }}>
                      <div onClick={() => fichaEsImg(a) ? setVisor(a) : window.open(a.url, '_blank')} title={a.nombre}
                        style={{ width: 70, height: 56, borderRadius: 10, border: '1px solid #e7e9ec', background: '#f4f5f7', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {fichaEsImg(a)
                          ? <img src={a.url} alt={a.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 20 }}>{(a.mime || '').includes('pdf') ? '📄' : '📁'}</span>}
                      </div>
                      <div style={{ fontSize: 9.5, color: '#8a929c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{a.nombre}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {lead.observaciones && (
              <div className="fg-card" style={tarjeta}>
                <div style={tituloTarjeta}>Observaciones</div>
                <div style={{ fontSize: 13, color: '#2b333d', whiteSpace: 'pre-wrap' }}>{lead.observaciones}</div>
              </div>
            )}
          </div>

          {/* ══ DERECHA ══ */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="fg-card" style={tarjeta}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={tituloTarjeta}>👤 Cliente</div>
                {!editando && <button onClick={empezarEdicion} style={{ padding: '4px 10px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>✏️ Editar</button>}
              </div>
              {editando ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {[['nombre', 'Nombre'], ['apellidos', 'Apellidos'], ['razonSocial', 'Razón social'], ['telefono', 'Teléfono'], ['movil', 'Móvil'], ['email', 'Email'], ['cp', 'CP'], ['provincia', 'Provincia'], ['marca', 'Marca'], ['modelo', 'Modelo'], ['matricula', 'Matrícula'], ['kilometros', 'Kilómetros']].map(([k, lbl]) => (
                      <div key={k}>
                        <label style={{ display: 'block', fontSize: 10.5, fontWeight: 600, color: '#8a929c', marginBottom: 2 }}>{lbl}</label>
                        <input value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={{ ...inp, width: '100%', fontSize: 12.5 }} />
                      </div>
                    ))}
                    <div>
                      <label style={{ display: 'block', fontSize: 10.5, fontWeight: 600, color: '#8a929c', marginBottom: 2 }}>Tipo venta</label>
                      <select value={form.tipoVenta} onChange={e => setForm(f => ({ ...f, tipoVenta: e.target.value }))} style={{ ...inp, width: '100%', fontSize: 12.5 }}>
                        <option value="">—</option><option value="VN">VN</option><option value="VO">VO</option><option value="B2B">B2B</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                    <button onClick={() => setEditando(false)} style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 12.5, cursor: 'pointer' }}>Cancelar</button>
                    <button onClick={guardarEdicion} disabled={guardando} style={{ padding: '6px 16px', borderRadius: 10, border: 'none', background: '#005187', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', opacity: guardando ? 0.6 : 1 }}>Guardar</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>NOMBRE</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2a37' }}>{nombreCompleto}</div>
                    <div style={{ fontSize: 11.5, color: '#8a929c', marginTop: 2 }}>{lead.tipoCliente || 'Particular'}{lead.cp || lead.provincia ? ` · ${[lead.cp, lead.provincia].filter(Boolean).join(' ')}` : ''}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>TELÉFONOS</div>
                    {telefonoLead ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <a href={`tel:${telefonoLead}`} style={{ fontSize: 14, fontWeight: 600, color: '#005187' }}>{telefonoLead}</a>
                        <button onClick={() => dimoLlamarDesdeCrm(telefonoLead, lead)}
                          title="Llama a este número desde tu móvil con Dimo Phone (ten la app abierta)"
                          style={{ padding: '3px 10px', borderRadius: 10, border: `1px solid ${C.verde}55`, background: `${C.verde}14`, fontSize: 11.5, fontWeight: 800, color: C.verde, cursor: 'pointer' }}>📲 Llamar</button>
                      </div>
                    ) : <div style={{ fontSize: 13, color: '#8a929c' }}>—</div>}
                    {lead.telefono && lead.movil && lead.telefono !== lead.movil && <div><a href={`tel:${lead.telefono}`} style={{ fontSize: 12.5, color: '#005187' }}>{lead.telefono}</a></div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>EMAIL</div>
                    {lead.email ? <a href={`mailto:${lead.email}`} style={{ fontSize: 13, fontWeight: 600, color: '#005187', wordBreak: 'break-all' }}>{lead.email}</a> : <div style={{ fontSize: 13, color: '#8a929c' }}>—</div>}
                  </div>
                </div>
              )}
            </div>

            {/* Estado */}
            <div className="fg-card" style={tarjeta}>
              <div style={tituloTarjeta}>Estado</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.keys(FICHA_CHIP).map(e => (
                  <button key={e} onClick={() => cambiarEstado(e)} disabled={guardando} className="fg-nav" style={{
                    padding: '6px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12.5, fontWeight: 800,
                    border: estActual === e ? `1.5px solid ${FICHA_CHIP[e].color}` : `1px solid ${C.line}`,
                    background: estActual === e ? FICHA_CHIP[e].bg : '#fff',
                    color: estActual === e ? FICHA_CHIP[e].color : C.muted,
                  }}>{e}</button>
                ))}
              </div>
              {motivoPendiente != null && (
                <div style={{ marginTop: 10, background: `${C.rojo}0d`, border: `1px solid ${C.rojo}33`, borderRadius: 12, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.rojo }}>Motivo de no compra:</span>
                  <select value={motivoPendiente} onChange={e => setMotivoPendiente(e.target.value)} style={inp}>
                    {MOTIVOS_NO_COMPRA.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <button onClick={confirmarCancelacion} disabled={guardando} style={{ ...botonPrimario, padding: '7px 14px', background: C.rojo, boxShadow: '0 3px 10px rgba(210,59,52,.28)', fontSize: 12.5 }}>Cancelar lead</button>
                  <button onClick={() => setMotivoPendiente(null)} className="fg-nav" style={{ ...botonSecundario, padding: '7px 12px', fontSize: 12.5 }}>Atrás</button>
                </div>
              )}
            </div>

            {/* Botonera */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              <button onClick={abrirPanelCita} style={{ ...btnAccion, border: accion === 'cita' ? `1.5px solid ${C.naranja}` : btnAccion.border }}>{hayCita ? '📅 Modif. cita' : '📅 Cita'}</button>
              <button onClick={() => setAccion(a => a === 'llamada' ? null : 'llamada')} style={{ ...btnAccion, border: accion === 'llamada' ? `1.5px solid ${C.verde}` : btnAccion.border }}>📞 Llamada</button>
              <button onClick={() => setAccion(a => a === 'seguimiento' ? null : 'seguimiento')} style={{ ...btnAccion, border: accion === 'seguimiento' ? `1.5px solid ${C.rojo}` : btnAccion.border }}>🚩 Seguim.</button>
              <button onClick={() => setAccion(a => a === 'tarea' ? null : 'tarea')} title="Programar una tarea con fecha y hora para este cliente" style={{ ...btnAccion, border: accion === 'tarea' ? `1.5px solid ${C.morado}` : btnAccion.border }}>☑️ Tarea</button>
              <button onClick={() => setAccion(a => a === 'comentario' ? null : 'comentario')} style={{ ...btnAccion, border: accion === 'comentario' ? '1.5px solid #1a9cd4' : btnAccion.border }}>🗒️ Comentario</button>
              <button onClick={handleWhatsAppFicha} disabled={!telefonoLead} style={{ ...btnAccion, color: C.verde, border: `1px solid ${C.verde}55`, opacity: telefonoLead ? 1 : 0.4 }}>💬 WhatsApp</button>
              {lead.email
                ? <a href={`mailto:${lead.email}`} style={{ ...btnAccion, textDecoration: 'none' }}>✉️ Email</a>
                : <button disabled style={{ ...btnAccion, opacity: 0.4 }}>✉️ Email</button>}
            </div>

            {/* Panel de acción */}
            {accion && (
              <div style={{ ...tarjeta, background: `${C.brand}0d`, border: `1px solid ${C.brand}33` }}>
                {accion === 'cita' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#005187' }}>📅 {hayCita ? 'Nueva fecha y hora de la cita:' : 'Fecha y hora de la cita:'}</span>
                    <input type="datetime-local" value={inCita} onChange={e => setInCita(e.target.value)} style={inp} autoFocus />
                    <span style={{ fontSize: 12, color: '#8a929c' }}>
                      {hayCita
                        ? `Ahora está el ${fichaFmt(lead.citaAgendada)}. Al guardar se cambia y se borra la asistencia marcada.`
                        : 'El lead pasará a Citado.'}
                    </span>
                  </div>
                )}
                {accion === 'seguimiento' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.naranja }}>🚩 Día de seguimiento:</span>
                    <input type="date" value={inSeguimiento} onChange={e => setInSeguimiento(e.target.value)} style={inp} autoFocus />
                  </div>
                )}
                {accion === 'tarea' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.morado }}>☑️ Tarea:</span>
                    <input value={inTarea.titulo} onChange={e => setInTarea(t => ({ ...t, titulo: e.target.value }))} placeholder="Qué hay que hacer… (p. ej. enviar presupuesto)" style={{ ...inp, flex: 1, minWidth: 200 }} autoFocus />
                    <input type="date" value={inTarea.fecha} onChange={e => setInTarea(t => ({ ...t, fecha: e.target.value }))} title="Día" style={inp} />
                    <input type="time" value={inTarea.hora} onChange={e => setInTarea(t => ({ ...t, hora: e.target.value }))} title="Hora (opcional)" style={inp} />
                    <span style={{ fontSize: 11.5, color: C.muted, width: '100%' }}>Te saldrá en «Mis tareas» y en tu Agenda ese día.</span>
                  </div>
                )}
                {accion === 'llamada' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {telefonoLead && <a href={`tel:${telefonoLead}`} style={{ fontSize: 13, fontWeight: 700, color: '#005187' }}>📞 {telefonoLead}</a>}
                    {telefonoLead && (
                      <button onClick={() => dimoLlamarDesdeCrm(telefonoLead, lead)}
                        title="Llama a este número desde tu móvil con Dimo Phone (ten la app abierta)"
                        style={{ padding: '4px 10px', borderRadius: 10, border: `1px solid ${C.verde}55`, background: `${C.verde}14`, fontSize: 12, fontWeight: 800, color: C.verde, cursor: 'pointer' }}>📲 Marcar en el móvil</button>
                    )}
                    <select value={inLlamada.resultado} onChange={e => setInLlamada(l => ({ ...l, resultado: e.target.value }))} style={inp}>
                      {['Contactado', 'No contesta', 'Buzón de voz', 'Número erróneo', 'Volver a llamar'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input value={inLlamada.nota} onChange={e => setInLlamada(l => ({ ...l, nota: e.target.value }))} placeholder="Nota breve (opcional)…" style={{ ...inp, flex: 1, minWidth: 160 }} />
                  </div>
                )}
                {accion === 'comentario' && (
                  <div>
                    {respondiendoA && (
                      <div style={{ fontSize: 12, color: '#005187', background: `${C.brand}0d`, border: '1px solid #1a9cd4', borderRadius: 10, padding: '6px 10px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span>↳ Respondiendo a <strong>{respondiendoA.usuario}</strong>: “{String(respondiendoA.texto).slice(0, 80)}{String(respondiendoA.texto).length > 80 ? '…' : ''}”</span>
                        <button onClick={() => setRespondiendoA(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#005187', fontWeight: 700, padding: 0 }}>✕</button>
                      </div>
                    )}
                    <textarea value={inComentario} onChange={e => setInComentario(e.target.value)} rows={2}
                      onPaste={async (e) => {
                        const cd = e.clipboardData; if (!cd || !lead?.id) return;
                        const files = [];
                        for (const it of [...(cd.items || [])]) {
                          if (it.kind === 'file' && (it.type || '').startsWith('image/')) {
                            const f = it.getAsFile(); if (f) files.push(f);
                          }
                        }
                        for (const f of [...(cd.files || [])]) {
                          if ((f.type || '').startsWith('image/') && !files.includes(f)) files.push(f);
                        }
                        let urlHtml = null;
                        if (!files.length) {
                          const html = cd.getData('text/html') || '';
                          const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
                          if (m) urlHtml = m[1];
                        }
                        if (!files.length && !urlHtml) return;
                        e.preventDefault();
                        try {
                          const urls = [];
                          for (const f of files) urls.push(await subirImagenComentarioCrm(lead.id, f));
                          if (urlHtml) {
                            const blob = await (await fetch(urlHtml)).blob();
                            urls.push(await subirImagenComentarioCrm(lead.id,
                              new File([blob], 'captura.png', { type: blob.type || 'image/png' })));
                          }
                          setInComentario(v => `${v}${v && !v.endsWith('\n') ? '\n' : ''}${urls.join('\n')}\n`);
                        } catch (err) {
                          alert('No se pudo subir la imagen pegada: ' + (err?.message || 'error'));
                        }
                      }}
                      placeholder={respondiendoA ? 'Escribe tu respuesta…' : 'Escribe un comentario… (@nombre crea tarea · puedes pegar una captura)'}
                      style={{ ...inp, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} autoFocus />
                    <div style={{ fontSize: 11.5, color: '#8a929c', marginTop: 4 }}>
                      💡 Menciona con <strong>@nombre</strong> (p. ej. @{RECEPCION_NOMBRE_CRM}) y se le creará una tarea en su perfil automáticamente.
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button onClick={() => { setAccion(null); setRespondiendoA(null); }} style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 12.5, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={ejecutarAccion} disabled={guardando} style={{ padding: '7px 18px', borderRadius: 10, border: 'none', background: '#005187', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', opacity: guardando ? 0.6 : 1 }}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}

            {/* Llamadas registradas por Dimo Phone */}
            <DimoLlamadasCard leadId={lead.id} telefonos={[lead.movil, lead.telefono]} />

            {/* Timeline */}
            <div className="fg-card" style={tarjeta}>
              <div style={tituloTarjeta}>🕓 Actividad con el cliente ({timeline.length})</div>
              {timeline.length === 0 && <div style={{ fontSize: 13, color: '#8a929c' }}>Sin actividad todavía.</div>}
              <div style={{ display: 'grid', gap: 8, maxHeight: 440, overflowY: 'auto' }}>
                {timeline.map((it, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, background: '#f4f5f7', borderRadius: 10, padding: '9px 12px', borderLeft: `4px solid ${COLOR[it.tipo] || '#8a929c'}`, alignItems: 'flex-start', marginLeft: it.comentario?.respuesta_a ? 22 : 0 }}>
                    <span style={{ fontSize: 15 }}>{it.comentario?.respuesta_a ? '↳' : (ICONO[it.tipo] || '•')}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {it.comentario?.respuesta_a && (
                        <div style={{ fontSize: 10.5, color: '#1a9cd4', fontWeight: 600, marginBottom: 2 }}>
                          En respuesta a {it.padre?.usuario || 'un comentario'}{it.padre ? `: “${String(it.padre.texto).slice(0, 60)}${String(it.padre.texto).length > 60 ? '…' : ''}”` : ''}
                        </div>
                      )}
                      {it.tipo === 'comentario' ? (
                        /* Comentarios en bloque: quién y cuándo arriba, el texto abajo con sus saltos de línea */
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#1a9cd4' }}>
                            {it.quien || 'Comentario'} <span style={{ fontWeight: 400, color: '#8a929c' }}>— {fichaFmt(it.fecha)}{it.comentario?.editado_fecha ? ' · ✏️ editado' : ''}</span>
                          </div>
                          {editandoCom && it.comentario && editandoCom.fecha === it.comentario.fecha ? (
                            <div style={{ marginTop: 6 }}>
                              <textarea value={editandoCom.texto} onChange={e => setEditandoCom(ec => ({ ...ec, texto: e.target.value }))} rows={3} autoFocus
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #1a9cd4', borderRadius: 10, padding: '10px 12px', fontSize: 13.5, color: '#1f2a37', lineHeight: 1.55, resize: 'vertical' }} />
                              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                <button onClick={guardarComentarioEditado} disabled={!editandoCom.texto.trim()}
                                  style={{ padding: '6px 14px', borderRadius: 10, border: 'none', background: '#005187', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Guardar</button>
                                <button onClick={() => setEditandoCom(null)}
                                  style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', color: '#8a929c', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
                              </div>
                            </div>
                          ) : (
                          <div style={{ marginTop: 6, background: '#fff', border: '1px solid #e7e9ec', borderRadius: 10, padding: '10px 12px', fontSize: 13.5, color: '#1f2a37', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>
                            {formatearComentarioLargo(it.texto)}
                          </div>
                          )}
                          {Array.isArray(it.comentario?.imagenes) && it.comentario.imagenes.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                              {it.comentario.imagenes.map((img, j) => (
                                <img key={j} src={img.url} alt={img.nombre || 'captura'} title="Clic para ampliar"
                                  onClick={() => setVisor({ url: img.url, nombre: img.nombre || 'Captura' })}
                                  style={{ width: 76, height: 58, objectFit: 'cover', borderRadius: 10, border: '1px solid #e7e9ec', cursor: 'zoom-in' }} />
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, color: '#1f2a37', wordBreak: 'break-word' }}>
                            {it.adjunto
                              ? <span onClick={() => fichaEsImg(it.adjunto) ? setVisor(it.adjunto) : window.open(it.adjunto.url, '_blank')} style={{ cursor: 'pointer', color: '#005187', fontWeight: 600 }}>{it.texto}</span>
                              : it.texto}
                          </div>
                          <div style={{ fontSize: 11, color: '#8a929c', marginTop: 2 }}>
                            {fichaFmt(it.fecha)}{it.quien ? ` — ${it.quien}` : ''}
                          </div>
                        </>
                      )}
                    </div>
                    {it.comentario && (
                      <button
                        onClick={() => setEditandoCom({ fecha: it.comentario.fecha, texto: it.comentario.texto || '' })}
                        title="Editar este comentario"
                        style={{ border: `1px solid ${C.naranja}44`, background: `${C.naranja}14`, color: C.naranja, borderRadius: 10, fontSize: 11, fontWeight: 600, padding: '3px 9px', cursor: 'pointer', whiteSpace: 'nowrap', marginRight: 6 }}>
                        ✏️ Editar
                      </button>
                    )}
                    {it.comentario && (
                      <button
                        onClick={() => { setRespondiendoA(it.comentario); setAccion('comentario'); }}
                        title={`Responder a ${it.quien || 'este comentario'}`}
                        style={{ border: '1px solid #1a9cd4', background: `${C.brand}0d`, color: '#005187', borderRadius: 10, fontSize: 11, fontWeight: 600, padding: '3px 9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ↳ Responder
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visor de imagen */}
      {visor && (
        <div onClick={() => setVisor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, cursor: 'zoom-out' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={visor.url} alt={visor.nombre} style={{ maxWidth: '90vw', maxHeight: '84vh', borderRadius: 10 }} />
            <div style={{ color: '#fff', fontSize: 13, marginTop: 8 }}>{visor.nombre} · <a href={visor.url} target="_blank" rel="noreferrer" style={{ color: '#1a9cd4' }}>Abrir original ↗</a></div>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileLeadCard = ({ lead, onSelect, onCall, onWhatsApp }) => {
  const estadoInfo = ESTADOS_MOBILE[lead.estado] || { color: '#fff', bg: C.muted, icon: '?' };
  const nombreCompleto = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim() || 'Sin vehículo';
  const telefono = lead.movil || lead.telefono || '';

  return (
    <div
      onClick={() => onSelect(lead)}
      className="fg-card"
      style={{
        ...tarjeta,
        padding: '14px 16px',
        marginBottom: '10px',
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: C.ink, marginBottom: '6px' }}>{nombreCompleto}</div>
          <span style={pildora(estadoInfo.bg)}>
            {lead.estado}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {telefono && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); onCall(telefono); }}
                style={{ ...botonPrimario, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                📞 Llamar
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onWhatsApp(lead); }}
                style={{ ...botonSecundario, color: C.verde, border: `1px solid ${C.verde}55`, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                💬 WhatsApp
              </button>
            </>
          )}
        </div>
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: C.page, fontSize: '13px', marginBottom: '4px' }}>
          <span>🚗</span> <strong>{vehiculo}</strong>
        </div>
        {(lead.motor || lead.combustible) && <div style={{ color: C.muted, fontSize: '12.5px', marginLeft: '22px' }}>{lead.motor} {lead.combustible && `• ${lead.combustible}`}</div>}
        {lead.campana && (
          <div style={{ ...pildora(C.muted), marginTop: '6px' }}>
            📍 {lead.campana}
          </div>
        )}
        {lead.citaAgendada && (
          <div style={{ ...pildora(C.brand), marginTop: '6px' }}>
            {lead.tipoAgenda === 'seguimiento' ? '🚩' : lead.tipoAgenda === 'llamada' ? '📞' : '📅'}{' '}
            {new Date(lead.citaAgendada).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
            {lead.tipoAgenda !== 'seguimiento' ? ` · ${new Date(lead.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} h` : ''}
          </div>
        )}
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: SEPARADOR_FILA }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: C.faint, fontSize: '12.5px' }}>
          <span>🕐</span> {formatTimeMobile(lead.fechaCreacion)} {formatRelativeDate(lead.fechaCreacion)}
          {lead.fechaTraspaso && lead.fechaEntrada === lead.fechaTraspaso && (
            <span style={{ color: '#005187', fontWeight: 600 }} title="Cuándo pasó a tu cartera">· ↪ te llegó {formatRelativeDate(lead.fechaTraspaso)}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {lead.comentarios && lead.comentarios.length > 0 && <span title="Tiene comentarios" style={{ fontSize: '14px' }}>💬 {lead.comentarios.length}</span>}
          {lead.noContesta > 0 && <span title="No contesta" style={{ fontSize: '14px' }}>📵 {lead.noContesta}</span>}
          <span style={{ color: C.faint, fontSize: '18px' }}>›</span>
        </div>
      </div>
    </div>
  );
};

// Modal de cambio de estado móvil
const MobileStatusModal = ({ lead, onClose, onUpdate, comercialNombre }) => {
  const [selectedEstado, setSelectedEstado] = useState(lead.estado);
  const [motivoNoCompra, setMotivoNoCompra] = useState(lead.motivoNoCompra || '');
  const [citaAgendada, setCitaAgendada] = useState(lead.citaAgendada ? new Date(lead.citaAgendada).toISOString().slice(0, 16) : '');
  // Tipo de agenda manual cuando el estado NO es "Citado": '', 'llamada' o 'seguimiento'.
  const [tipoManual, setTipoManual] = useState(
    (lead.tipoAgenda === 'llamada' || lead.tipoAgenda === 'seguimiento') && lead.citaAgendada ? lead.tipoAgenda : ''
  );
  const [saving, setSaving] = useState(false);

  const estadosCancelacion = ['Cancelado'];
  const mostrarMotivo = estadosCancelacion.includes(selectedEstado);
  const mostrarFechaCita = selectedEstado === 'Citado';
  const esSeguimiento = !mostrarFechaCita && tipoManual === 'seguimiento';
  const mostrarFechaLlamada = !mostrarFechaCita && tipoManual === 'llamada';
  const mostrarInputFecha = mostrarFechaCita || mostrarFechaLlamada || esSeguimiento;

  const handleSave = async () => {
    // Validación: si el estado es "Citado", la fecha y hora de cita es obligatoria
    if (selectedEstado === 'Citado' && !citaAgendada) {
      alert('Para cambiar el estado a "Citado" debes indicar la fecha y hora de la cita');
      return;
    }
    // Motivo OBLIGATORIO al cancelar (petición Adriana 26/08): no puede quedar en blanco
    if (mostrarMotivo && !String(motivoNoCompra || '').trim()) {
      alert('Para cancelar el lead tienes que indicar el motivo de la cancelación.');
      return;
    }
    if (mostrarFechaLlamada && !citaAgendada) {
      alert('Indica la fecha y hora de la llamada');
      return;
    }
    if (esSeguimiento && !citaAgendada) {
      alert('Indica el día de seguimiento');
      return;
    }

    setSaving(true);
    const now = new Date().toISOString();

    let nuevaFechaAgendada = lead.citaAgendada;
    let nuevoTipoAgenda = lead.tipoAgenda;
    if (mostrarFechaCita && citaAgendada) {
      nuevaFechaAgendada = new Date(citaAgendada);
      nuevoTipoAgenda = 'cita';
    } else if (mostrarFechaLlamada && citaAgendada) {
      nuevaFechaAgendada = new Date(citaAgendada);
      nuevoTipoAgenda = 'llamada';
    } else if (esSeguimiento && citaAgendada) {
      // Seguimiento se fija a nivel de día (sin hora obligatoria): 09:00 por defecto.
      nuevaFechaAgendada = new Date(citaAgendada.length === 10 ? citaAgendada + 'T09:00' : citaAgendada);
      nuevoTipoAgenda = 'seguimiento';
    } else if (!mostrarFechaCita && !tipoManual && (lead.tipoAgenda === 'llamada' || lead.tipoAgenda === 'seguimiento')) {
      // El usuario quitó la agenda manual -> limpiar el recordatorio
      nuevaFechaAgendada = null;
      nuevoTipoAgenda = null;
    }

    const updates = {
      estado: selectedEstado,
      motivoNoCompra: mostrarMotivo ? motivoNoCompra : lead.motivoNoCompra,
      citaAgendada: nuevaFechaAgendada,
      tipoAgenda: nuevoTipoAgenda,
      fechaModificacion: now,
      historial: [...(lead.historial || []), {
        fecha: now,
        estado: selectedEstado,
        usuario: comercialNombre,
        accion: nuevoTipoAgenda === 'llamada' && lead.tipoAgenda !== 'llamada'
          ? 'Programada llamada'
          : 'Cambio de estado'
      }]
    };

    const result = await updateLeadInSupabase(lead.id, updates);
    if (result.success) {
      onUpdate(lead.id, updates);
      onClose();
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,28,43,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', width: '100%', borderRadius: '14px 14px 0 0', padding: '24px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Cambiar Estado</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9aa4af' }}>×</button>
        </div>
        
        <div style={{ marginBottom: '20px', padding: '12px', background: '#f4f5f7', borderRadius: 10 }}>
          <div style={{ fontWeight: 600, color: '#1f2a37' }}>{lead.nombre} {lead.apellidos}</div>
          <div style={{ fontSize: '13px', color: '#8a929c' }}>{lead.marca} {lead.modelo}</div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
          {ESTADOS_ORDEN_MOBILE.map(estado => {
            const info = ESTADOS_MOBILE[estado] || { color: '#fff', bg: '#8a929c' };
            const isSelected = selectedEstado === estado;
            return (
              <button
                key={estado}
                onClick={() => setSelectedEstado(estado)}
                style={{
                  padding: '12px 10px',
                  borderRadius: 10,
                  border: isSelected ? `2px solid ${info.bg}` : '2px solid #e7e9ec',
                  background: isSelected ? info.bg : 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: isSelected ? info.color : '#2b333d'
                }}
              >
                {estado}
              </button>
            );
          })}
        </div>

        {!mostrarFechaCita && (
          <div style={{ marginBottom: '20px', padding: '12px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2a37', marginBottom: '8px' }}>🗓️ Agendar próxima acción</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[
                { key: '', label: 'Ninguna' },
                { key: 'llamada', label: '📞 Llamada' },
                { key: 'seguimiento', label: '🚩 Seguimiento' }
              ].map(opt => {
                const on = tipoManual === opt.key;
                return (
                  <button
                    key={opt.key || 'none'}
                    type="button"
                    onClick={() => {
                      setTipoManual(opt.key);
                      // Al pasar a un tipo con hora, completa la hora si solo había día.
                      if (opt.key === 'llamada' && citaAgendada && citaAgendada.length === 10) setCitaAgendada(citaAgendada + 'T10:00');
                    }}
                    style={{
                      padding: '10px 6px', borderRadius: 10,
                      border: on ? '2px solid #005187' : '2px solid #e7e9ec',
                      background: on ? `${C.brand}0d` : 'white',
                      color: on ? '#005187' : '#2b333d',
                      fontSize: '12px', fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {tipoManual && (
              <div style={{ fontSize: '11px', color: '#8a929c', marginTop: '8px' }}>
                Saldrá de "pendientes de hoy" hasta ese día y aparecerá en tu agenda.
              </div>
            )}
          </div>
        )}

        {mostrarInputFecha && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#2b333d' }}>
              {mostrarFechaCita ? '📅 Fecha y hora de cita *' : mostrarFechaLlamada ? '📞 Fecha y hora de la llamada *' : '🚩 Día de seguimiento *'}
            </label>
            <input
              type={esSeguimiento ? 'date' : 'datetime-local'}
              value={esSeguimiento ? (citaAgendada ? citaAgendada.slice(0, 10) : '') : citaAgendada}
              onChange={(e) => setCitaAgendada(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {mostrarMotivo && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d23b34' }}>Motivo de cancelación (obligatorio)</label>
            <select
              value={motivoNoCompra}
              onChange={(e) => setMotivoNoCompra(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '14px' }}
            >
              <option value="">Seleccionar motivo...</option>
              {MOTIVOS_NO_COMPRA.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
        
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '16px',
            background: saving ? '#e7e9ec' : '#005187',
            color: saving ? '#9aa4af' : 'white',
            border: 'none',
            borderRadius: 12,
            fontSize: '16px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>
    </div>
  );
};

// Detalle de Lead Móvil completo
const MobileLeadDetail = ({ lead, onClose, onUpdate, onChangeStatus, comercialNombre }) => {
  const estadoInfo = ESTADOS_MOBILE[lead.estado] || { color: '#8a929c', bg: '#f4f5f7', icon: '?' };
  const nombreCompleto = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  const telefono = lead.movil || lead.telefono || '';
  const [activeSection, setActiveSection] = useState('info');
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [editandoCom, setEditandoCom] = useState(null); // comentario en edición { fecha, texto }
  const guardarComentarioEditado = async () => {
    if (!editandoCom || !editandoCom.texto.trim()) return;
    const lista = Array.isArray(lead.comentarios) ? lead.comentarios : [];
    const comentarios = lista.map(cc => cc.fecha === editandoCom.fecha
      ? { ...cc, texto: editandoCom.texto.trim(), editado_fecha: new Date().toISOString(), editado_por: comercialNombre }
      : cc);
    const result = await updateLeadInSupabase(lead.id, { comentarios });
    if (result.success) { onUpdate(lead.id, { comentarios }); setEditandoCom(null); }
  };
  
  const handleCall = () => {
    if (telefono) dimoLlamarDesdeCrm(telefono, lead);
  };

  // Cita de verdad (no seguimiento ni llamada programada)
  const tieneCita = !!lead.citaAgendada && lead.tipoAgenda !== 'seguimiento' && lead.tipoAgenda !== 'llamada';
  const marcarAsistenciaMovil = async (valor) => {
    const v = lead.asistenciaCita === valor ? null : valor;
    const entrada = { fecha: new Date().toISOString(), usuario: comercialNombre, estado: lead.estado,
      accion: v ? `📅 Cita — el cliente ${v === 'Ha venido' ? 'HA VENIDO' : 'NO ha venido'}` : '📅 Asistencia a la cita borrada' };
    const updates = { asistenciaCita: v, historial: [...(Array.isArray(lead.historial) ? lead.historial : []), entrada] };
    const result = await updateLeadInSupabase(lead.id, updates);
    if (result.success) onUpdate(lead.id, updates);
    else alert('No se pudo guardar la asistencia.');
  };

  const handleWhatsApp = () => {
    if (telefono) {
      const phone = telefono.replace(/\D/g, '');
      const fullPhone = phone.startsWith('34') ? phone : `34${phone}`;
      const nombre = lead.nombre || 'cliente';
      const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
      const mensaje = `Hola ${nombre}! soy ${comercialNombre || 'tu asesor'} del Grupo Dimoauto. Veo que estás interesado en el ${vehiculo || 'vehículo'}`;
      window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(mensaje)}`, '_blank');
      // Solo se registra como enviado si de verdad se pudo enviar (hay clientes sin WhatsApp)
      setTimeout(() => {
        const enviado = window.confirm('¿Se ha podido enviar el WhatsApp?\n\nAceptar = registrar como ENVIADO\nCancelar = el número NO tiene WhatsApp (queda registrado el aviso)');
        const entradaWa = { fecha: new Date().toISOString(), accion: enviado
          ? `💬 WhatsApp enviado a ${telefono} — «${mensaje.slice(0, 120)}${mensaje.length > 120 ? '…' : ''}»`
          : `⚠️ WhatsApp NO enviado a ${telefono} — el número no tiene WhatsApp`, usuario: comercialNombre, estado: lead.estado };
        const historialWa = [...(Array.isArray(lead.historial) ? lead.historial : []), entradaWa];
        updateLeadInSupabase(lead.id, { historial: historialWa }).then(r => { if (r.success) onUpdate(lead.id, { historial: historialWa }); }).catch(() => {});
      }, 400);
    }
  };

  const handleAddComment = async () => {
    if (!nuevoComentario.trim()) return;
    const now = new Date().toISOString();
    const newComment = { fecha: now, usuario: comercialNombre, texto: nuevoComentario.trim() };
    const updatedComments = [...(lead.comentarios || []), newComment];
    // Persistir en Supabase antes del update optimista; handleUpdateLead solo
    // toca estado local, así que sin esto los comentarios se perdían al recargar.
    const result = await updateLeadInSupabase(lead.id, { comentarios: updatedComments });
    if (result.success) {
      onUpdate(lead.id, { comentarios: updatedComments });
      // @menciones: crear tarea automática a la persona mencionada
      try {
        const menciones = await procesarMencionesCrm(lead, nuevoComentario, comercialNombre);
        if (menciones.length) alert(`✅ Tarea creada para ${menciones.map(m => m.persona).join(', ')}.`);
      } catch (e) { /* noop */ }
      setNuevoComentario('');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f4f5f7', zIndex: 1000, overflowY: 'auto', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #005187 0%, #1a9cd4 100%)', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, padding: '8px 12px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'white', fontWeight: 600, fontSize: '16px' }}>{nombreCompleto}</div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>{lead.marca} {lead.modelo}</div>
        </div>
      </div>
      
      {/* Estado clickeable */}
      <div style={{ padding: '16px' }}>
        <button 
          onClick={onChangeStatus}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: 10,
            border: 'none',
            background: estadoInfo.bg,
            color: estadoInfo.color,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {lead.estado} <span style={{ marginLeft: 'auto', opacity: 0.7 }}>Cambiar ›</span>
        </button>
      </div>
      
      {/* Botones de acción */}
      <div style={{ padding: '0 16px 16px', display: 'flex', gap: '12px' }}>
        <button onClick={handleCall} style={{ flex: 1, padding: '14px', background: '#005187', color: 'white', border: 'none', borderRadius: 10, fontSize: '15px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          📞 Llamar
        </button>
        <button onClick={handleWhatsApp} style={{ flex: 1, padding: '14px', background: 'white', color: '#2e9e5b', border: '2px solid #2e9e5b', borderRadius: 10, fontSize: '15px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          💬 WhatsApp
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e7e9ec', padding: '0 16px', background: 'white' }}>
        {[
          { id: 'info', label: 'Info', icon: '📋' },
          { id: 'historial', label: 'Historial', icon: '📜' },
          { id: 'comentarios', label: 'Notas', icon: '💬' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: activeSection === tab.id ? '2px solid #005187' : '2px solid transparent',
              color: activeSection === tab.id ? '#005187' : '#8a929c',
              fontWeight: activeSection === tab.id ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '13px'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
      
      {/* Contenido según tab */}
      <div style={{ padding: '16px' }}>
        {activeSection === 'info' && (
          <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f4f5f7' }}>
              <div style={{ fontSize: '12px', color: '#9aa4af', marginBottom: '4px' }}>CONTACTO</div>
              <div style={{ fontWeight: 500, color: '#1f2a37' }}>{telefono || 'Sin teléfono'}</div>
              {lead.email && <div style={{ fontSize: '14px', color: '#005187', marginTop: '4px' }}>{lead.email}</div>}
            </div>
            
            {lead.citaAgendada && (
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #f4f5f7', background: `${C.brand}0d` }}>
                <div style={{ fontSize: '12px', color: '#005187', fontWeight: 700, marginBottom: '4px' }}>
                  {lead.tipoAgenda === 'seguimiento' ? '🚩 SEGUIMIENTO' : lead.tipoAgenda === 'llamada' ? '📞 LLAMADA PROGRAMADA' : '📅 CITA AGENDADA'}
                </div>
                <div style={{ fontWeight: 600, color: '#1f2a37', textTransform: 'capitalize' }}>
                  {new Date(lead.citaAgendada).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {lead.tipoAgenda !== 'seguimiento' ? ` · ${new Date(lead.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} h` : ''}
                </div>
                {tieneCita && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#005187', fontWeight: 700, marginBottom: '5px' }}>¿HA VENIDO EL CLIENTE?</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => marcarAsistenciaMovil('Ha venido')}
                        style={{ flex: 1, padding: '9px', borderRadius: 10, border: `1px solid ${C.verde}55`, background: lead.asistenciaCita === 'Ha venido' ? `${C.verde}14` : '#fff', color: C.verde, fontSize: '13px', fontWeight: 600 }}>✅ Ha venido</button>
                      <button onClick={() => marcarAsistenciaMovil('No ha venido')}
                        style={{ flex: 1, padding: '9px', borderRadius: 10, border: `1px solid ${C.rojo}55`, background: lead.asistenciaCita === 'No ha venido' ? `${C.rojo}14` : '#fff', color: C.rojo, fontSize: '13px', fontWeight: 600 }}>❌ No ha venido</button>
                    </div>
                    <button onClick={() => onChangeStatus(lead)}
                      style={{ width: '100%', marginTop: '8px', padding: '9px', borderRadius: 10, border: `1px solid ${C.brand}33`, background: '#fff', color: '#005187', fontSize: '13px', fontWeight: 600 }}>✏️ Modificar la cita</button>
                  </div>
                )}
              </div>
            )}

            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f4f5f7' }}>
              <div style={{ fontSize: '12px', color: '#9aa4af', marginBottom: '4px' }}>VEHÍCULO DE INTERÉS</div>
              <div style={{ fontWeight: 500, color: '#1f2a37' }}>{lead.marca} {lead.modelo}</div>
              {(lead.motor || lead.combustible) && <div style={{ fontSize: '14px', color: '#8a929c', marginTop: '4px' }}>{lead.motor} {lead.combustible && `• ${lead.combustible}`}</div>}
            </div>
            
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f4f5f7' }}>
              <div style={{ fontSize: '12px', color: '#9aa4af', marginBottom: '4px' }}>ORIGEN / CAMPAÑA</div>
              <div style={{ fontWeight: 500, color: '#1f2a37' }}>{lead.origen || 'No especificado'}</div>
              {lead.campana && <div style={{ fontSize: '14px', color: '#8a929c', marginTop: '4px' }}>{lead.campana}</div>}
            </div>
            
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f4f5f7' }}>
              <div style={{ fontSize: '12px', color: '#9aa4af', marginBottom: '4px' }}>CENTRO / VENDEDOR</div>
              <div style={{ fontWeight: 500, color: '#1f2a37' }}>{lead.centro}</div>
              <div style={{ fontSize: '14px', color: '#8a929c', marginTop: '4px' }}>{lead.vendedor}</div>
            </div>
            
            <div style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '12px', color: '#9aa4af', marginBottom: '4px' }}>FECHA DE ENTRADA</div>
              <div style={{ fontWeight: 500, color: '#1f2a37' }}>
                {lead.fechaCreacion ? new Date(lead.fechaCreacion).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
              </div>
            </div>
            <AdjuntosLead leadId={lead.id} />
          </div>
        )}

        {activeSection === 'historial' && (
          <div style={{ background: 'white', borderRadius: 12, padding: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <DimoLlamadasCard leadId={lead.id} telefonos={[lead.movil, lead.telefono]} compacta />
            </div>
            {(!lead.historial || lead.historial.length === 0) ? (
              <EmptyState icon="📜" title="Sin historial" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[...(lead.historial || [])].reverse().map((h, i) => (
                  <div key={i} style={{ padding: '12px', background: '#f4f5f7', borderRadius: 10, borderLeft: '3px solid #005187' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#1f2a37' }}>{h.accion}</div>
                    <div style={{ fontSize: '12px', color: '#8a929c', marginTop: '4px' }}>
                      {h.usuario} • {new Date(h.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === 'comentarios' && (
          <div>
            <div style={{ background: 'white', borderRadius: 12, padding: '16px', marginBottom: '16px' }}>
              <textarea
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Añadir una nota..."
                style={{ width: '100%', padding: '12px', border: `1.5px solid ${C.line}`, borderRadius: 10, fontSize: '14px', minHeight: '80px', resize: 'none', boxSizing: 'border-box' }}
              />
              <button
                onClick={handleAddComment}
                disabled={!nuevoComentario.trim()}
                style={{
                  marginTop: '12px',
                  width: '100%',
                  padding: '12px',
                  background: nuevoComentario.trim() ? '#005187' : '#e7e9ec',
                  color: nuevoComentario.trim() ? 'white' : '#9aa4af',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: nuevoComentario.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Guardar Nota
              </button>
            </div>
            
            {lead.comentarios && lead.comentarios.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[...(lead.comentarios || [])].reverse().map((c, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 12, padding: '14px', marginLeft: c.respuesta_a ? '18px' : 0 }}>
                    {editandoCom && editandoCom.fecha === c.fecha ? (
                      <div style={{ marginBottom: '8px' }}>
                        <textarea value={editandoCom.texto} onChange={e => setEditandoCom(ec => ({ ...ec, texto: e.target.value }))} rows={3} autoFocus
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #1a9cd4', borderRadius: 10, padding: '10px', fontSize: '14px', lineHeight: 1.5, resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '8px' }}>
                          <button onClick={guardarComentarioEditado} disabled={!editandoCom.texto.trim()}
                            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#005187', color: 'white', fontSize: '13px', fontWeight: 600 }}>Guardar</button>
                          <button onClick={() => setEditandoCom(null)}
                            style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e7e9ec', background: 'white', color: '#8a929c', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                    <div style={{ fontSize: '14px', color: '#1f2a37', marginBottom: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>{c.respuesta_a ? '↳ ' : ''}{formatearComentarioLargo(c.texto)}</div>
                    )}
                    {Array.isArray(c.imagenes) && c.imagenes.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {c.imagenes.map((img, j) => (
                          <a key={j} href={img.url} target="_blank" rel="noreferrer">
                            <img src={img.url} alt={img.nombre || 'captura'} style={{ width: 76, height: 58, objectFit: 'cover', borderRadius: 10, border: '1px solid #e7e9ec' }} />
                          </a>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#9aa4af', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span>{c.usuario} • {new Date(c.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{c.editado_fecha ? ' • ✏️ editado' : ''}</span>
                      {!editandoCom && (
                        <button onClick={() => setEditandoCom({ fecha: c.fecha, texto: c.texto || '' })}
                          style={{ border: 'none', background: 'none', color: '#005187', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>✏️ Editar</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Bottom Navigation Móvil
const MobileBottomNav = ({ activeTab, setActiveTab, counts }) => {
  const tabs = [
    { id: 'leads', label: 'Leads', count: counts.pendientes },
    { id: 'agenda', label: 'Agenda', count: counts.citas },
    { id: 'clientes', label: 'Clientes', count: null }
  ];
  
  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: C.card,
      borderTop: `1px solid ${C.line}`,
      display: 'flex',
      justifyContent: 'space-around',
      padding: '10px 16px 26px',
      gap: '8px',
      zIndex: 100
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className="fg-nav"
          style={{
            flex: 1,
            background: activeTab === tab.id ? GRAD : 'transparent',
            boxShadow: activeTab === tab.id ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
            border: 'none',
            borderRadius: 10,
            padding: '11px 8px',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <span style={{
            fontSize: '13px',
            fontWeight: 700,
            color: activeTab === tab.id ? '#fff' : C.page
          }}>
            {tab.label}
          </span>
          {tab.count > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '8px',
              background: `${C.rojo}18`,
              color: C.rojo,
              border: `1px solid ${C.rojo}44`,
              fontSize: '9.5px',
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 999,
              minWidth: '18px',
              textAlign: 'center'
            }}>{tab.count}</span>
          )}
        </button>
      ))}
    </nav>
  );
};

// Vista principal de leads móvil
const MobileLeadsView = ({ leads, onUpdateLead, comercialNombre }) => {
  const [filter, setFilter] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [mostrarAgendados, setMostrarAgendados] = useState(false);

  // Leads "Pendiente" agendados a un día futuro (snooze): ocultos de la lista de hoy.
  const agendadosCount = useMemo(() => leads.filter(estaAgendadoFuturo).length, [leads]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (!mostrarAgendados && estaAgendadoFuturo(lead)) return false;
      const matchesFilter = filter === 'todos' || lead.estado === filter;
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        (lead.nombre?.toLowerCase().includes(searchLower)) ||
        (lead.apellidos?.toLowerCase().includes(searchLower)) ||
        (lead.razonSocial?.toLowerCase().includes(searchLower)) ||
        (lead.telefono?.includes(searchTerm)) ||
        (lead.movil?.includes(searchTerm)) ||
        (lead.marca?.toLowerCase().includes(searchLower)) ||
        (lead.modelo?.toLowerCase().includes(searchLower));
      return matchesFilter && matchesSearch;
    });
  }, [leads, filter, searchTerm, mostrarAgendados]);

  const handleCall = (telefono, leadDe = null) => dimoLlamarDesdeCrm(telefono, leadDe);

  const handleWhatsApp = (lead) => {
    const telefono = lead.movil || lead.telefono || '';
    if (!telefono) return;
    const phone = telefono.replace(/\D/g, '');
    const fullPhone = phone.startsWith('34') ? phone : `34${phone}`;
    const nombre = lead.nombre || 'cliente';
    const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
    const mensaje = `Hola ${nombre}! soy ${comercialNombre || 'tu asesor'} del Grupo Dimoauto. Veo que estás interesado en el ${vehiculo || 'vehículo'}`;
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(mensaje)}`, '_blank');
  };

  const handleUpdate = (leadId, updates) => {
    onUpdateLead(leadId, updates);
    if (selectedLead && selectedLead.id === leadId) {
      setSelectedLead(prev => ({ ...prev, ...updates }));
    }
  };

  return (
    <>
      <MobileFilterBar filter={filter} setFilter={setFilter} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {agendadosCount > 0 && (
        <div style={{ padding: '8px 16px 0' }}>
          <button
            type="button"
            onClick={() => setMostrarAgendados(v => !v)}
            style={{
              width: '100%', padding: '10px', borderRadius: 10,
              border: '1px solid #e7e9ec', background: mostrarAgendados ? `${C.brand}0d` : 'white',
              color: '#005187', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            {mostrarAgendados ? `Ocultar agendados (${agendadosCount})` : `🗓️ Ver agendados a futuro (${agendadosCount})`}
          </button>
        </div>
      )}

      <div style={{ padding: '16px', paddingBottom: '100px' }}>
        {filteredLeads.length === 0 ? (
          <EmptyState icon="📭" title="No hay leads que mostrar" />
        ) : (
          filteredLeads.map(lead => (
            <MobileLeadCard 
              key={lead.id} 
              lead={lead} 
              onSelect={setSelectedLead}
              onCall={handleCall}
              onWhatsApp={handleWhatsApp}
            />
          ))
        )}
      </div>

      {selectedLead && !showStatusModal && (
        <MobileLeadDetail 
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdate}
          onChangeStatus={() => setShowStatusModal(true)}
          comercialNombre={comercialNombre}
        />
      )}

      {showStatusModal && selectedLead && (
        <MobileStatusModal 
          lead={selectedLead}
          onClose={() => setShowStatusModal(false)}
          onUpdate={(leadId, updates) => {
            handleUpdate(leadId, updates);
            setShowStatusModal(false);
          }}
          comercialNombre={comercialNombre}
        />
      )}
    </>
  );
};

// Login móvil
const MobileLoginScreen = ({ onLogin, comerciales, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    
    try {
      // 1. Autenticar con Supabase Auth
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password);
      
      if (authError) {
        setLoginError(authError);
        setIsLoading(false);
        return;
      }
      
      // 2. Buscar comercial vinculado
      const comercial = await findComercialByAuthId(authData.user.id);
      
      if (!comercial) {
        setLoginError('No tienes acceso como comercial');
        await supabaseAuth.signOut();
        setIsLoading(false);
        return;
      }
      
      if (!comercial.activo) {
        setLoginError('Usuario desactivado');
        await supabaseAuth.signOut();
        setIsLoading(false);
        return;
      }
      
      onLogin(comercial);
    } catch (err) {
      setLoginError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', overflow: 'hidden' }}>
      <AnimatedLoginBackground cardRect={null} />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '320px', background: '#ffffff', borderRadius: 14, padding: '24px 20px', boxShadow: '0 20px 60px rgba(15,42,70,0.35)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '20px' }}>
          <img src="/logotipo.png" alt="Dimoauto" style={{ display: 'block', height: '38px', margin: '0 auto 12px auto' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2a37', margin: '0 0 2px 0' }}>Portal Comercial</h1>
          <p style={{ fontSize: '11px', color: '#8a929c', margin: 0 }}>Dimoauto CRM</p>
        </div>

        {loginError && (
          <div style={{ background: `${C.rojo}14`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px', marginBottom: '14px', textAlign: 'center' }}>
            <span style={{ color: '#d23b34', fontSize: '12px' }}>⚠️ {loginError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@dimoauto.com"
            required
            style={{ width: '100%', padding: '11px 12px', marginBottom: '10px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '14px', boxSizing: 'border-box' }}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            required
            style={{ width: '100%', padding: '11px 12px', marginBottom: '14px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '14px', boxSizing: 'border-box' }}
          />
          <button
            type="submit"
            disabled={isLoading}
            style={{ width: '100%', padding: '11px', background: isLoading ? '#9aa4af' : '#005187', border: 'none', borderRadius: 10, color: 'white', fontSize: '14px', fontWeight: 600, cursor: isLoading ? 'wait' : 'pointer' }}
          >
            {isLoading ? 'Verificando...' : '🔐 Iniciar Sesión'}
          </button>
        </form>

        <div style={{ marginTop: '12px', padding: '8px', background: `${C.verde}0d`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
          <span style={{ fontSize: '12px' }}>🔒</span>
          <span style={{ fontSize: '10px', color: C.verde }}>Conexión segura</span>
        </div>
      </div>
    </div>
  );
};

// Vista de Agenda Móvil
const MobileAgendaView = ({ leads }) => {
  const citasHoy = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);
    
    return leads.filter(l => {
      if (!l.citaAgendada) return false;
      const cita = new Date(l.citaAgendada);
      return cita >= hoy && cita < manana;
    }).sort((a, b) => new Date(a.citaAgendada) - new Date(b.citaAgendada));
  }, [leads]);

  const citasProximas = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);
    
    return leads.filter(l => {
      if (!l.citaAgendada) return false;
      const cita = new Date(l.citaAgendada);
      return cita >= manana;
    }).sort((a, b) => new Date(a.citaAgendada) - new Date(b.citaAgendada));
  }, [leads]);

  const citasVencidas = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    return leads.filter(l => {
      if (!l.citaAgendada) return false;
      if (l.estado === 'Vendido' || l.estado === 'Cancelado') return false;
      const cita = new Date(l.citaAgendada);
      return cita < hoy;
    }).sort((a, b) => new Date(a.citaAgendada) - new Date(b.citaAgendada));
  }, [leads]);

  const CitaCard = ({ lead }) => {
    const nombreCompleto = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim();
    const esLlamada = lead.tipoAgenda === 'llamada';
    const esSeguimiento = lead.tipoAgenda === 'seguimiento';
    const colorAccent = esSeguimiento ? C.naranja : esLlamada ? '#2e9e5b' : '#005187';
    const tagBg = esSeguimiento ? `${C.naranja}14` : esLlamada ? `${C.verde}14` : `${C.brand}0d`;
    const tagColor = esSeguimiento ? C.naranja : esLlamada ? C.verde : '#005187';
    const tagLabel = esSeguimiento ? '🚩 SEGUIMIENTO' : esLlamada ? '📞 LLAMADA' : '📅 CITA';
    return (
      <div className="fg-card" style={{ ...tarjeta, padding: '14px 16px', marginBottom: '10px', borderLeft: `4px solid ${colorAccent}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ ...pildora(tagColor), marginBottom: '6px' }}>
              {tagLabel}
            </div>
            <div style={{ fontWeight: 800, fontSize: '13.5px', color: C.ink, marginBottom: '4px' }}>{nombreCompleto}</div>
            <div style={{ fontSize: '12.5px', color: C.muted }}>{lead.marca} {lead.modelo}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {!esSeguimiento && (
              <div style={{ fontSize: '18px', fontWeight: 700, color: colorAccent }}>
                {new Date(lead.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <div style={{ fontSize: esSeguimiento ? '15px' : '12px', fontWeight: esSeguimiento ? 700 : 400, color: esSeguimiento ? colorAccent : '#8a929c' }}>
              {new Date(lead.citaAgendada).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      {citasVencidas.length > 0 && (
        <>
          <h2 style={{ fontSize: '14px', fontWeight: 800, color: C.rojo, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Vencidas ({citasVencidas.length})
          </h2>
          {citasVencidas.map(lead => <CitaCard key={lead.id} lead={lead} />)}
        </>
      )}
      <h2 style={{ fontSize: '14px', fontWeight: 800, color: C.ink, marginBottom: '12px', marginTop: citasVencidas.length > 0 ? '20px' : 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
        📅 Hoy ({citasHoy.length})
      </h2>
      {citasHoy.length === 0 ? (
        <div className="fg-card" style={{ ...tarjeta, marginBottom: '20px' }}>
          <EmptyState icon="📅" title="No tienes citas para hoy" />
        </div>
      ) : (
        citasHoy.map(lead => <CitaCard key={lead.id} lead={lead} />)
      )}

      <h2 style={{ fontSize: '14px', fontWeight: 800, color: C.ink, marginBottom: '12px', marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🗓️ Próximos ({citasProximas.length})
      </h2>
      {citasProximas.length === 0 ? (
        <div className="fg-card" style={tarjeta}>
          <EmptyState icon="🗓️" title="No tienes citas programadas" />
        </div>
      ) : (
        citasProximas.slice(0, 10).map(lead => <CitaCard key={lead.id} lead={lead} />)
      )}
    </div>
  );
};

// Vista de Clientes Móvil
const MobileClientesView = ({ leads }) => {
  const [busqueda, setBusqueda] = useState('');

  const clientes = useMemo(() => {
    const clientesMap = {};
    leads.forEach(lead => {
      const key = lead.telefono || lead.movil || lead.email || lead.leadId;
      if (!clientesMap[key]) {
        clientesMap[key] = {
          id: key,
          nombre: lead.nombre,
          apellidos: lead.apellidos,
          razonSocial: lead.razonSocial,
          tipoCliente: lead.tipoCliente,
          telefono: lead.telefono,
          movil: lead.movil,
          email: lead.email,
          provincia: lead.provincia,
          leads: [],
          ultimaInteraccion: null
        };
      }
      clientesMap[key].leads.push(lead);
      const fecha = lead.fechaModificacion || lead.fechaCreacion;
      if (fecha && (!clientesMap[key].ultimaInteraccion || new Date(fecha) > new Date(clientesMap[key].ultimaInteraccion))) {
        clientesMap[key].ultimaInteraccion = fecha;
      }
    });
    return Object.values(clientesMap).sort((a, b) => 
      new Date(b.ultimaInteraccion) - new Date(a.ultimaInteraccion)
    );
  }, [leads]);

  const filteredClientes = useMemo(() => {
    if (!busqueda) return clientes;
    const search = busqueda.toLowerCase();
    return clientes.filter(c => 
      `${c.nombre} ${c.apellidos}`.toLowerCase().includes(search) ||
      c.razonSocial?.toLowerCase().includes(search) ||
      c.telefono?.includes(search) ||
      c.movil?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  }, [clientes, busqueda]);

  const handleCall = (telefono) => {
    if (telefono) dimoLlamarDesdeCrm(telefono);
  };

  const handleWhatsApp = (telefono) => {
    if (telefono) {
      const phone = telefono.replace(/\D/g, '');
      const fullPhone = phone.startsWith('34') ? phone : `34${phone}`;
      window.open(`https://wa.me/${fullPhone}`, '_blank');
    }
  };

  return (
    <div style={{ paddingBottom: '100px' }}>
      {/* Header con búsqueda */}
      <div style={{ background: GRAD, padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: 600 }}>
            👥 Clientes ({clientes.length})
          </h2>
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9aa4af', fontSize: '16px' }}>🔍</span>
          <input
            type="text"
            placeholder="Buscar cliente, teléfono, email..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ ...campo, width: '100%', padding: '11px 12px 11px 42px', border: 'none' }}
          />
        </div>
      </div>

      {/* Lista de clientes */}
      <div style={{ padding: '16px' }}>
        {filteredClientes.length === 0 ? (
          <EmptyState icon="👥" title="No hay clientes que mostrar" />
        ) : (
          filteredClientes.map(cliente => {
            const nombreCompleto = cliente.razonSocial || `${cliente.nombre} ${cliente.apellidos}`.trim() || 'Sin nombre';
            const telefono = cliente.movil || cliente.telefono || '';
            
            return (
              <div key={cliente.id} style={{ background: 'white', borderRadius: 12, padding: '16px', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2a37', marginBottom: '4px' }}>{nombreCompleto}</div>
                    <div style={{ fontSize: '13px', color: '#8a929c' }}>{cliente.tipoCliente} {cliente.provincia && `• ${cliente.provincia}`}</div>
                  </div>
                  <div style={{ background: '#005187', color: 'white', padding: '4px 12px', borderRadius: 14, fontSize: '13px', fontWeight: 600 }}>
                    {cliente.leads.length} lead{cliente.leads.length !== 1 ? 's' : ''}
                  </div>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  {telefono && <div style={{ fontSize: '14px', color: '#2b333d', marginBottom: '4px' }}>📱 {telefono}</div>}
                  {cliente.email && <div style={{ fontSize: '13px', color: '#005187' }}>✉️ {cliente.email}</div>}
                </div>

                {telefono && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => handleCall(telefono)}
                      style={{ flex: 1, padding: '10px', background: '#005187', color: 'white', border: 'none', borderRadius: 10, fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      📞 Llamar
                    </button>
                    <button 
                      onClick={() => handleWhatsApp(telefono)}
                      style={{ flex: 1, padding: '10px', background: 'white', color: '#2e9e5b', border: '2px solid #2e9e5b', borderRadius: 10, fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      💬 WhatsApp
                    </button>
                  </div>
                )}

                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f4f5f7', fontSize: '12px', color: '#9aa4af' }}>
                  Última interacción: {cliente.ultimaInteraccion ? new Date(cliente.ultimaInteraccion).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Vista de Perfil Móvil
const MobilePerfilView = ({ comercial, onLogout, onOpenTelegram }) => (
  <div style={{ padding: '16px', paddingBottom: '100px' }}>
    <div style={{ background: 'white', borderRadius: 12, padding: '24px', marginBottom: '16px', textAlign: 'center' }}>
      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #005187 0%, #1a9cd4 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px', color: 'white', fontWeight: 700 }}>
        {comercial.nombre?.charAt(0) || '?'}
      </div>
      <div style={{ fontSize: '19px', fontWeight: 800, color: C.ink, marginBottom: '4px' }}>{comercial.nombre}</div>
      <div style={{ fontSize: '12.5px', color: C.muted }}>{comercial.centro}</div>
      <div style={{ fontSize: '11px', color: C.faint, marginTop: '4px' }}>{comercial.email}</div>
    </div>
    
    <button 
      onClick={onOpenTelegram}
      style={{ width: '100%', padding: '16px', background: 'white', border: '1px solid #e7e9ec', borderRadius: 12, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}
    >
      <span style={{ fontSize: '24px' }}>📱</span>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 500, color: '#1f2a37' }}>Configurar Telegram</div>
        <div style={{ fontSize: '13px', color: '#8a929c' }}>{comercial.telegram_chat_id ? 'Vinculado ✓' : 'No vinculado'}</div>
      </div>
    </button>
    
    <button 
      onClick={onLogout}
      style={{ width: '100%', padding: '16px', background: `${C.rojo}14`, color: '#d23b34', border: 'none', borderRadius: 12, fontWeight: 600, cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
    >
      🚪 Cerrar Sesión
    </button>
  </div>
);

// ==================== APP MÓVIL PRINCIPAL ====================
const MobileApp = ({ comercial, comerciales, leads, isLoading, error, onLogin, onLogout, onUpdateLead, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('leads');
  const [showTelegram, setShowTelegram] = useState(false);
  const [showDimoPhone, setShowDimoPhone] = useState(false);
  const dimoVinculadoMovil = useDimoVinculado(comercial?.email || '');
  const [dimoAviso, cerrarDimoAviso] = useDimoAviso(comercial, dimoVinculadoMovil, !!comercial?.email);

  const misLeads = useMemo(() => {
    if (!comercial) return [];
    const norm = (s) => (s || '').toString().toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const nombreComercial = norm(comercial.nombre);
    if (!nombreComercial) return [];
    const out = leads.filter(lead => {
      const vendedor = norm(lead.vendedor);
      if (!vendedor) return false;
      return vendedor === nombreComercial || vendedor.includes(nombreComercial) || nombreComercial.includes(vendedor);
    });
    if (out.length === 0 && leads.length > 0 && process.env.NODE_ENV === 'development') {
      const distinct = [...new Set(leads.map(l => l.vendedor).filter(Boolean))].slice(0, 8);
      console.warn('[misLeads] 0 leads para comercial=' + comercial.nombre + ' aunque hay ' + leads.length + ' cargados. Vendedores distintos:', distinct);
    }
    return out;
  }, [leads, comercial]);

  const pendientesCount = useMemo(() => misLeads.filter(l => l.estado === 'Pendiente' && !estaAgendadoFuturo(l)).length, [misLeads]);
  const citasCount = useMemo(() => misLeads.filter(l => l.citaAgendada && l.estado !== 'Vendido' && l.estado !== 'Cancelado').length, [misLeads]);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f4f5f7' }}>
        <img src="/logotipo.png" alt="Dimoauto" style={{ height: '50px', marginBottom: '24px' }} onError={(e) => e.target.style.display = 'none'} />
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#1f2a37' }}>Portal Comercial</div>
        <div style={{ fontSize: '14px', color: '#8a929c', marginBottom: '24px' }}>Cargando...</div>
        <div style={{ width: '150px', height: '4px', background: '#e7e9ec', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, #005187, #1a9cd4)', animation: 'loading 1s ease-in-out infinite' }} />
        </div>
        <style>{`@keyframes loading { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
      </div>
    );
  }

  if (!comercial) {
    return <MobileLoginScreen onLogin={onLogin} comerciales={comerciales} error={null} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f5f7', fontFamily: 'inherit' }}>
      <MobileHeader comercial={comercial} onLogout={onLogout} onRefresh={onRefresh} isLoading={isLoading} onOpenDimoPhone={() => { setShowDimoPhone(true); cerrarDimoAviso(); }} />

      {dimoAviso && !showDimoPhone && (
        <DimoPhoneAviso
          onAbrir={() => { cerrarDimoAviso(); setShowDimoPhone(true); }}
          onCerrar={cerrarDimoAviso}
          movil
        />
      )}
      
      {activeTab === 'leads' && (
        <MobileLeadsView leads={misLeads} onUpdateLead={onUpdateLead} comercialNombre={comercial.nombre} />
      )}
      
      {activeTab === 'agenda' && (
        <MobileAgendaView leads={misLeads} />
      )}
      
      {activeTab === 'clientes' && (
        <MobileClientesView leads={misLeads} />
      )}
      
      <MobileBottomNav 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        counts={{ pendientes: pendientesCount, citas: citasCount }} 
      />

      {showTelegram && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,28,43,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', width: '100%', borderRadius: '14px 14px 0 0', padding: '24px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>📱 Configurar Telegram</h3>
              <button onClick={() => setShowTelegram(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9aa4af' }}>×</button>
            </div>
            <p style={{ color: '#8a929c', marginBottom: '16px' }}>Para vincular Telegram, usa la versión de escritorio.</p>
            <button onClick={() => setShowTelegram(false)} style={{ width: '100%', padding: '14px', background: '#005187', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {showDimoPhone && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,28,43,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowDimoPhone(false)}>
          <div style={{ background: 'white', width: '100%', borderRadius: '14px 14px 0 0', padding: '24px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>📱 Dimo Phone</h3>
              <button onClick={() => setShowDimoPhone(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9aa4af' }}>×</button>
            </div>
            <DimoPhoneComercialPanel comercial={comercial} />
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ESTILOS GLOBALES ====================
const styles = {
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #2b333d',
    background: '#2b333d',
    color: '#f4f5f7',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#9aa4af'
  },
  card: {
    background: '#2b333d',
    borderRadius: 12,
    padding: '16px',
    border: '1px solid #2b333d'
  },
  button: {
    padding: '9px 16px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 800,
    fontFamily: 'inherit'
  }
};

// ==================== COMPONENTES ====================

// Login Screen
// ==================== ANIMATED LOGIN BACKGROUND (Canvas) ====================
const AnimatedLoginBackground = React.forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({
    nodes: [],
    clickBoost: 0,
    initialized: false
  });
  
  React.useImperativeHandle(ref, () => ({
    triggerPulse: () => {
      stateRef.current.clickBoost = 1;
    }
  }));
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width, height;
    
    const numNodes = 80;
    const connectionDistance = 180;
    const nodeSpeed = 0.3;
    
    const createNodes = () => {
      const nodes = [];
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * nodeSpeed,
          vy: (Math.random() - 0.5) * nodeSpeed,
          radius: 2 + Math.random() * 2,
          baseRadius: 2 + Math.random() * 2
        });
      }
      return nodes;
    };
    
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      if (stateRef.current.nodes.length === 0) {
        stateRef.current.nodes = createNodes();
      }
    };
    
    setup();
    window.addEventListener('resize', setup);
    
    const animate = () => {
      const state = stateRef.current;
      const nodes = state.nodes;
      
      if (state.clickBoost > 0) {
        state.clickBoost = Math.max(0, state.clickBoost - 0.015);
      }
      
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, '#0a1628');
      bgGradient.addColorStop(0.5, '#0d1f3c');
      bgGradient.addColorStop(1, '#081430');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0) { node.x = 0; node.vx *= -1; }
        if (node.x > width) { node.x = width; node.vx *= -1; }
        if (node.y < 0) { node.y = 0; node.vy *= -1; }
        if (node.y > height) { node.y = height; node.vy *= -1; }
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5);
      }
      
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.6;
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3);
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            gradient.addColorStop(0, `rgba(77, 130, 188, ${boostedOpacity})`);
            gradient.addColorStop(0.5, `rgba(132, 182, 244, ${boostedOpacity * 0.8})`);
            gradient.addColorStop(1, `rgba(77, 130, 188, ${boostedOpacity})`);
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const glowRadius = node.radius * 4;
        const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius);
        const glowIntensity = 0.15 + state.clickBoost * 0.2;
        glowGradient.addColorStop(0, `rgba(132, 182, 244, ${glowIntensity})`);
        glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`);
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
        
        const nodeGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius);
        nodeGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(0.3, `rgba(196, 218, 250, ${0.8 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(1, `rgba(77, 130, 188, 0.6)`);
        ctx.fillStyle = nodeGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2;
        const cardCenterY = cardRect.y + cardRect.height / 2;
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8;
        const cardGlow = ctx.createRadialGradient(cardCenterX, cardCenterY, 0, cardCenterX, cardCenterY, glowRadius);
        const intensity = 0.1 + state.clickBoost * 0.2;
        cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`);
        cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`);
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)');
        ctx.fillStyle = cardGlow;
        ctx.fillRect(0, 0, width, height);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    stateRef.current.nodes = createNodes();
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      window.removeEventListener('resize', setup);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [cardRect]);
  
  return (
    <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />
  );
});

const LoginScreen = ({ onLogin, comerciales, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cardRect, setCardRect] = useState(null);
  const [rememberMe, setRememberMe] = useState(false); // NUEVO
  const [showForgotPassword, setShowForgotPassword] = useState(false); // NUEVO
  const [resetEmail, setResetEmail] = useState(''); // NUEVO
  const [resetMessage, setResetMessage] = useState(''); // NUEVO
  
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  
  // SEGURIDAD: Cargar solo email guardado (NUNCA guardamos contraseñas)
  useEffect(() => {
    // Limpiar contraseñas antiguas si existen (migración de seguridad)
    if (localStorage.getItem('remember-password-comercial')) {
      localStorage.removeItem('remember-password-comercial');
    }
    
    const savedEmail = localStorage.getItem('remember-email-comercial');
    const savedRemember = localStorage.getItem('remember-me-comercial');
    
    if (savedRemember === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);
  
  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }
    };
    updateCardRect();
    window.addEventListener('resize', updateCardRect);
    const timer = setTimeout(updateCardRect, 100);
    return () => { window.removeEventListener('resize', updateCardRect); clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    
    if (canvasRef.current?.triggerPulse) canvasRef.current.triggerPulse();
    
    try {
      // 1. Autenticar con Supabase Auth
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password);
      
      if (authError) {
        setLoginError(authError);
        setIsLoading(false);
        return;
      }
      
      // 2. Buscar comercial vinculado
      const comercial = await findComercialByAuthId(authData.user.id);
      
      if (!comercial) {
        setLoginError('No tienes acceso como comercial');
        await supabaseAuth.signOut();
        setIsLoading(false);
        return;
      }
      
      if (!comercial.activo) {
        setLoginError('Usuario desactivado. Contacta con administración.');
        await supabaseAuth.signOut();
        setIsLoading(false);
        return;
      }
      
      // SEGURIDAD: Guardar solo email (NUNCA guardamos contraseñas)
      if (rememberMe) {
        localStorage.setItem('remember-email-comercial', email);
        localStorage.setItem('remember-me-comercial', 'true');
      } else {
        localStorage.removeItem('remember-email-comercial');
        localStorage.removeItem('remember-me-comercial');
      }
      // Siempre limpiar cualquier contraseña que pudiera existir
      localStorage.removeItem('remember-password-comercial');
      
      // NUEVO: Registrar login en auditoría
      await registrarAuditoria('login', 'comerciales', comercial.id, {
        metodo: 'supabase_auth',
        email: comercial.email
      });
      
      onLogin(comercial);
    } catch (err) {
      setLoginError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  // NUEVO: Función para recuperar contraseña
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    
    if (!resetEmail) {
      setResetMessage('error:Por favor ingresa tu email');
      return;
    }
    
    try {
      // Flujo implícito: sin PKCE para que el enlace funcione cross-device
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          redirect_to: window.location.origin
        })
      });
      
      if (response.ok) {
        setResetMessage('success:✅ Email de recuperación enviado. Revisa tu bandeja de entrada.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage('error:No se pudo enviar el email. Verifica que el email sea correcto.');
      }
    } catch (error) {
      setResetMessage('error:Error de conexión. Intenta de nuevo más tarde.');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #e7e9ec',
    fontSize: '13px',
    background: '#f4f5f7',
    color: '#1f2a37',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#8a929c',
    marginBottom: '5px'
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0a1628',
      padding: '16px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      <div ref={cardRef} style={{
        width: '100%',
        maxWidth: '360px',
        background: 'rgba(255, 255, 255, 0.98)',
        borderRadius: 12,
        padding: '28px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
        border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
        zIndex: 1,
        backdropFilter: 'blur(10px)'
      }}>
        {/* Logo y título */}
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <div style={{
            width: '160px',
            height: '55px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px'
          }}>
            <img src="/logotipo.png" alt="Dimoauto" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}} />
          </div>
          <h1 style={{color: '#1f2a37', fontSize: '20px', fontWeight: 700, margin: '0 0 4px 0'}}>
            Portal Comercial
          </h1>
          <p style={{color: '#555', fontSize: '12px', margin: 0}}>
            Dimoauto CRM
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '14px'}}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@dimoauto.com"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'}
              onBlur={(e) => e.target.style.borderColor = '#e7e9ec'}
            />
          </div>

          <div style={{marginBottom: '12px'}}>
            <label style={labelStyle}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'}
              onBlur={(e) => e.target.style.borderColor = '#e7e9ec'}
            />
          </div>

          {/* NUEVO: Checkbox Recordar credenciales y Link Olvidé contraseña */}
          <div style={{marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', color: '#8a929c'}}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{cursor: 'pointer', width: '14px', height: '14px'}}
              />
              Recordar credenciales
            </label>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#005187',
                fontSize: '11px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {/* Error de login */}
          {loginError && (
            <div style={{
              background: 'rgba(220, 53, 69, 0.1)',
              border: '1px solid #d23b34',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: '14px',
              textAlign: 'center'
            }}>
              <span style={{color: '#d23b34', fontSize: '12px'}}>❌ {loginError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '11px',
              background: isLoading ? '#9aa4af' : 'linear-gradient(135deg, #005187, #1a9cd4)',
              border: 'none',
              borderRadius: 10,
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isLoading ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isLoading ? (
              <>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Verificando...
              </>
            ) : (
              <>🔐 Iniciar Sesión</>
            )}
          </button>
        </form>

        {/* Indicador de seguridad */}
        <div style={{
          marginTop: '16px',
          padding: '8px',
          background: `${C.verde}0d`,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <span style={{fontSize: '14px'}}>🔒</span>
          <span style={{fontSize: '10px', color: C.verde}}>
            Conexión segura con autenticación cifrada
          </span>
        </div>

        {/* NUEVO: Modal de recuperar contraseña */}
        {showForgotPassword && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15,28,43,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }} onClick={() => setShowForgotPassword(false)}>
            <div style={{
              background: 'white',
              borderRadius: 10,
              padding: '20px',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{margin: '0 0 8px 0', color: '#1f2a37', fontSize: '16px'}}>
                Recuperar Contraseña
              </h3>
              <p style={{margin: '0 0 14px 0', color: '#8a929c', fontSize: '12px'}}>
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>
              
              <form onSubmit={handleForgotPassword}>
                <div style={{marginBottom: '14px'}}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="comercial@dimoauto.com"
                    required
                    style={inputStyle}
                  />
                </div>
                
                {resetMessage && (
                  <div style={{
                    background: resetMessage.startsWith('success:') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                    border: `1px solid ${resetMessage.startsWith('success:') ? '#2e9e5b' : '#d23b34'}`,
                    borderRadius: 10,
                    padding: '10px',
                    marginBottom: '14px',
                    fontSize: '11px',
                    color: resetMessage.startsWith('success:') ? C.verde : '#d23b34'
                  }}>
                    {resetMessage.replace('success:', '').replace('error:', '')}
                  </div>
                )}
                
                <div style={{display: 'flex', gap: '8px'}}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetMessage('');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#e7e9ec',
                      border: 'none',
                      borderRadius: 10,
                      color: '#1f2a37',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'linear-gradient(135deg, #005187, #1a9cd4)',
                      border: 'none',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Enviar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Logo de Dimoauto como componente (fallback si no carga la imagen)
const DimoautoLogo = () => (
  <div style={{display: 'flex', alignItems: 'center', height: '32px'}}>
    <span style={{fontSize: '22px', fontWeight: 800, color: '#1f2a37', letterSpacing: '-1px'}}>Dimo</span>
    <span style={{fontSize: '22px', fontWeight: 800, color: 'white', background: '#1a9cd4', padding: '2px 8px', marginLeft: '0px', letterSpacing: '-1px'}}>auto</span>
  </div>
);

// Header - Estilo blanco como App principal
const Header = ({ comercial, onLogout }) => {
  const [logoError, setLogoError] = React.useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleChangePassword = async () => {
    setPasswordError('');
    
    // Validaciones
    if (!passwordForm.actual || !passwordForm.nueva || !passwordForm.confirmar) {
      setPasswordError('Todos los campos son obligatorios');
      return;
    }
    
    // Validar política de contraseña
    const passwordValidation = validatePassword(passwordForm.nueva);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      return;
    }
    
    if (passwordForm.nueva !== passwordForm.confirmar) {
      setPasswordError('Las contraseñas nuevas no coinciden');
      return;
    }

    setPasswordLoading(true);
    try {
      // Verificar contraseña actual intentando hacer login con Supabase Auth
      const verifyResponse = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: comercial.email, 
          password: passwordForm.actual 
        })
      });
      
      if (!verifyResponse.ok) {
        setPasswordError('La contraseña actual es incorrecta');
        setPasswordLoading(false);
        return;
      }

      const authData = await verifyResponse.json();
      
      // Actualizar contraseña usando Supabase Auth
      const updateResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${authData.access_token}`
        },
        body: JSON.stringify({ password: passwordForm.nueva })
      });

      if (updateResponse.ok) {
        // Actualizar el token en localStorage con el nuevo
        localStorage.setItem('sb-access-token', authData.access_token);
        if (authData.refresh_token) {
          localStorage.setItem('sb-refresh-token', authData.refresh_token);
        }
        
        setPasswordSuccess(true);
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordForm({ actual: '', nueva: '', confirmar: '' });
          setPasswordSuccess(false);
        }, 2000);
      } else {
        setPasswordError('Error al actualizar la contraseña');
      }
    } catch (error) {
      setPasswordError('Error de conexión');
    } finally {
      setPasswordLoading(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ actual: '', nueva: '', confirmar: '' });
    setPasswordError('');
    setPasswordSuccess(false);
  };
  
  return (
  <>
  {/* Cabecera con la forma de la suite: título de 19 px, una línea que explica
      para qué sirve la pantalla y el logotipo pequeño y alineado (este portal
      también se abre solo por su dominio, donde la marca no la pone nadie más). */}
  <header style={{background: C.card, padding: '14px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', borderBottom: `1px solid ${C.line}`}}>
    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
      {!logoError ? (
        <img
          src="/logotipo.png"
          alt="Dimoauto"
          style={{height: '22px', objectFit: 'contain', display: 'block', flexShrink: 0}}
          onError={() => setLogoError(true)}
        />
      ) : (
        <DimoautoLogo />
      )}
      <div>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
          <h1 style={{fontSize: '19px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-0.2px'}}>Portal Comercial</h1>
          <span style={pildora(C.verde)}>
            <span className="fg-breath" style={{width: 6, height: 6, borderRadius: '50%', background: C.verde}}></span>
            Conectado
          </span>
        </div>
        <div style={{fontSize: '12.5px', color: C.muted, marginTop: '3px'}}>Tus leads, citas y tareas del día, todo en la misma pantalla</div>
      </div>
    </div>
    <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', color: C.page}}>
      <span style={pildora(C.naranja)}>
        <span className="fg-breath" style={{width: 6, height: 6, borderRadius: '50%', background: C.naranja}}></span>
        Real-time activo
      </span>
      <span style={{fontSize: '12px', fontWeight: 700, color: C.muted}}>{comercial.centro}</span>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        {/* Botón de usuario clickable */}
        <button
          onClick={() => setShowPasswordModal(true)}
          className="fg-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: '#fff',
            borderRadius: 999,
            fontSize: '12px',
            fontWeight: 700,
            color: C.page,
            border: `1px solid ${C.line}`,
            cursor: 'pointer'
          }}
          title="Configuración de usuario"
        >
          <span>👤</span>
          <span>{comercial.nombre}</span>
        </button>
        <button
          onClick={onLogout}
          style={{...botonSecundario, padding: '6px 12px', fontSize: '12px', color: C.rojo, display: 'flex', alignItems: 'center', gap: '4px'}}
          title="Cerrar sesión"
        >
          🚪 Salir
        </button>
      </div>
    </div>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
  </header>

  {/* Modal de Cambio de Contraseña */}
  {showPasswordModal && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15,28,43,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 14,
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(15,42,70,0.35)'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px',
          background: GRAD,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px'}}>
              🔐 Cambiar Contraseña
            </div>
            <div style={{fontSize: '13px', opacity: 0.9, marginTop: '4px'}}>
              {comercial.nombre}
            </div>
          </div>
          <button
            onClick={closePasswordModal}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{padding: '24px'}}>
          {passwordSuccess ? (
            <div style={{
              textAlign: 'center',
              padding: '30px 20px'
            }}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>✅</div>
              <div style={{fontSize: '18px', fontWeight: 600, color: '#2e9e5b'}}>
                ¡Contraseña actualizada!
              </div>
              <div style={{fontSize: '14px', color: '#8a929c', marginTop: '8px'}}>
                Tu contraseña se ha cambiado correctamente
              </div>
            </div>
          ) : (
            <>
              {passwordError && (
                <div style={{
                  background: `${C.rojo}14`,
                  border: `1px solid ${C.rojo}44`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  marginBottom: '20px',
                  color: '#d23b34',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⚠️ {passwordError}
                </div>
              )}

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '6px'}}>
                  Contraseña Actual
                </label>
                <input
                  type="password"
                  value={passwordForm.actual}
                  onChange={(e) => setPasswordForm({...passwordForm, actual: e.target.value})}
                  placeholder="Introduce tu contraseña actual"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1.5px solid ${C.line}`,
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#005187'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e7e9ec'}
                />
              </div>

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '6px'}}>
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordForm.nueva}
                  onChange={(e) => setPasswordForm({...passwordForm, nueva: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1.5px solid ${C.line}`,
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#005187'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e7e9ec'}
                />
              </div>

              <div style={{marginBottom: '24px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '6px'}}>
                  Confirmar Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmar}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmar: e.target.value})}
                  placeholder="Repite la nueva contraseña"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1.5px solid ${C.line}`,
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#005187'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e7e9ec'}
                />
              </div>

              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  onClick={closePasswordModal}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f4f5f7',
                    border: '1px solid #e7e9ec',
                    borderRadius: 10,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#2b333d',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: passwordLoading ? '#9aa4af' : '#005187',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'white',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {passwordLoading ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )}
  </>
  );
};

// Navigation - Estilo azul #005187 con botones centrados
// Navegación interna en píldoras: la activa lleva el gradiente de marca, las
// demás se quedan transparentes. Mismas pestañas y mismo orden de siempre.
const Navigation = ({ activeTab, setActiveTab, leadsCount, clientesCount, citasCount, tareasCount = 0 }) => (
  <nav style={{background: C.card, padding: '10px 20px', display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap', borderBottom: `1px solid ${C.line}`}}>
    {[
      { id: 'inicio', label: 'Leads', icon: '📋', count: leadsCount },
      { id: 'agenda', label: 'Agenda', icon: '📅', count: citasCount },
      { id: 'tareas', label: 'Tareas', icon: '☑️', count: tareasCount },
      { id: 'clientes', label: 'Clientes', icon: '👥', count: clientesCount }
    ].map(tab => {
      const activa = activeTab === tab.id;
      return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className="fg-nav"
        style={{
          padding: '7px 14px',
          background: activa ? GRAD : 'transparent',
          border: 'none',
          color: activa ? '#fff' : C.page,
          fontSize: '13px',
          fontWeight: 700,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          borderRadius: 10,
          boxShadow: activa ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
        }}
      >
        <span style={{fontSize: '14px'}}>{tab.icon}</span>
        {tab.label}
        <span style={activa
          ? {borderRadius: 999, padding: '1px 7px', fontSize: '9.5px', fontWeight: 800, background: 'rgba(255,255,255,0.22)', color: '#fff'}
          : {borderRadius: 999, padding: '1px 7px', fontSize: '9.5px', fontWeight: 800, background: `${C.brand}12`, color: C.brand, border: `1px solid ${C.brand}2b`}}>{tab.count}</span>
      </button>
      );
    })}
  </nav>
);

// ============================================================
// TareasComercialPage — tareas del comercial (tabla lead_tareas,
// compartida con el Panel Recepción de Adriana). Aquí aparecen:
//  · las que le delega Adriana (o le llegan por mención @nombre)
//  · las que el propio comercial se programa con fecha y hora (petición
//    Adriana 26/08: «que puedan programarse tareas con fecha y hora y que
//    les salga en agenda») — desde aquí o desde «☑️ Tarea» en la ficha.
// Pantalla al estilo del panel de Recepción: resumen visual arriba (hoy,
// vencidas, esta semana), alta rápida y pestañas.
// ============================================================
const TareasComercialPage = ({ comercial, comerciales = [], tareas = [], onRecargar, onAbrirLead }) => {
  const [tab, setTab] = useState('pendientes'); // pendientes | futuras | hechas
  const [aviso, setAviso] = useState('');
  const [editT, setEditT] = useState(null); // { id, titulo, fecha, hora }
  const [nuevaT, setNuevaT] = useState({ titulo: '', fecha: '', hora: '' });
  const [creando, setCreando] = useState(false);

  const crearNueva = async () => {
    if (!nuevaT.titulo.trim()) return;
    setCreando(true);
    try {
      await crearTareaCrm({ leadId: null, titulo: nuevaT.titulo.trim(), fecha: nuevaT.fecha || null, hora: nuevaT.hora || null, asignadaA: comercial.nombre, quien: comercial.nombre });
      setNuevaT({ titulo: '', fecha: '', hora: '' });
      setAviso(nuevaT.fecha ? '✅ Tarea programada — te saldrá en tu Agenda ese día' : '✅ Tarea creada');
      onRecargar?.();
    } catch (e) { alert('No se pudo crear la tarea.'); }
    finally { setCreando(false); }
  };

  const abrirEditT = (t) => setEditT({
    id: t.id, titulo: t.titulo || '',
    fecha: t.fecha ? String(t.fecha).slice(0, 10) : '',
    hora: t.hora ? String(t.hora).slice(0, 5) : '',
  });

  const guardarEditT = async () => {
    if (!editT?.id) return;
    const ed = editT; setEditT(null);
    try {
      const cambios = { fecha: ed.fecha || null, hora: ed.hora || null };
      if ((ed.titulo || '').trim()) cambios.titulo = ed.titulo.trim();
      await actualizarTareaCrm(ed.id, cambios);
      onRecargar?.();
    } catch (e) { alert('No se pudo guardar la tarea.'); }
  };

  const hoy = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const enDias = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const pendientes = tareas.filter(t => !t.hecha && (!t.fecha || t.fecha <= hoy));
  const futuras = tareas.filter(t => !t.hecha && t.fecha && t.fecha > hoy);
  const hechasTodas = tareas.filter(t => !!t.hecha);
  const hechas = hechasTodas.slice().sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || ''))).slice(0, 200);
  const lista = tab === 'pendientes' ? pendientes : tab === 'futuras' ? futuras : hechas;
  // Resumen visual (como el panel de Recepción)
  const nHoy = tareas.filter(t => !t.hecha && t.fecha === hoy).length;
  const nVencidas = tareas.filter(t => !t.hecha && t.fecha && t.fecha < hoy).length;
  const nSinFecha = tareas.filter(t => !t.hecha && !t.fecha).length;
  const nSemana = tareas.filter(t => !t.hecha && t.fecha && t.fecha > hoy && t.fecha <= enDias(7)).length;
  const nDeRecepcion = tareas.filter(t => !t.hecha && t.creada_por && t.creada_por !== comercial?.nombre).length;

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(''), 4000);
    return () => clearTimeout(t);
  }, [aviso]);

  const marcarHecha = async (t) => {
    if (t.hecha) return;
    try { await actualizarTareaCrm(t.id, { hecha: true }); onRecargar?.(); }
    catch { alert('No se pudo marcar la tarea.'); }
  };

  const fmtF = (f) => f ? new Date(f + 'T00:00').toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—';
  // Pestañas de tareas con la forma de la navegación: la activa con gradiente.
  const tabBtn = (k, lbl, n, color) => (
    <button key={k} onClick={() => setTab(k)} className="fg-nav" style={{
      padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
      background: tab === k ? GRAD : 'transparent', color: tab === k ? '#fff' : C.page,
      boxShadow: tab === k ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
      display: 'flex', alignItems: 'center', gap: 7,
    }}>
      {lbl}
      <span style={tab === k
        ? { background: 'rgba(255,255,255,0.22)', color: '#fff', borderRadius: 999, fontSize: 9.5, fontWeight: 800, padding: '1px 7px' }
        : { background: `${color}18`, color, border: `1px solid ${color}44`, borderRadius: 999, fontSize: 9.5, fontWeight: 800, padding: '1px 7px' }}>{n}</span>
    </button>
  );

  return (
    <div className="fg-fade" style={{ maxWidth: 980, margin: '0 auto', padding: '16px 20px' }}>
      {aviso && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, background: '#1f2a37', color: '#fff', padding: '12px 18px', borderRadius: 10, fontSize: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>{aviso}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.2, color: C.ink, margin: 0 }}>☑️ Mis tareas</h1>
          <p style={{ fontSize: 12.5, color: C.muted, margin: '3px 0 0' }}>
            Tus tareas programadas, lo que te encarga {RECEPCION_NOMBRE_CRM} (Recepción) y las menciones de tus compañeros · clica una tarea para abrir la ficha del cliente
          </p>
        </div>
        <button onClick={() => onRecargar?.()} style={{ ...botonPrimario, padding: '8px 14px' }}>⟳ Actualizar</button>
      </div>

      {/* Resumen visual */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { icon: '📌', label: 'Para hoy', n: nHoy, color: C.naranja, ir: 'pendientes' },
          { icon: '⚠️', label: 'Vencidas', n: nVencidas, color: C.rojo, ir: 'pendientes' },
          { icon: '📝', label: 'Sin fecha', n: nSinFecha, color: C.muted, ir: 'pendientes' },
          { icon: '📅', label: 'Próximos 7 días', n: nSemana, color: C.brand, ir: 'futuras' },
          { icon: '📥', label: `De ${RECEPCION_NOMBRE_CRM} / compañeros`, n: nDeRecepcion, color: C.morado, ir: 'pendientes' },
        ].map(k => (
          <div key={k.label} className="fg-card" onClick={() => setTab(k.ir)} style={{
            ...tarjeta, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            borderLeft: `4px solid ${k.n > 0 ? k.color : C.line}`,
          }}>
            <span style={{ fontSize: 20 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: k.n > 0 ? k.color : C.faint, lineHeight: 1 }}>{k.n}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginTop: 3 }}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Alta rápida: título + día + hora → sale en Mis tareas y en la Agenda */}
      <div className="fg-card" style={{ ...tarjeta, padding: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={nuevaT.titulo} onChange={e => setNuevaT(t => ({ ...t, titulo: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && crearNueva()}
            placeholder="Nueva tarea… (para ligarla a un cliente, usa «☑️ Tarea» en su ficha)"
            style={{ flex: 1, minWidth: 230, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: 13, fontFamily: 'inherit', color: C.ink }} />
          <input type="date" value={nuevaT.fecha} onChange={e => setNuevaT(t => ({ ...t, fecha: e.target.value }))} title="Día (opcional)"
            style={{ padding: '9px 10px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: 13, fontFamily: 'inherit' }} />
          <input type="time" value={nuevaT.hora} onChange={e => setNuevaT(t => ({ ...t, hora: e.target.value }))} title="Hora (opcional)"
            style={{ padding: '9px 10px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: 13, fontFamily: 'inherit' }} />
          <button onClick={crearNueva} disabled={!nuevaT.titulo.trim() || creando} style={{ ...botonPrimario, padding: '10px 18px', opacity: !nuevaT.titulo.trim() || creando ? 0.5 : 1 }}>＋ Añadir</button>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>
          Con día (y hora) la tarea te sale también en tu <strong>Agenda</strong>. Sin día, se queda en «Pendientes» hasta que la hagas.
        </div>
      </div>

      {/* Pestañas */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabBtn('pendientes', '⏰ Pendientes', pendientes.length, C.rojo)}
        {tabBtn('futuras', '📅 Futuras', futuras.length, C.naranja)}
        {tabBtn('hechas', '✅ Hechas', hechasTodas.length, C.verde)}
      </div>
      {tab === 'hechas' && hechasTodas.length > hechas.length && (
        <p style={{ fontSize: 12, color: C.muted, margin: '-6px 0 10px' }}>Se muestran las {hechas.length} más recientes de {hechasTodas.length}.</p>
      )}

      {lista.length === 0 && (
        <EmptyState icon={tab === 'pendientes' ? '🎉' : tab === 'futuras' ? '📅' : '✅'}
          title={tab === 'pendientes' ? 'Sin tareas pendientes' : tab === 'futuras' ? 'Sin tareas futuras programadas' : 'Todavía no hay tareas hechas'} />
      )}

      {lista.length > 0 && (
        <div className="fg-card" style={{ ...tarjeta, overflow: 'hidden' }}>
          {editT && (
            <div style={{ background: `${C.brand}0d`, border: `1px solid ${C.brand}33`, borderRadius: 12, padding: 10, margin: '0 0 8px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: C.brand, marginBottom: 6 }}>✏️ Editar la tarea</div>
              <input type="text" value={editT.titulo} onChange={e => setEditT(ed => ({ ...ed, titulo: e.target.value }))} placeholder="Título de la tarea"
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 12.5, color: C.ink, background: '#fff', border: `1px solid ${C.brand}33`, borderRadius: 10, padding: '6px 9px', marginBottom: 6 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="date" value={editT.fecha} onChange={e => setEditT(ed => ({ ...ed, fecha: e.target.value }))}
                  style={{ padding: '5px 8px', borderRadius: 10, border: `1px solid ${C.brand}33`, fontSize: 12 }} />
                <input type="time" value={editT.hora} onChange={e => setEditT(ed => ({ ...ed, hora: e.target.value }))}
                  style={{ padding: '5px 8px', borderRadius: 10, border: `1px solid ${C.brand}33`, fontSize: 12 }} />
                <button onClick={guardarEditT} style={{ padding: '5px 12px', borderRadius: 10, border: 'none', background: C.brandDark, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Guardar</button>
                <button onClick={() => setEditT(null)} style={{ padding: '5px 10px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          )}
          {lista.map(t => {
            const vencida = !t.hecha && t.fecha && t.fecha < hoy;
            const deOtro = t.creada_por && t.creada_por !== comercial.nombre;
            return (
              <div key={t.id}
                onClick={() => t.lead_id && onAbrirLead?.(t.lead_id)}
                title={t.lead_id ? 'Abrir la ficha del lead' : ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderBottom: SEPARADOR_FILA, cursor: t.lead_id ? 'pointer' : 'default',
                  borderLeft: `4px solid ${t.hecha ? C.verde : vencida ? C.rojo : t.fecha === hoy ? C.naranja : C.line}`,
                }}>
                <span style={{ width: 22 }} onClick={e => e.stopPropagation()}>
                  {t.hecha
                    ? <span style={{ fontSize: 14, color: '#2e9e5b' }}>✔</span>
                    : <input type="checkbox" checked={false} onChange={() => marcarHecha(t)} style={{ width: 15, height: 15, cursor: 'pointer' }} title="Marcar como hecha" />}
                </span>
                {!t.hecha && (
                  <span onClick={e => { e.stopPropagation(); abrirEditT(t); }} title="Modificar la tarea"
                    style={{ cursor: 'pointer', fontSize: 13, opacity: 0.7, padding: '0 2px' }}>✏️</span>
                )}
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: t.hecha ? C.faint : C.ink, textDecoration: t.hecha ? 'line-through' : 'none' }}>
                  {deOtro && <span style={{ ...pildora(C.brand), marginRight: 6 }}>de {t.creada_por}</span>}
                  {t.titulo}
                  {t.lead_id && <span style={{ marginLeft: 6, fontSize: 11.5, color: C.brand, fontWeight: 800 }}>↗</span>}
                </span>
                <span style={{ width: 120, fontSize: 12, fontWeight: 700, color: vencida ? C.rojo : C.muted, textAlign: 'right' }}>
                  {fmtF(t.fecha)}{t.hora ? ` · ${String(t.hora).slice(0, 5)}` : ''}{vencida ? ' ⚠️' : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Estado Badge
const EstadoBadge = ({ estado, size = 'normal' }) => {
  const config = ESTADOS[estado] || { color: C.muted, textColor: '#fff', icon: '?' };
  // Píldora del sistema: fondo tenue del propio color y borde a juego.
  const padding = size === 'small' ? '2px 9px' : '3px 11px';
  const fontSize = size === 'small' ? '10px' : '10.5px';
  return (
    <span style={{ ...pildora(config.color), padding, fontSize }}>
      <span>{config.icon}</span>
      {estado}
    </span>
  );
};

// Lead Row - Vista de lista con toda la información
const LeadRow = ({ lead, onClick, onUpdateEstado, isSelected, comercialNombre }) => {
  const clienteNombre = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  const hora = formatTime(lead.fechaCreacion);
  const estadoConfig = ESTADOS[lead.estado] || { color: '#8a929c', textColor: '#fff' };
  
  const handleEstadoChange = (e) => {
    e.stopPropagation();
    onUpdateEstado(lead.id, e.target.value);
  };

  // Generar enlace de WhatsApp
  const getWhatsAppLink = () => {
    const telefono = lead.movil || lead.telefono || '';
    if (!telefono) return null;
    const telefonoLimpio = telefono.replace(/\D/g, '').replace(/^(?!34)/, '34');
    const nombre = lead.nombre || 'cliente';
    const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
    const mensaje = `Hola ${nombre}! soy ${comercialNombre || 'tu asesor'} del Grupo Dimoauto. Veo que estás interesado en el ${vehiculo || 'vehículo'}`;
    return `https://wa.me/${telefonoLimpio}?text=${encodeURIComponent(mensaje)}`;
  };

  // Generar enlace de Email
  const getEmailLink = () => {
    if (!lead.email) return null;
    const nombre = lead.nombre || 'cliente';
    const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
    const asunto = vehiculo ? `Información sobre ${vehiculo} - Grupo Dimoauto` : 'Información - Grupo Dimoauto';
    const cuerpo = `Hola ${nombre},\n\nGracias por tu interés${vehiculo ? ` en el ${vehiculo}` : ''}.\n\nQuedo a tu disposición para cualquier consulta.\n\nSaludos,\nGrupo Dimoauto`;
    return `mailto:${lead.email}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
  };

  const whatsappLink = getWhatsAppLink();
  const emailLink = getEmailLink();
  
  // Fila de lead: el realce al pasar el ratón lo pone `fg-nav` (antes iba
  // escrito a mano con onMouseEnter/Leave).
  return (
    <div
      onClick={() => onClick(lead)}
      className="fg-nav"
      style={{
        background: isSelected ? '#eef5fa' : '#ffffff',
        padding: '12px 20px',
        cursor: 'pointer',
        borderBottom: SEPARADOR_FILA,
        borderLeft: isSelected ? `3px solid ${C.brand}` : '3px solid transparent'
      }}
    >
      <div style={{display: 'grid', gridTemplateColumns: '100px 1.3fr 130px 1.2fr 1.2fr 140px', gap: '16px', alignItems: 'center'}}>
        {/* Columna 1: Hora y Estado */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start'}}>
          <span style={{color: C.brand, fontSize: '12.5px', fontWeight: 800}}>{hora}</span>
          <EstadoBadge estado={lead.estado} size="small" />
        </div>

        {/* Columna 2: Cliente */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <span style={{color: C.ink, fontSize: '13.5px', fontWeight: 700}}>{clienteNombre}</span>
            <span style={pildora(C.muted)}>{lead.tipoCliente}</span>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12.5px', color: C.muted}}>
            {lead.email && <span style={{color: C.brand}}>✉️ {lead.email}</span>}
          </div>
        </div>

        {/* Columna 3: Botones WhatsApp y Email - Centrado */}
        <div onClick={(e) => e.stopPropagation()} style={{display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center'}}>
          {whatsappLink ? (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="fg-nav"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '6px 14px',
                background: C.verde,
                color: 'white',
                borderRadius: 10,
                textDecoration: 'none',
                fontSize: '12.5px',
                fontWeight: 800,
                boxShadow: '0 3px 10px rgba(46,158,91,.28)',
                whiteSpace: 'nowrap',
                minWidth: '110px'
              }}
            >
              <span style={{fontSize: '14px'}}>💬</span> WhatsApp
            </a>
          ) : (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '6px 14px',
              background: '#f4f5f7',
              color: C.faint,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              fontSize: '12.5px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              minWidth: '110px'
            }}>
              <span style={{fontSize: '14px'}}>💬</span> Sin tel.
            </span>
          )}
          {emailLink ? (
            <a
              href={emailLink}
              target="_blank"
              rel="noopener noreferrer"
              className="fg-nav"
              style={{
                ...botonSecundario,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '6px 14px',
                textDecoration: 'none',
                fontSize: '12.5px',
                whiteSpace: 'nowrap',
                minWidth: '110px'
              }}
            >
              <span style={{fontSize: '14px'}}>✉️</span> Email
            </a>
          ) : (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '6px 14px',
              background: '#f4f5f7',
              color: C.faint,
              borderRadius: 10,
              fontSize: '12.5px',
              fontWeight: 700,
              border: `1px solid ${C.line}`,
              whiteSpace: 'nowrap',
              minWidth: '110px'
            }}>
              <span style={{fontSize: '14px'}}>✉️</span> Sin email
            </span>
          )}
        </div>

        {/* Columna 4: Vehículo */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          <div style={{color: C.page, fontSize: '12.5px', fontWeight: 700}}>
            🚗 {lead.marca || 'Sin marca'} {lead.modelo}
          </div>
          <div style={{fontSize: '11px', color: C.faint}}>
            📍 {lead.centro} • 👤 {lead.vendedor || 'Sin asignar'}
          </div>
        </div>

        {/* Columna 5: Lead Info */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          <div style={{fontSize: '12.5px', color: C.page}}>
            <span style={{fontWeight: 800}}>#{String(lead.num || lead.rowIndex || '0').padStart(3, '0')}</span> • {lead.origen}
          </div>
          <div style={{fontSize: '11px', color: C.faint}}>
            📅 {formatDateTime(lead.fechaCreacion)} • {lead.usuarioAlta || '-'}
          </div>
          {lead.fechaTraspaso && lead.fechaEntrada === lead.fechaTraspaso && (
            <div style={{fontSize: '11px', color: '#005187', fontWeight: 600}} title="Fecha en que este lead pasó a tu cartera (asignación o traspaso)">
              ↪ Te llegó el {formatDateTime(lead.fechaTraspaso)}
            </div>
          )}
        </div>

        {/* Columna 6: Cambiar Estado */}
        <div onClick={(e) => e.stopPropagation()} title={tooltipEstadoCrm(lead)}>
          <select
            value={lead.estado}
            onChange={handleEstadoChange}
            title={tooltipEstadoCrm(lead)}
            style={{
              ...campo,
              width: '100%',
              padding: '7px 10px',
              fontSize: '12px',
              fontWeight: 700,
              color: C.page,
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {Object.keys(ESTADOS).map(estado => (
              <option key={estado} value={estado}>{ESTADOS[estado].icon} {estado}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Lead Detail Panel - Modal emergente grande estilo CRM profesional
const LeadDetailPanel = ({ lead, onClose, onUpdate, comercialNombre }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    estado: lead.estado,
    motivoNoCompra: lead.motivoNoCompra || '',
    citaAgendada: formatDateForInput(lead.citaAgendada),
    tipoAgenda: lead.tipoAgenda || (lead.citaAgendada ? 'cita' : 'cita'),
    completada: lead.completada,
    noContesta: lead.noContesta,
    pospuesta: lead.pospuesta,
    observaciones: lead.observaciones
  });
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [saving, setSaving] = useState(false);

  // Actualizar formData cuando cambia el lead
  React.useEffect(() => {
    setFormData({
      estado: lead.estado,
      motivoNoCompra: lead.motivoNoCompra || '',
      citaAgendada: formatDateForInput(lead.citaAgendada),
      tipoAgenda: lead.tipoAgenda || (lead.citaAgendada ? 'cita' : 'cita'),
      completada: lead.completada,
      noContesta: lead.noContesta,
      pospuesta: lead.pospuesta,
      observaciones: lead.observaciones
    });
  }, [lead]);

  const handleSave = async () => {
    // Validación: si el estado es "Citado", la fecha y hora de cita es obligatoria
    if (formData.estado === 'Citado' && !formData.citaAgendada) {
      alert('Para cambiar el estado a "Citado" debes indicar la fecha y hora de la cita');
      return;
    }
    // Motivo OBLIGATORIO al cancelar (petición Adriana 26/08): no puede quedar en blanco
    if (formData.estado === 'Cancelado' && !String(formData.motivoNoCompra || '').trim()) {
      alert('Para cancelar el lead tienes que indicar el motivo de la cancelación.');
      return;
    }
    
    setSaving(true);
    const now = new Date().toISOString();
    
    const historialEntry = {
      fecha: now,
      estado: formData.estado,
      usuario: comercialNombre,
      accion: formData.completada ? 'Llamada completada' : formData.noContesta ? 'No contesta' : formData.pospuesta ? 'Pospuesta' : 'Actualización de estado'
    };
    
    const updates = {
      estado: formData.estado,
      motivoNoCompra: formData.motivoNoCompra,
      citaAgendada: formData.citaAgendada ? new Date(formData.citaAgendada) : null,
      tipoAgenda: formData.citaAgendada ? (formData.tipoAgenda || 'cita') : null,
      completada: formData.completada,
      noContesta: formData.noContesta,
      pospuesta: formData.pospuesta,
      observaciones: formData.observaciones,
      fechaModificacion: now,
      ultimaLlamada: formData.completada || formData.noContesta ? now : lead.ultimaLlamada,
      historial: [...(lead.historial || []), historialEntry]
    };

    const result = await updateLeadInSupabase(lead.id, updates);
    setSaving(false);
    if (result && result.success === false) {
      alert('No se pudo guardar el cambio. Inténtalo de nuevo.');
      return;
    }
    onUpdate(lead.id, updates);
  };

  const handleAddComment = async () => {
    if (!nuevoComentario.trim()) return;
    const now = new Date().toISOString();
    const newComment = {
      fecha: now,
      usuario: comercialNombre,
      texto: nuevoComentario.trim()
    };
    const updatedComments = [...(lead.comentarios || []), newComment];
    // Persistir en Supabase antes del update optimista; handleUpdateLead solo
    // toca estado local, así que sin esto los comentarios se perdían al recargar.
    const result = await updateLeadInSupabase(lead.id, { comentarios: updatedComments });
    if (result.success) {
      onUpdate(lead.id, { comentarios: updatedComments });
      setNuevoComentario('');
    }
  };

  const clienteNombre = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  const iniciales = clienteNombre.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const estadosCancelacion = ['Cancelado'];
  const mostrarMotivoNoCompra = estadosCancelacion.includes(formData.estado);
  const numLead = String(lead.num || lead.rowIndex || '0').padStart(3, '0');

  const tabs = [
    { id: 'info', label: 'Información', icon: '📋' },
    { id: 'gestion', label: 'Gestión', icon: '⚡' },
    { id: 'historial', label: 'Historial', icon: '📜' },
    { id: 'comentarios', label: 'Comentarios', icon: '💬' }
  ];

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15,28,43,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '90vh',
          background: '#ffffff',
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15,42,70,0.35)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header con acciones */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e7e9ec',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: GRAD
        }}>
          <div style={{color: 'white', fontSize: '15px', fontWeight: 800}}>
            Lead #{numLead} - {clienteNombre}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            {lead.movil && (
              <a href={`tel:${lead.movil}`}
                onClick={(e) => { if (!esMovil()) { e.preventDefault(); dimoLlamarDesdeCrm(lead.movil, lead); } }}
                title="En el ordenador se marca en tu móvil con Dimo Phone (ten la app abierta)"
                style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: 10,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 500
              }}>
                📞 Llamar
              </a>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: 10,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 500
              }}>
                ✉️ Email
              </a>
            )}
            <button onClick={onClose} style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: 500
            }}>
              ✕ Cerrar
            </button>
          </div>
        </div>

        <div style={{display: 'flex', flex: 1, overflow: 'hidden'}}>
          {/* Panel izquierdo - Perfil del cliente */}
          <div style={{
            width: '300px',
            background: '#f4f5f7',
            borderRight: '1px solid #e7e9ec',
            overflowY: 'auto',
            padding: '24px 20px',
            flexShrink: 0
          }}>
            {/* Avatar y nombre */}
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <div style={{
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #005187 0%, #1a9cd4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: 'white',
                fontSize: '32px',
                fontWeight: 700,
                border: '4px solid #ffffff',
                boxShadow: '0 4px 12px rgba(0,81,135,0.3)'
              }}>
                {iniciales}
              </div>
              <h3 style={{margin: '0 0 4px 0', color: '#1f2a37', fontSize: '18px', fontWeight: 600}}>{clienteNombre}</h3>
              <p style={{margin: 0, color: '#8a929c', fontSize: '13px'}}>{lead.tipoCliente} • {lead.provincia || 'Sin ubicación'}</p>
            </div>

            {/* Estado */}
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '24px', padding: '12px', background: '#ffffff', borderRadius: 10, border: '1px solid #e7e9ec'}}>
              <EstadoBadge estado={lead.estado} />
            </div>

            {/* Información de contacto */}
            <div style={{marginBottom: '24px'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px'}}>Información de contacto</h4>
              
              {lead.email && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{color: '#9aa4af', fontSize: '11px', marginBottom: '2px'}}>Email</div>
                  <a href={`mailto:${lead.email}`} style={{color: '#005187', fontSize: '13px', textDecoration: 'none'}}>{lead.email}</a>
                </div>
              )}
              
              {lead.telefono && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{color: '#9aa4af', fontSize: '11px', marginBottom: '2px'}}>Teléfono</div>
                  <a href={`tel:${lead.telefono}`} style={{color: '#005187', fontSize: '13px', textDecoration: 'none'}}>{lead.telefono}</a>
                </div>
              )}
              
              {lead.movil && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{color: '#9aa4af', fontSize: '11px', marginBottom: '2px'}}>Móvil</div>
                  <a href={`tel:${lead.movil}`} style={{color: '#005187', fontSize: '13px', textDecoration: 'none'}}>{lead.movil}</a>
                </div>
              )}
              
              {(lead.provincia || lead.cp) && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{color: '#9aa4af', fontSize: '11px', marginBottom: '2px'}}>Dirección</div>
                  <div style={{color: '#2b333d', fontSize: '13px'}}>{lead.cp} {lead.provincia}</div>
                </div>
              )}

              {lead.enlaceOrigen && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{color: '#9aa4af', fontSize: '11px', marginBottom: '2px'}}>Enlace origen</div>
                  <a href={lead.enlaceOrigen} target="_blank" rel="noopener noreferrer" style={{color: '#005187', fontSize: '13px', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>🔗 Abrir lead en plataforma</a>
                </div>
              )}
            </div>

            {/* Etiquetas */}
            <div style={{marginBottom: '24px'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px'}}>Etiquetas</h4>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                <span style={{background: `${C.brand}0d`, color: '#005187', padding: '4px 10px', borderRadius: 12, fontSize: '11px', fontWeight: 500}}>{lead.origen}</span>
                <span style={{background: `${C.verde}14`, color: C.verde, padding: '4px 10px', borderRadius: 12, fontSize: '11px', fontWeight: 500}}>{lead.tipoVenta}</span>
                {lead.marca && <span style={{background: `${C.naranja}14`, color: C.naranja, padding: '4px 10px', borderRadius: 12, fontSize: '11px', fontWeight: 500}}>{lead.marca}</span>}
                {lead.campana && <span style={{background: `${C.morado}14`, color: C.morado, padding: '4px 10px', borderRadius: 12, fontSize: '11px', fontWeight: 500}}>{lead.campana}</span>}
              </div>
            </div>

            {/* Vendedor asignado */}
            <div>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px'}}>Vendedor asignado</h4>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#ffffff', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                <div style={{width: '36px', height: '36px', borderRadius: '50%', background: '#005187', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 600}}>
                  {lead.vendedor?.charAt(0) || '?'}
                </div>
                <div>
                  <div style={{color: '#2b333d', fontSize: '13px', fontWeight: 500}}>{lead.vendedor || 'Sin asignar'}</div>
                  <div style={{color: '#9aa4af', fontSize: '11px'}}>{lead.centro}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Panel derecho - Tabs con contenido */}
          <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {/* Tabs */}
            <div style={{display: 'flex', borderBottom: '1px solid #e7e9ec', background: '#ffffff'}}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '14px 24px',
                    background: 'transparent',
                    border: 'none',
                    color: activeTab === tab.id ? '#005187' : '#8a929c',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    borderBottom: activeTab === tab.id ? '2px solid #005187' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Contenido del tab */}
            <div style={{flex: 1, overflowY: 'auto', padding: '24px'}}>
              
              {/* TAB INFO */}
              {activeTab === 'info' && (
                <div>
                  {/* Datos del Lead */}
                  <div style={{marginBottom: '24px'}}>
                    <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: '#e08c1e'}}>📋</span> Datos del Lead
                    </h4>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px'}}>
                      <InfoRowLight label="Nº Lead" value={`#${numLead}`} />
                      <InfoRowLight label="Tipo" value={lead.tipo} />
                      <InfoRowLight label="Origen" value={lead.origen} />
                      <InfoRowLight label="Detalle Origen" value={lead.detalleOrigen} />
                      <InfoRowLight label="Campaña" value={lead.campana} />
                      <InfoRowLight label="Fecha Creación" value={formatDateTime(lead.fechaCreacion)} />
                      <InfoRowLight label="Usuario Alta" value={lead.usuarioAlta} />
                      <InfoRowLight label="Última Modificación" value={formatDateTime(lead.fechaModificacion)} />
                    </div>
                  </div>

                  {/* Vehículo */}
                  <div style={{marginBottom: '24px'}}>
                    <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: '#2e9e5b'}}>🚗</span> Vehículo de Interés
                    </h4>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px'}}>
                      <InfoRowLight label="Marca" value={lead.marca} />
                      <InfoRowLight label="Modelo" value={lead.modelo} />
                      <InfoRowLight label="Motor" value={lead.motor} />
                      <InfoRowLight label="Combustible" value={lead.combustible} />
                      <InfoRowLight label="Kilómetros" value={lead.kilometros ? `${lead.kilometros} km` : '-'} />
                      <InfoRowLight label="Matrícula" value={lead.matricula} />
                      <InfoRowLight label="Tipo Venta" value={lead.tipoVenta === 'VN' ? 'Vehículo Nuevo' : lead.tipoVenta === 'VO' ? 'Vehículo Ocasión' : lead.tipoVenta === 'B2B' ? 'Empresa (B2B)' : lead.tipoVenta} />
                      <InfoRowLight label="Centro" value={lead.centro} />
                      <InfoRowLight label="Vendedor" value={lead.vendedor} />
                    </div>
                  </div>

                  {/* UTM */}
                  {(lead.utmSource || lead.utmMedium || lead.utmCampaign) && (
                    <div>
                      <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{color: '#7a5bd0'}}>📊</span> Datos UTM
                      </h4>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px'}}>
                        <InfoRowLight label="UTM Source" value={lead.utmSource} />
                        <InfoRowLight label="UTM Medium" value={lead.utmMedium} />
                        <InfoRowLight label="UTM Campaign" value={lead.utmCampaign} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB GESTIÓN */}
              {activeTab === 'gestion' && (
                <div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>Estado del Lead</label>
                      <select 
                        value={formData.estado} 
                        onChange={(e) => setFormData({...formData, estado: e.target.value})}
                        style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', cursor: 'pointer', outline: 'none'}}
                      >
                        {Object.keys(ESTADOS).map(estado => (
                          <option key={estado} value={estado}>{ESTADOS[estado].icon} {estado}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{opacity: mostrarMotivoNoCompra ? 1 : 0.4, pointerEvents: mostrarMotivoNoCompra ? 'auto' : 'none'}}>
                      <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: mostrarMotivoNoCompra ? '#d23b34' : '#8a929c'}}>Motivo No Compra{mostrarMotivoNoCompra ? ' (obligatorio)' : ''}</label>
                      <select 
                        value={formData.motivoNoCompra} 
                        onChange={(e) => setFormData({...formData, motivoNoCompra: e.target.value})}
                        style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', cursor: 'pointer', outline: 'none'}}
                      >
                        <option value="">Seleccionar motivo...</option>
                        {motivosConActual(formData.motivoNoCompra).map(motivo => (
                          <option key={motivo} value={motivo}>{motivo}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{marginBottom: '24px'}}>
                    <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>
                      {formData.tipoAgenda === 'llamada' ? '📞 Próxima llamada' : formData.tipoAgenda === 'seguimiento' ? '🚩 Día de seguimiento' : '📅 Fecha y Hora de Cita'}
                    </label>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px'}}>
                      <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'cita'})}
                        style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'cita' ? '#005187' : '#e7e9ec'}`, background: formData.tipoAgenda === 'cita' ? `${C.brand}0d` : '#ffffff', color: formData.tipoAgenda === 'cita' ? '#005187' : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                        📅 Cita
                      </button>
                      <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'llamada'})}
                        style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'llamada' ? '#2e9e5b' : '#e7e9ec'}`, background: formData.tipoAgenda === 'llamada' ? `${C.verde}14` : '#ffffff', color: formData.tipoAgenda === 'llamada' ? C.verde : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                        📞 Llamada
                      </button>
                      <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'seguimiento'})}
                        style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'seguimiento' ? C.naranja : '#e7e9ec'}`, background: formData.tipoAgenda === 'seguimiento' ? `${C.naranja}14` : '#ffffff', color: formData.tipoAgenda === 'seguimiento' ? C.naranja : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                        🚩 Seguimiento
                      </button>
                    </div>
                    <input
                      type="datetime-local"
                      value={formData.citaAgendada}
                      onChange={(e) => setFormData({...formData, citaAgendada: e.target.value})}
                      style={{padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', outline: 'none', maxWidth: '300px'}}
                    />
                    {formData.citaAgendada && (
                      <div style={{marginTop: '8px', padding: '10px 14px', background: formData.tipoAgenda === 'seguimiento' ? `${C.naranja}14` : formData.tipoAgenda === 'llamada' ? `${C.verde}14` : `${C.brand}0d`, borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: '8px', border: `1px solid ${formData.tipoAgenda === 'seguimiento' ? C.naranja : formData.tipoAgenda === 'llamada' ? '#2e9e5b' : '#005187'}`}}>
                        <span style={{fontSize: '16px'}}>{formData.tipoAgenda === 'seguimiento' ? '🚩' : formData.tipoAgenda === 'llamada' ? '📞' : '📅'}</span>
                        <span style={{color: formData.tipoAgenda === 'seguimiento' ? C.naranja : formData.tipoAgenda === 'llamada' ? C.verde : '#005187', fontSize: '13px', fontWeight: 500}}>
                          {formData.tipoAgenda === 'seguimiento' ? 'Seguimiento' : formData.tipoAgenda === 'llamada' ? 'Llamar' : 'Cita'}: {new Date(formData.citaAgendada).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}{formData.tipoAgenda !== 'seguimiento' ? ' · ' + new Date(formData.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.completada ? `${C.verde}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.completada ? '#2e9e5b' : '#e7e9ec'}`}}>
                      <input type="checkbox" checked={formData.completada} onChange={(e) => setFormData({...formData, completada: e.target.checked, noContesta: false, pospuesta: false})} style={{width: '18px', height: '18px', accentColor: '#2e9e5b'}} />
                      ✅ Llamada Completada
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.noContesta ? `${C.rojo}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.noContesta ? '#d23b34' : '#e7e9ec'}`}}>
                      <input type="checkbox" checked={formData.noContesta} onChange={(e) => setFormData({...formData, noContesta: e.target.checked, completada: false})} style={{width: '18px', height: '18px', accentColor: '#d23b34'}} />
                      ❌ No Contesta
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.pospuesta ? `${C.naranja}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.pospuesta ? '#e08c1e' : '#e7e9ec'}`}}>
                      <input type="checkbox" checked={formData.pospuesta} onChange={(e) => setFormData({...formData, pospuesta: e.target.checked, completada: false})} style={{width: '18px', height: '18px', accentColor: '#e08c1e'}} />
                      ⏸️ Pospuesta
                    </label>
                  </div>

                  <div style={{marginBottom: '24px'}}>
                    <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>Observaciones</label>
                    <textarea 
                      value={formData.observaciones}
                      onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                      placeholder="Añade notas sobre la gestión del lead..."
                      style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', minHeight: '100px', resize: 'vertical', outline: 'none', boxSizing: 'border-box'}}
                    />
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e7e9ec'}}>
                    <button onClick={handleSave} disabled={saving} style={{padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: '#2e9e5b', color: 'white', minWidth: '160px'}}>
                      {saving ? '💾 Guardando...' : '💾 Guardar Cambios'}
                    </button>
                  </div>

                  <AdjuntosLead leadId={lead.id} />
                </div>
              )}

              {/* TAB HISTORIAL */}
              {activeTab === 'historial' && (
                <div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px'}}>
                    <div style={{padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                      <div style={{color: '#8a929c', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase'}}>Primera Llamada</div>
                      <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{formatDateTime(lead.primeraLlamada)}</div>
                    </div>
                    <div style={{padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                      <div style={{color: '#8a929c', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase'}}>Última Llamada</div>
                      <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{formatDateTime(lead.ultimaLlamada)}</div>
                    </div>
                  </div>

                  <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📜 Historial de Acciones</h4>
                  {lead.historial && lead.historial.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {[...lead.historial].reverse().map((item, idx) => (
                        <div key={idx} style={{display: 'flex', gap: '12px', padding: '14px', background: '#f4f5f7', borderRadius: 10, borderLeft: '3px solid #005187'}}>
                          <div style={{flex: 1}}>
                            <div style={{color: '#1f2a37', fontSize: '13px', fontWeight: 500}}>{item.accion}</div>
                            <div style={{color: '#8a929c', fontSize: '11px', marginTop: '4px'}}>
                              {item.usuario} • {formatDateTime(item.fecha)}
                            </div>
                          </div>
                          <EstadoBadge estado={item.estado} size="small" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '40px', color: '#8a929c', background: '#f4f5f7', borderRadius: 10}}>
                      <div style={{fontSize: '32px', marginBottom: '8px'}}>📭</div>
                      <div>Sin historial de acciones</div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB COMENTARIOS */}
              {activeTab === 'comentarios' && (
                <div>
                  <div style={{marginBottom: '24px', padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                    <h4 style={{margin: '0 0 12px 0', color: '#1f2a37', fontSize: '14px', fontWeight: 600}}>💬 Añadir Comentario</h4>
                    <textarea 
                      value={nuevoComentario}
                      onChange={(e) => setNuevoComentario(e.target.value)}
                      placeholder="Escribe un comentario..."
                      style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', minHeight: '80px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: '12px'}}
                    />
                    <div style={{textAlign: 'right'}}>
                      <button 
                        onClick={handleAddComment}
                        disabled={!nuevoComentario.trim()}
                        style={{padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: nuevoComentario.trim() ? '#005187' : '#e7e9ec', color: nuevoComentario.trim() ? 'white' : '#9aa4af'}}
                      >
                        Añadir Comentario
                      </button>
                    </div>
                  </div>

                  <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📝 Comentarios ({(lead.comentarios || []).length})</h4>
                  {lead.comentarios && lead.comentarios.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {[...lead.comentarios].reverse().map((comment, idx) => (
                        <div key={idx} style={{padding: '14px', background: '#f4f5f7', borderRadius: 10, borderLeft: '3px solid #1a9cd4', marginLeft: comment.respuesta_a ? '18px' : 0}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                            <span style={{color: '#005187', fontSize: '12px', fontWeight: 600}}>{comment.respuesta_a ? '↳ ' : ''}{comment.usuario}</span>
                            <span style={{color: '#8a929c', fontSize: '11px'}}>{formatDateTime(comment.fecha)}{comment.editado_fecha ? ' · ✏️' : ''}</span>
                          </div>
                          <div style={{color: '#2b333d', fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{formatearComentarioLargo(comment.texto)}</div>
                          {Array.isArray(comment.imagenes) && comment.imagenes.length > 0 && (
                            <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px'}}>
                              {comment.imagenes.map((img, j) => (
                                <a key={j} href={img.url} target="_blank" rel="noreferrer">
                                  <img src={img.url} alt={img.nombre || 'captura'} style={{width: '76px', height: '58px', objectFit: 'cover', borderRadius: 10, border: '1px solid #e7e9ec'}} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '40px', color: '#8a929c', background: '#f4f5f7', borderRadius: 10}}>
                      <div style={{fontSize: '32px', marginBottom: '8px'}}>💬</div>
                      <div>Sin comentarios</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Info Row Light (para tema claro)
const InfoRowLight = ({ label, value }) => (
  <div style={{padding: '10px 12px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
    <div style={{color: '#8a929c', fontSize: '11px', marginBottom: '4px'}}>{label}</div>
    <div style={{color: '#1f2a37', fontSize: '13px', fontWeight: 500}}>{value || '-'}</div>
  </div>
);

// Lead Detail Modal - Tema claro para Agenda y Clientes
const LeadDetailModal = ({ lead, onClose, onUpdate, comercialNombre }) => {
  const [activeSection, setActiveSection] = useState('gestion');
  const [formData, setFormData] = useState({
    estado: lead.estado,
    motivoNoCompra: lead.motivoNoCompra || '',
    citaAgendada: formatDateForInput(lead.citaAgendada),
    tipoAgenda: lead.tipoAgenda || (lead.citaAgendada ? 'cita' : 'cita'),
    completada: lead.completada,
    noContesta: lead.noContesta,
    pospuesta: lead.pospuesta,
    observaciones: lead.observaciones
  });
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // Validación: si el estado es "Citado", la fecha y hora de cita es obligatoria
    if (formData.estado === 'Citado' && !formData.citaAgendada) {
      alert('Para cambiar el estado a "Citado" debes indicar la fecha y hora de la cita');
      return;
    }
    // Motivo OBLIGATORIO al cancelar (petición Adriana 26/08): no puede quedar en blanco
    if (formData.estado === 'Cancelado' && !String(formData.motivoNoCompra || '').trim()) {
      alert('Para cancelar el lead tienes que indicar el motivo de la cancelación.');
      return;
    }
    
    setSaving(true);
    const now = new Date().toISOString();
    
    const historialEntry = {
      fecha: now,
      estado: formData.estado,
      usuario: comercialNombre,
      accion: formData.completada ? 'Llamada completada' : formData.noContesta ? 'No contesta' : formData.pospuesta ? 'Pospuesta' : 'Actualización de estado'
    };
    
    const updates = {
      estado: formData.estado,
      motivoNoCompra: formData.motivoNoCompra,
      citaAgendada: formData.citaAgendada ? new Date(formData.citaAgendada) : null,
      tipoAgenda: formData.citaAgendada ? (formData.tipoAgenda || 'cita') : null,
      completada: formData.completada,
      noContesta: formData.noContesta,
      pospuesta: formData.pospuesta,
      observaciones: formData.observaciones,
      fechaModificacion: now,
      ultimaLlamada: formData.completada || formData.noContesta ? now : lead.ultimaLlamada,
      historial: [...(lead.historial || []), historialEntry]
    };

    const result = await updateLeadInSupabase(lead.id, updates);
    setSaving(false);
    if (result && result.success === false) {
      alert('No se pudo guardar el cambio. Inténtalo de nuevo.');
      return;
    }
    onUpdate(lead.id, updates);
    onClose();
  };

  const handleAddComment = async () => {
    if (!nuevoComentario.trim()) return;
    const now = new Date().toISOString();
    const newComment = {
      fecha: now,
      usuario: comercialNombre,
      texto: nuevoComentario.trim()
    };
    const updatedComments = [...(lead.comentarios || []), newComment];
    // Persistir en Supabase antes del update optimista; handleUpdateLead solo
    // toca estado local, así que sin esto los comentarios se perdían al recargar.
    const result = await updateLeadInSupabase(lead.id, { comentarios: updatedComments });
    if (result.success) {
      onUpdate(lead.id, { comentarios: updatedComments });
      setNuevoComentario('');
    }
  };

  const clienteNombre = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  const iniciales = clienteNombre.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const estadosCancelacion = ['Cancelado'];
  const mostrarMotivoNoCompra = estadosCancelacion.includes(formData.estado);

  return (
    <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,28,43,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)'}} onClick={onClose}>
      <div style={{background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', border: '1px solid #e7e9ec', boxShadow: '0 20px 60px rgba(15,42,70,0.35)'}} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{background: GRAD, padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
            <div style={{width: '50px', height: '50px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 700}}>
              {iniciales}
            </div>
            <div>
              <div style={{color: 'white', fontSize: '18px', fontWeight: 700}}>{clienteNombre}</div>
              <div style={{color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '4px'}}>{lead.marca} {lead.modelo} • {lead.tipoVenta}</div>
            </div>
          </div>
          <button onClick={onClose} style={{background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: 10, cursor: 'pointer', fontSize: '18px'}}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{display: 'flex', background: '#f4f5f7', borderBottom: '1px solid #e7e9ec'}}>
          {[
            { id: 'gestion', label: '⚡ Gestión' },
            { id: 'info', label: '📋 Información' },
            { id: 'vehiculo', label: '🚗 Vehículo' },
            { id: 'historial', label: '📜 Historial' },
            { id: 'comentarios', label: '💬 Comentarios' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              style={{
                flex: 1,
                padding: '14px',
                background: activeSection === tab.id ? '#ffffff' : 'transparent',
                border: 'none',
                color: activeSection === tab.id ? '#005187' : '#8a929c',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                borderBottom: activeSection === tab.id ? '2px solid #005187' : '2px solid transparent'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        <div style={{padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 200px)', background: '#ffffff'}}>
          
          {/* SECCIÓN GESTIÓN */}
          {activeSection === 'gestion' && (
            <div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>Estado del Lead</label>
                  <select 
                    value={formData.estado} 
                    onChange={(e) => setFormData({...formData, estado: e.target.value})}
                    style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', cursor: 'pointer', outline: 'none'}}
                  >
                    {Object.keys(ESTADOS).map(estado => (
                      <option key={estado} value={estado}>{ESTADOS[estado].icon} {estado}</option>
                    ))}
                  </select>
                </div>

                <div style={{opacity: mostrarMotivoNoCompra ? 1 : 0.4, pointerEvents: mostrarMotivoNoCompra ? 'auto' : 'none'}}>
                  <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: mostrarMotivoNoCompra ? '#d23b34' : '#8a929c'}}>Motivo No Compra{mostrarMotivoNoCompra ? ' (obligatorio)' : ''}</label>
                  <select 
                    value={formData.motivoNoCompra} 
                    onChange={(e) => setFormData({...formData, motivoNoCompra: e.target.value})}
                    style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', cursor: 'pointer', outline: 'none'}}
                  >
                    <option value="">Seleccionar motivo...</option>
                    {motivosConActual(formData.motivoNoCompra).map(motivo => (
                      <option key={motivo} value={motivo}>{motivo}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{marginBottom: '24px'}}>
                <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>
                  {formData.tipoAgenda === 'llamada' ? '📞 Próxima llamada' : formData.tipoAgenda === 'seguimiento' ? '🚩 Día de seguimiento' : '📅 Fecha y Hora de Cita'}
                </label>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px'}}>
                  <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'cita'})}
                    style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'cita' ? '#005187' : '#e7e9ec'}`, background: formData.tipoAgenda === 'cita' ? `${C.brand}0d` : '#ffffff', color: formData.tipoAgenda === 'cita' ? '#005187' : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                    📅 Cita
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'llamada'})}
                    style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'llamada' ? '#2e9e5b' : '#e7e9ec'}`, background: formData.tipoAgenda === 'llamada' ? `${C.verde}14` : '#ffffff', color: formData.tipoAgenda === 'llamada' ? C.verde : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                    📞 Llamada
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, tipoAgenda: 'seguimiento'})}
                    style={{padding: '6px 12px', borderRadius: 10, border: `1px solid ${formData.tipoAgenda === 'seguimiento' ? C.naranja : '#e7e9ec'}`, background: formData.tipoAgenda === 'seguimiento' ? `${C.naranja}14` : '#ffffff', color: formData.tipoAgenda === 'seguimiento' ? C.naranja : '#8a929c', fontSize: '12px', fontWeight: 500, cursor: 'pointer'}}>
                    🚩 Seguimiento
                  </button>
                </div>
                <input
                  type="datetime-local"
                  value={formData.citaAgendada}
                  onChange={(e) => setFormData({...formData, citaAgendada: e.target.value})}
                  style={{padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', outline: 'none', maxWidth: '300px'}}
                />
              </div>

              <div style={{display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.completada ? `${C.verde}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.completada ? '#2e9e5b' : '#e7e9ec'}`}}>
                  <input type="checkbox" checked={formData.completada} onChange={(e) => setFormData({...formData, completada: e.target.checked, noContesta: false, pospuesta: false})} style={{width: '18px', height: '18px', accentColor: '#2e9e5b'}} />
                  ✅ Llamada Completada
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.noContesta ? `${C.rojo}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.noContesta ? '#d23b34' : '#e7e9ec'}`}}>
                  <input type="checkbox" checked={formData.noContesta} onChange={(e) => setFormData({...formData, noContesta: e.target.checked, completada: false})} style={{width: '18px', height: '18px', accentColor: '#d23b34'}} />
                  ❌ No Contesta
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2b333d', fontSize: '13px', cursor: 'pointer', padding: '12px 16px', background: formData.pospuesta ? `${C.naranja}14` : `#f4f5f7`, borderRadius: 10, border: `1px solid ${formData.pospuesta ? '#e08c1e' : '#e7e9ec'}`}}>
                  <input type="checkbox" checked={formData.pospuesta} onChange={(e) => setFormData({...formData, pospuesta: e.target.checked, completada: false})} style={{width: '18px', height: '18px', accentColor: '#e08c1e'}} />
                  ⏸️ Pospuesta
                </label>
              </div>

              <div style={{marginBottom: '24px'}}>
                <label style={{display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#8a929c'}}>Observaciones</label>
                <textarea 
                  value={formData.observaciones}
                  onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                  placeholder="Añade notas..."
                  style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${C.line}`, background: '#ffffff', color: '#2b333d', fontSize: '13px', minHeight: '80px', resize: 'vertical', outline: 'none', boxSizing: 'border-box'}}
                />
              </div>

              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e7e9ec'}}>
                <button onClick={onClose} style={{padding: '10px 20px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#ffffff', color: '#2b333d', cursor: 'pointer', fontSize: '13px', fontWeight: 500}}>Cancelar</button>
                <button onClick={handleSave} disabled={saving} style={{padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#2e9e5b', color: 'white'}}>{saving ? 'Guardando...' : '💾 Guardar'}</button>
              </div>
            </div>
          )}

          {/* SECCIÓN INFORMACIÓN */}
          {activeSection === 'info' && (
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
              <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', border: '1px solid #e7e9ec'}}>
                <h4 style={{margin: '0 0 12px 0', color: '#e08c1e', fontSize: '13px', fontWeight: 600}}>📋 Datos del Lead</h4>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <InfoRowLight label="Lead ID" value={lead.leadId} />
                  <InfoRowLight label="Tipo" value={lead.tipo} />
                  <InfoRowLight label="Origen" value={lead.origen} />
                  <InfoRowLight label="Fecha Creación" value={formatDateTime(lead.fechaCreacion)} />
                </div>
              </div>
              <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', border: '1px solid #e7e9ec'}}>
                <h4 style={{margin: '0 0 12px 0', color: '#005187', fontSize: '13px', fontWeight: 600}}>👤 Cliente</h4>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <InfoRowLight label="Nombre" value={`${lead.nombre} ${lead.apellidos}`.trim()} />
                  <InfoRowLight label="Teléfono" value={lead.telefono} />
                  <InfoRowLight label="Móvil" value={lead.movil} />
                  <InfoRowLight label="Email" value={lead.email} />
                </div>
              </div>
              <div style={{gridColumn: '1 / -1'}}>
                <AdjuntosLead leadId={lead.id} />
              </div>
            </div>
          )}

          {/* SECCIÓN VEHÍCULO */}
          {activeSection === 'vehiculo' && (
            <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', border: '1px solid #e7e9ec'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2e9e5b', fontSize: '13px', fontWeight: 600}}>🚗 Vehículo</h4>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px'}}>
                <InfoRowLight label="Marca" value={lead.marca} />
                <InfoRowLight label="Modelo" value={lead.modelo} />
                <InfoRowLight label="Tipo Venta" value={lead.tipoVenta} />
                <InfoRowLight label="Centro" value={lead.centro} />
                <InfoRowLight label="Vendedor" value={lead.vendedor} />
              </div>
            </div>
          )}

          {/* SECCIÓN HISTORIAL */}
          {activeSection === 'historial' && (
            <div>
              {lead.historial && lead.historial.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {[...lead.historial].reverse().map((item, idx) => (
                    <div key={idx} style={{padding: '12px', background: '#f4f5f7', borderRadius: 10, borderLeft: '3px solid #005187'}}>
                      <div style={{color: '#1f2a37', fontSize: '13px', fontWeight: 500}}>{item.accion}</div>
                      <div style={{color: '#8a929c', fontSize: '11px', marginTop: '4px'}}>{item.usuario} • {formatDateTime(item.fecha)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{textAlign: 'center', padding: '40px', color: '#8a929c'}}>Sin historial</div>
              )}
            </div>
          )}

          {/* SECCIÓN COMENTARIOS */}
          {activeSection === 'comentarios' && (
            <div>
              <div style={{marginBottom: '16px'}}>
                <textarea 
                  value={nuevoComentario}
                  onChange={(e) => setNuevoComentario(e.target.value)}
                  placeholder="Escribe un comentario..."
                  style={{width: '100%', padding: '10px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '13px', minHeight: '60px', resize: 'vertical', outline: 'none', boxSizing: 'border-box'}}
                />
                <button onClick={handleAddComment} disabled={!nuevoComentario.trim()} style={{marginTop: '8px', padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '12px', background: nuevoComentario.trim() ? '#005187' : '#e7e9ec', color: nuevoComentario.trim() ? 'white' : '#9aa4af'}}>Añadir</button>
              </div>
              {lead.comentarios && lead.comentarios.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {[...lead.comentarios].reverse().map((c, idx) => (
                    <div key={idx} style={{padding: '12px', background: '#f4f5f7', borderRadius: 10, borderLeft: '3px solid #1a9cd4', marginLeft: c.respuesta_a ? '18px' : 0}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                        <span style={{color: '#005187', fontSize: '12px', fontWeight: 600}}>{c.respuesta_a ? '↳ ' : ''}{c.usuario}</span>
                        <span style={{color: '#8a929c', fontSize: '11px'}}>{formatDateTime(c.fecha)}{c.editado_fecha ? ' · ✏️' : ''}</span>
                      </div>
                      <div style={{color: '#2b333d', fontSize: '13px'}}>{c.texto}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{textAlign: 'center', padding: '40px', color: '#8a929c'}}>Sin comentarios</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Info Row Component
const InfoRow = ({ label, value, isPhone, isEmail }) => (
  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
    <span style={{color: '#8a929c', fontSize: '12px'}}>{label}</span>
    {isPhone && value ? (
      <a href={`tel:${value}`} style={{color: '#005187', fontSize: '13px', textDecoration: 'none', fontWeight: 500}}>{value}</a>
    ) : isEmail && value ? (
      <a href={`mailto:${value}`} style={{color: '#005187', fontSize: '13px', textDecoration: 'none', fontWeight: 500}}>{value}</a>
    ) : (
      <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 500}}>{value || '-'}</span>
    )}
  </div>
);

// ==================== AGENDA PAGE ====================
const AgendaPage = ({ leads, onSelectLead, comercial, onUpdateComercial, tareas = [], onTareaHecha = null, onAbrirLeadId = null }) => {
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' o 'monthly'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showBloqueoModal, setShowBloqueoModal] = useState(false);
  const [bloqueoLoading, setBloqueoLoading] = useState(false);
  // Selección dentro del modal de bloqueo (motivo + periodo)
  const hoyISO = new Date().toISOString().slice(0, 10);
  const [bloqueoMotivo, setBloqueoMotivo] = useState('');
  const [bloqueoDesde, setBloqueoDesde] = useState(hoyISO);
  const [bloqueoHasta, setBloqueoHasta] = useState('');
  const [bloqueoComentario, setBloqueoComentario] = useState('');

  // Abre el modal reseteando la selección
  const abrirBloqueoModal = () => {
    setBloqueoMotivo('');
    setBloqueoDesde(new Date().toISOString().slice(0, 10));
    setBloqueoHasta('');
    setBloqueoComentario('');
    setShowBloqueoModal(true);
  };

  // Estado de bloqueo del comercial
  const agendaBloqueada = comercial?.agenda_bloqueada || false;
  const motivoBloqueo = comercial?.motivo_bloqueo || '';

  // Opciones de bloqueo
  const MOTIVOS_BLOQUEO = [
    { value: 'Vacaciones', icon: '🏖️', color: '#e08c1e' },
    { value: 'Baja', icon: '🏥', color: '#d23b34' },
    { value: 'Tarea Propia', icon: '📋', color: '#7a5bd0' },
    { value: 'Formación', icon: '📚', color: '#1a9cd4' }
  ];

  // Función para bloquear agenda
  const handleBloquearAgenda = async (motivo, fechaInicio = null, fechaFin = null, comentario = '') => {
    setBloqueoLoading(true);
    // fechaInicio/fechaFin llegan como 'YYYY-MM-DD' del input date; las normalizamos a ISO
    const inicioISO = fechaInicio ? new Date(fechaInicio + 'T00:00:00').toISOString() : new Date().toISOString();
    const finISO = fechaFin ? new Date(fechaFin + 'T23:59:59').toISOString() : null;
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?id=eq.${comercial.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          agenda_bloqueada: true,
          motivo_bloqueo: motivo,
          fecha_inicio_bloqueo: inicioISO,
          fecha_fin_bloqueo: finISO,
          comentario_bloqueo: comentario,
          bloqueado_por: comercial.nombre
        })
      });

      if (response.ok || response.status === 204) {
        onUpdateComercial({
          ...comercial,
          agenda_bloqueada: true,
          motivo_bloqueo: motivo,
          fecha_inicio_bloqueo: inicioISO,
          fecha_fin_bloqueo: finISO,
          comentario_bloqueo: comentario
        });
        setShowBloqueoModal(false);
      } else {
        alert('Error al bloquear la agenda');
      }
    } catch (error) {
      secureLog.error('bloqueandoAgenda', error);
      alert('Error de conexión');
    } finally {
      setBloqueoLoading(false);
    }
  };

  // Función para desbloquear agenda
  const handleDesbloquearAgenda = async () => {
    if (!window.confirm('¿Estás seguro de desbloquear tu agenda? Volverás a recibir leads.')) return;
    
    setBloqueoLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?id=eq.${comercial.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          agenda_bloqueada: false,
          motivo_bloqueo: null,
          fecha_inicio_bloqueo: null,
          fecha_fin_bloqueo: null,
          comentario_bloqueo: null,
          bloqueado_por: null
        })
      });

      if (response.ok || response.status === 204) {
        onUpdateComercial({
          ...comercial,
          agenda_bloqueada: false,
          motivo_bloqueo: null,
          fecha_inicio_bloqueo: null,
          fecha_fin_bloqueo: null,
          comentario_bloqueo: null
        });
      } else {
        alert('Error al desbloquear la agenda');
      }
    } catch (error) {
      secureLog.error('desbloqueandoAgenda', error);
      alert('Error de conexión');
    } finally {
      setBloqueoLoading(false);
    }
  };

  // Obtener citas (leads con fecha agendada)
  const citas = useMemo(() => {
    return leads.filter(lead => lead.citaAgendada).sort((a, b) => new Date(a.citaAgendada) - new Date(b.citaAgendada));
  }, [leads]);

  // Obtener leads nuevos (creados recientemente, para mostrar en calendario)
  const leadsNuevos = useMemo(() => {
    return leads.filter(lead => lead.fechaCreacion && !lead.citaAgendada)
      .sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion));
  }, [leads]);

  // Helpers de fecha
  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1); // Lunes
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Días del mes anterior
    
    const days = [];
    // Días del mes anterior
    for (let i = startPad - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    // Días del mes actual
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    // Días del mes siguiente
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  // Obtener citas para una fecha específica
  const getCitasForDate = (date) => {
    return citas.filter(cita => {
      const citaDate = new Date(cita.citaAgendada);
      return citaDate.toDateString() === date.toDateString();
    });
  };

  // Tareas programadas (lead_tareas) con día: se pintan en la agenda
  // (petición Adriana 26/08). YYYY-MM-DD en hora local del día pedido.
  const ymdDia = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const getTareasForDate = (date) => {
    const k = ymdDia(date);
    return (tareas || []).filter(t => t.fecha && String(t.fecha).slice(0, 10) === k)
      .sort((a, b) => String(a.hora || '99').localeCompare(String(b.hora || '99')));
  };
  const abrirTarea = (t) => {
    if (t.lead_id) {
      const l = leads.find(x => x.id === t.lead_id);
      if (l) onSelectLead(l);
      else onAbrirLeadId?.(t.lead_id);
    }
  };
  const TareaAgenda = ({ t, compacta = false }) => (
    <div onClick={() => abrirTarea(t)} className={compacta ? undefined : 'fg-card'} title={t.titulo}
      style={compacta
        ? { background: `${C.morado}18`, color: t.hecha ? C.faint : C.morado, border: `1px solid ${C.morado}44`, fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: 999, marginBottom: '4px', cursor: t.lead_id ? 'pointer' : 'default', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: t.hecha ? 'line-through' : 'none' }
        : { background: `${C.morado}0d`, borderRadius: 12, padding: '10px', cursor: t.lead_id ? 'pointer' : 'default', borderLeft: `3px solid ${C.morado}`, border: `1px solid ${C.morado}44`, opacity: t.hecha ? 0.6 : 1 }}>
      {compacta ? (
        <>☑️ {t.hora ? `${String(t.hora).slice(0, 5)} ` : ''}{t.titulo}</>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: '4px' }}>
            <span style={{ color: C.morado, fontSize: '11px', fontWeight: 800 }}>☑️ {t.hora ? `${String(t.hora).slice(0, 5)} h` : 'Tarea'}</span>
            {!t.hecha && onTareaHecha && (
              <input type="checkbox" checked={false} onClick={e => e.stopPropagation()} onChange={() => onTareaHecha(t)} title="Marcar como hecha" style={{ width: 14, height: 14, cursor: 'pointer' }} />
            )}
            {t.hecha && <span style={{ fontSize: 11, color: C.verde }}>✔</span>}
          </div>
          <div style={{ color: C.ink, fontSize: '12px', fontWeight: 700, textDecoration: t.hecha ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{t.titulo}</div>
          {t.creada_por && t.creada_por !== comercial?.nombre && <div style={{ color: C.muted, fontSize: '10px', marginTop: 2 }}>de {t.creada_por}</div>}
        </>
      )}
    </div>
  );

  // Obtener leads nuevos creados en una fecha específica
  const getLeadsNuevosForDate = (date) => {
    return leadsNuevos.filter(lead => {
      const creacionDate = new Date(lead.fechaCreacion);
      return creacionDate.toDateString() === date.toDateString();
    });
  };

  // Obtener todos los eventos (citas + leads nuevos) para una fecha
  const getEventosForDate = (date) => {
    const citasDelDia = getCitasForDate(date);
    const leadsNuevosDelDia = getLeadsNuevosForDate(date);
    return {
      citas: citasDelDia,
      leadsNuevos: leadsNuevosDelDia,
      total: citasDelDia.length + leadsNuevosDelDia.length
    };
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const weekDays = getWeekDays(currentDate);
  const monthDays = getMonthDays(currentDate);
  const today = new Date().toDateString();

  const formatWeekRange = () => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${end.getDate()} ${start.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`;
    }
    return `${start.getDate()} ${start.toLocaleDateString('es-ES', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}`;
  };

  return (
    <div className="fg-fade" style={{padding: '16px 20px', height: 'calc(100vh - 110px)', overflowY: 'auto', background: C.bg}}>
      {/* Banner de Agenda Bloqueada */}
      {agendaBloqueada && (
        <div className="fg-card" style={{
          background: `${C.naranja}14`,
          border: `1px solid ${C.naranja}55`,
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <span style={{fontSize: '28px'}}>
              {MOTIVOS_BLOQUEO.find(m => m.value === motivoBloqueo)?.icon || '🔒'}
            </span>
            <div>
              <div style={{color: C.ink, fontSize: '14px', fontWeight: 800}}>
                Agenda Bloqueada - {motivoBloqueo}
              </div>
              <div style={{color: C.muted, fontSize: '12.5px', marginTop: '2px'}}>
                No estás recibiendo leads ni puedes añadir citas
                {comercial?.fecha_fin_bloqueo && (
                  <span> • Hasta: {new Date(comercial.fecha_fin_bloqueo).toLocaleDateString('es-ES')}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={handleDesbloquearAgenda}
            disabled={bloqueoLoading}
            style={{
              ...botonPrimario,
              background: C.verde,
              boxShadow: '0 3px 10px rgba(46,158,91,.28)',
              cursor: bloqueoLoading ? 'not-allowed' : 'pointer',
              opacity: bloqueoLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {bloqueoLoading ? '⏳' : '🔓'} Desbloquear Agenda
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '14px'}}>
        <div>
          <h2 style={{margin: 0, color: C.ink, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.2px'}}>
            📅 Agenda
          </h2>
          <p style={{margin: '3px 0 0 0', color: C.muted, fontSize: '12.5px'}}>
            {citas.length} citas programadas
          </p>
        </div>

        <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
          {/* Botón Bloquear Agenda */}
          {!agendaBloqueada && (
            <button
              onClick={abrirBloqueoModal}
              className="fg-nav"
              style={{
                ...botonSecundario,
                background: `${C.rojo}14`,
                color: C.rojo,
                border: `1px solid ${C.rojo}44`,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🔒 Bloquear Agenda
            </button>
          )}

          {/* Toggle Vista */}
          <div style={{display: 'flex', background: '#ffffff', borderRadius: 10, padding: '4px', border: `1px solid ${C.line}`}}>
            <button
              onClick={() => setViewMode('weekly')}
              className="fg-nav"
              style={{
                ...styles.button,
                padding: '7px 14px',
                background: viewMode === 'weekly' ? GRAD : 'transparent',
                color: viewMode === 'weekly' ? '#fff' : C.page,
                boxShadow: viewMode === 'weekly' ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
              }}
            >
              📆 Semanal
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className="fg-nav"
              style={{
                ...styles.button,
                padding: '7px 14px',
                background: viewMode === 'monthly' ? GRAD : 'transparent',
                color: viewMode === 'monthly' ? '#fff' : C.page,
                boxShadow: viewMode === 'monthly' ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
              }}
            >
              🗓️ Mensual
            </button>
          </div>

          <button onClick={goToToday} className="fg-nav" style={{...styles.button, ...botonSecundario}}>
            Hoy
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="fg-card" style={{...tarjeta, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '12px 14px'}}>
        <button
          onClick={() => viewMode === 'weekly' ? navigateWeek(-1) : navigateMonth(-1)}
          className="fg-nav"
          style={{...styles.button, ...botonSecundario}}
        >
          ← Anterior
        </button>
        <div style={{color: C.ink, fontSize: '15px', fontWeight: 800}}>
          {viewMode === 'weekly' ? formatWeekRange() : currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
        <button
          onClick={() => viewMode === 'weekly' ? navigateWeek(1) : navigateMonth(1)}
          className="fg-nav"
          style={{...styles.button, ...botonSecundario}}
        >
          Siguiente →
        </button>
      </div>

      {/* Vista Semanal */}
      {viewMode === 'weekly' && (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px'}}>
          {weekDays.map((day, idx) => {
            const citasDelDia = getCitasForDate(day);
            const leadsNuevosDelDia = getLeadsNuevosForDate(day);
            const tareasDelDia = getTareasForDate(day);
            const isToday = day.toDateString() === today;
            const dayName = day.toLocaleDateString('es-ES', { weekday: 'short' });
            const hayEventos = citasDelDia.length > 0 || leadsNuevosDelDia.length > 0 || tareasDelDia.length > 0;
            
            return (
              <div
                key={idx}
                className="fg-card"
                style={{
                  background: '#ffffff',
                  borderRadius: 12,
                  border: `1px solid ${isToday ? C.brand : C.line}`,
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{
                  padding: '12px',
                  borderBottom: `1px solid ${C.line}`,
                  textAlign: 'center',
                  background: isToday ? GRAD : 'transparent',
                  borderRadius: '11px 11px 0 0'
                }}>
                  <div style={{color: isToday ? '#fff' : C.muted, fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 800}}>{dayName}</div>
                  <div style={{color: isToday ? '#fff' : C.ink, fontSize: '20px', fontWeight: 800, marginTop: '4px'}}>{day.getDate()}</div>
                </div>
                
                <div style={{padding: '12px', flex: 1, overflowY: 'auto'}}>
                  {hayEventos ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                      {/* Leads nuevos del día */}
                      {leadsNuevosDelDia.map(lead => (
                        <div
                          key={lead.id}
                          onClick={() => onSelectLead(lead)}
                          className="fg-card"
                          style={{
                            background: `${C.naranja}14`,
                            borderRadius: 12,
                            padding: '10px',
                            cursor: 'pointer',
                            borderLeft: `3px solid ${C.naranja}`,
                            border: `1px solid ${C.naranja}44`
                          }}
                        >
                          <div style={{color: C.naranja, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', marginBottom: '4px'}}>
                            🆕 Nuevo Lead
                          </div>
                          <div style={{color: C.ink, fontSize: '12px', fontWeight: 700, marginBottom: '4px'}}>
                            {lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre'}
                          </div>
                          <div style={{color: C.muted, fontSize: '10px'}}>
                            {lead.marca} {lead.modelo}
                          </div>
                        </div>
                      ))}
                      {/* Tareas programadas del día */}
                      {tareasDelDia.map(t => <TareaAgenda key={`t-${t.id}`} t={t} />)}
                      {/* Citas del día */}
                      {citasDelDia.map(cita => (
                        <div
                          key={cita.id}
                          onClick={() => onSelectLead(cita)}
                          className="fg-card"
                          style={{
                            background: '#fff',
                            borderRadius: 12,
                            padding: '10px',
                            cursor: 'pointer',
                            borderLeft: `3px solid ${ESTADOS[cita.estado]?.color || C.muted}`,
                            border: `1px solid ${C.line}`
                          }}
                        >
                          <div style={{color: C.brand, fontSize: '11px', fontWeight: 800, marginBottom: '4px'}}>
                            📅 {formatTime(cita.citaAgendada)}
                          </div>
                          <div style={{color: C.ink, fontSize: '12px', fontWeight: 700, marginBottom: '4px'}}>
                            {cita.razonSocial || `${cita.nombre} ${cita.apellidos}`.trim() || 'Sin nombre'}
                          </div>
                          <div style={{color: C.muted, fontSize: '10px'}}>
                            {cita.marca} {cita.modelo}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '20px', color: C.faint, fontSize: '12px'}}>
                      Sin eventos
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista Mensual */}
      {viewMode === 'monthly' && (
        <div>
          {/* Cabecera días */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px'}}>
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
              <div key={day} style={{textAlign: 'center', padding: '8px', color: C.muted, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase'}}>
                {day}
              </div>
            ))}
          </div>
          
          {/* Grid días */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px'}}>
            {monthDays.map((dayObj, idx) => {
              const citasDelDia = getCitasForDate(dayObj.date);
              const leadsNuevosDelDia = getLeadsNuevosForDate(dayObj.date);
              const tareasDelDia = getTareasForDate(dayObj.date);
              const isToday = dayObj.date.toDateString() === today;
              const totalEventos = citasDelDia.length + leadsNuevosDelDia.length + tareasDelDia.length;
              
              return (
                <div
                  key={idx}
                  style={{
                    background: isToday ? `${C.brand}0d` : dayObj.isCurrentMonth ? '#ffffff' : '#f7f8fa',
                    borderRadius: 12,
                    minHeight: '100px',
                    padding: '8px',
                    border: `1px solid ${isToday ? C.brand : C.line}`,
                    opacity: dayObj.isCurrentMonth ? 1 : 0.5
                  }}
                >
                  <div style={{
                    color: isToday ? C.brand : C.ink,
                    fontSize: '13px',
                    fontWeight: isToday ? 800 : 700,
                    marginBottom: '6px'
                  }}>
                    {dayObj.date.getDate()}
                  </div>
                  
                  {/* Leads nuevos del día */}
                  {leadsNuevosDelDia.slice(0, 2).map(lead => (
                    <div
                      key={lead.id}
                      onClick={() => onSelectLead(lead)}
                      style={{
                        background: `${C.naranja}18`,
                        color: C.naranja,
                        border: `1px solid ${C.naranja}44`,
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '2px 8px',
                        borderRadius: 999,
                        marginBottom: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      🆕 {lead.nombre || lead.razonSocial}
                    </div>
                  ))}
                  
                  {/* Tareas programadas del día */}
                  {tareasDelDia.slice(0, 2).map(t => <TareaAgenda key={`t-${t.id}`} t={t} compacta />)}
                  {/* Citas del día */}
                  {citasDelDia.slice(0, leadsNuevosDelDia.length > 0 ? 1 : 3).map(cita => (
                    <div
                      key={cita.id}
                      onClick={() => onSelectLead(cita)}
                      style={{
                        background: `${ESTADOS[cita.estado]?.color || C.muted}18`,
                        color: ESTADOS[cita.estado]?.color || C.muted,
                        border: `1px solid ${ESTADOS[cita.estado]?.color || C.muted}44`,
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '2px 8px',
                        borderRadius: 999,
                        marginBottom: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {formatTime(cita.citaAgendada)} {cita.nombre || cita.razonSocial}
                    </div>
                  ))}
                  
                  {totalEventos > 3 && (
                    <div style={{color: '#8a929c', fontSize: '10px', textAlign: 'center'}}>
                      +{totalEventos - 3} más
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista de próximas citas */}
      <div style={{marginTop: '32px'}}>
        <h3 style={{color: C.ink, fontSize: '14px', fontWeight: 800, letterSpacing: '0.2px', marginBottom: '12px'}}>📋 Próximas Citas y Llamadas</h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px'}}>
          {citas.filter(c => new Date(c.citaAgendada) >= new Date()).slice(0, 6).map(cita => (
            <div
              key={cita.id}
              onClick={() => onSelectLead(cita)}
              className="fg-card"
              style={{
                background: '#ffffff',
                borderRadius: 12,
                padding: '14px 16px',
                border: `1px solid ${C.line}`,
                cursor: 'pointer',
                borderLeft: `4px solid ${ESTADOS[cita.estado]?.color || C.muted}`
              }}
            >
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                <div>
                  <div style={{...pildora(cita.tipoAgenda === 'seguimiento' ? C.naranja : cita.tipoAgenda === 'llamada' ? C.verde : C.brand), marginBottom: '6px'}}>
                    {cita.tipoAgenda === 'seguimiento' ? '🚩 SEGUIMIENTO' : cita.tipoAgenda === 'llamada' ? '📞 LLAMADA' : '📅 CITA'}
                  </div>
                  <div style={{color: C.ink, fontSize: '13.5px', fontWeight: 700}}>
                    {cita.razonSocial || `${cita.nombre} ${cita.apellidos}`.trim() || 'Sin nombre'}
                  </div>
                  <div style={{color: C.muted, fontSize: '12px', marginTop: '4px'}}>
                    {cita.marca} {cita.modelo}
                  </div>
                </div>
                <EstadoBadge estado={cita.estado} size="small" />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: cita.tipoAgenda === 'seguimiento' ? C.naranja : cita.tipoAgenda === 'llamada' ? C.verde : C.brand, fontSize: '12.5px', fontWeight: 700}}>
                <span>{cita.tipoAgenda === 'seguimiento' ? '🚩' : cita.tipoAgenda === 'llamada' ? '📞' : '📅'}</span>
                {new Date(cita.citaAgendada).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}{cita.tipoAgenda !== 'seguimiento' ? ' - ' + formatTime(cita.citaAgendada) : ''}
              </div>
              {/* Notas del cliente */}
              {(cita.observaciones || (cita.comentarios && cita.comentarios.length > 0)) && (
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: `1px solid ${C.line}`
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px'}}>
                    <span style={{fontSize: '12px'}}>📝</span>
                    <span style={etiquetaCifra}>Notas</span>
                  </div>
                  {cita.observaciones && (
                    <div style={{
                      fontSize: '12.5px',
                      color: C.page,
                      lineHeight: '1.45',
                      background: '#f7f8fa',
                      padding: '8px 10px',
                      borderRadius: 10,
                      whiteSpace: 'pre-wrap',
                      maxHeight: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {cita.observaciones.length > 150 ? cita.observaciones.substring(0, 150) + '...' : cita.observaciones}
                    </div>
                  )}
                  {!cita.observaciones && cita.comentarios && cita.comentarios.length > 0 && (
                    <div style={{
                      fontSize: '12.5px',
                      color: C.page,
                      lineHeight: '1.45',
                      background: '#f7f8fa',
                      padding: '8px 10px',
                      borderRadius: 10
                    }}>
                      <span style={{color: C.faint, fontSize: '11px'}}>
                        {new Date(cita.comentarios[cita.comentarios.length - 1].fecha).toLocaleDateString('es-ES')}:
                      </span>{' '}
                      {cita.comentarios[cita.comentarios.length - 1].texto.length > 100 
                        ? cita.comentarios[cita.comentarios.length - 1].texto.substring(0, 100) + '...' 
                        : cita.comentarios[cita.comentarios.length - 1].texto}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Bloqueo de Agenda */}
      {showBloqueoModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15,28,43,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            background: 'white',
            borderRadius: 14,
            width: '100%',
            maxWidth: '500px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15,42,70,0.35)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              background: '#d23b34',
              color: 'white'
            }}>
              <div style={{fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px'}}>
                🔒 Bloquear Agenda
              </div>
              <div style={{fontSize: '13px', opacity: 0.9, marginTop: '4px'}}>
                No recibirás leads mientras tu agenda esté bloqueada
              </div>
            </div>

            {/* Content */}
            <div style={{padding: '24px', maxHeight: '70vh', overflowY: 'auto'}}>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#2b333d', marginBottom: '16px'}}>
                1. Selecciona el motivo del bloqueo:
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                {MOTIVOS_BLOQUEO.map(motivo => {
                  const seleccionado = bloqueoMotivo === motivo.value;
                  return (
                    <button
                      key={motivo.value}
                      onClick={() => setBloqueoMotivo(motivo.value)}
                      disabled={bloqueoLoading}
                      className="fg-card"
                      style={{
                        padding: '16px 14px',
                        background: seleccionado ? `${motivo.color}18` : '#fff',
                        border: `1px solid ${seleccionado ? motivo.color : C.line}`,
                        borderRadius: 12,
                        cursor: bloqueoLoading ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        fontFamily: 'inherit',
                        opacity: bloqueoLoading ? 0.6 : 1
                      }}
                    >
                      <div style={{fontSize: '28px', marginBottom: '8px'}}>{motivo.icon}</div>
                      <div style={{fontSize: '13px', fontWeight: 800, color: C.ink}}>{motivo.value}</div>
                    </button>
                  );
                })}
              </div>

              {/* Periodo (desde / hasta) */}
              <div style={{fontSize: '14px', fontWeight: 600, color: '#2b333d', margin: '24px 0 12px'}}>
                2. Indica el periodo:
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                <div>
                  <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#8a929c', marginBottom: '6px'}}>
                    Desde
                  </label>
                  <input
                    type="date"
                    value={bloqueoDesde}
                    onChange={(e) => setBloqueoDesde(e.target.value)}
                    disabled={bloqueoLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e7e9ec',
                      borderRadius: 10,
                      fontSize: '14px',
                      color: '#2b333d',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#8a929c', marginBottom: '6px'}}>
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={bloqueoHasta}
                    min={bloqueoDesde || undefined}
                    onChange={(e) => setBloqueoHasta(e.target.value)}
                    disabled={bloqueoLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e7e9ec',
                      borderRadius: 10,
                      fontSize: '14px',
                      color: '#2b333d',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{fontSize: '11px', color: '#9aa4af', marginTop: '6px'}}>
                Deja "Hasta" vacío si no sabes cuándo volverás (bloqueo indefinido).
              </div>

              {/* Comentario opcional */}
              <div style={{marginTop: '16px'}}>
                <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#8a929c', marginBottom: '6px'}}>
                  Comentario (opcional)
                </label>
                <input
                  type="text"
                  value={bloqueoComentario}
                  onChange={(e) => setBloqueoComentario(e.target.value)}
                  disabled={bloqueoLoading}
                  placeholder="Ej: Vacaciones de verano"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e7e9ec',
                    borderRadius: 10,
                    fontSize: '14px',
                    color: '#2b333d',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Info adicional */}
              <div style={{
                marginTop: '20px',
                padding: '14px',
                background: `${C.naranja}14`,
                borderRadius: 10,
                border: '1px solid #f5dfae'
              }}>
                <div style={{fontSize: '12px', color: C.naranja, lineHeight: '1.5'}}>
                  <strong>⚠️ Importante:</strong> Mientras tu agenda esté bloqueada:
                  <ul style={{margin: '8px 0 0 0', paddingLeft: '18px'}}>
                    <li>No recibirás nuevos leads asignados</li>
                    <li>Podrás ver tus leads actuales pero no añadir citas</li>
                    <li>Puedes desbloquear en cualquier momento</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              background: '#f4f5f7',
              borderTop: '1px solid #e7e9ec',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowBloqueoModal(false)}
                disabled={bloqueoLoading}
                style={{
                  padding: '10px 20px',
                  background: '#8a929c',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: bloqueoLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleBloquearAgenda(bloqueoMotivo, bloqueoDesde, bloqueoHasta || null, bloqueoComentario)}
                disabled={bloqueoLoading || !bloqueoMotivo}
                style={{
                  padding: '10px 20px',
                  background: (bloqueoLoading || !bloqueoMotivo) ? `${C.rojo}55` : '#d23b34',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: (bloqueoLoading || !bloqueoMotivo) ? 'not-allowed' : 'pointer'
                }}
              >
                {bloqueoLoading ? 'Bloqueando…' : '🔒 Bloquear agenda'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== INICIO PAGE ====================
const TraspasoLeadComercialModal = ({ misLeads, comerciales, comercialActual, onClose, onTraspasar }) => {
  const [leadId, setLeadId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const candidatos = (comerciales || [])
    .filter(c => c.activo !== false && c.id !== comercialActual?.id)
    .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || '')));

  const lead = (misLeads || []).find(l => String(l.id) === String(leadId));
  const destino = candidatos.find(c => String(c.id) === String(destinoId));

  const aplicar = async () => {
    if (!lead || !destino) return;
    if (!window.confirm(`¿Traspasar "${(lead.nombre || '') + ' ' + (lead.apellidos || '')}" a ${destino.nombre}?\nEste lead dejará de estar en tu listado.`)) return;
    setEnviando(true);
    setErrorMsg('');
    try {
      const now = new Date().toISOString();
      const historialEntry = {
        fecha: now,
        accion: `Traspaso de ${comercialActual?.nombre || ''} a ${destino.nombre}`,
        usuario: comercialActual?.nombre || '-',
        estado: lead.estado
      };
      const updates = {
        vendedor_nombre: destino.nombre,
        centro: destino.centro || lead.centro,
        fecha_modificacion: now,
        historial: [...(lead.historial || []), historialEntry]
      };
      const result = await updateLeadInSupabase(lead.id, updates);
      if (result && result.success === false) {
        setErrorMsg('No se pudo traspasar el lead. Inténtalo de nuevo.');
        setEnviando(false);
        return;
      }
      onTraspasar && onTraspasar(lead.id, updates);
      onClose();
    } catch (e) {
      setErrorMsg('Error inesperado al traspasar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{...velo, zIndex: 1500}} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{...tarjetaModal, width: '92%', maxWidth: '480px', fontFamily: 'inherit'}}>
        <div style={{padding: '18px 20px', background: GRAD, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <strong>↔ Traspasar lead a otro comercial</strong>
          <button onClick={onClose} style={{background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer'}}>×</button>
        </div>
        <div style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px'}}>
          <div>
            <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#2b333d', marginBottom: '6px'}}>1. Lead a traspasar</label>
            <select value={leadId} onChange={e => setLeadId(e.target.value)} style={{width: '100%', padding: '10px', border: `1.5px solid ${C.line}`, borderRadius: 10, fontSize: '13px'}}>
              <option value="">— Selecciona uno de tus leads —</option>
              {(misLeads || []).slice().sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion)).map(l => (
                <option key={l.id} value={l.id}>
                  {(l.nombre || '') + ' ' + (l.apellidos || '')} — {l.marca || ''} {l.modelo || ''} ({l.estado || '-'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#2b333d', marginBottom: '6px'}}>2. Comercial destino</label>
            <select value={destinoId} onChange={e => setDestinoId(e.target.value)} style={{width: '100%', padding: '10px', border: `1.5px solid ${C.line}`, borderRadius: 10, fontSize: '13px'}}>
              <option value="">— Selecciona el comercial destino —</option>
              {candidatos.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}{c.centro ? ` — ${c.centro}` : ''}</option>
              ))}
            </select>
          </div>
          {errorMsg && <div style={{color: C.rojo, fontSize: '12px'}}>⚠️ {errorMsg}</div>}
          <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px'}}>
            <button onClick={onClose} disabled={enviando} style={{padding: '8px 14px', border: '1px solid #e7e9ec', background: '#fff', borderRadius: 10, fontSize: '13px', cursor: 'pointer', color: '#2b333d'}}>Cancelar</button>
            <button onClick={aplicar} disabled={enviando || !lead || !destino} style={{padding: '8px 16px', border: 'none', background: enviando || !lead || !destino ? '#9aa4af' : '#005187', color: '#fff', borderRadius: 10, fontSize: '13px', fontWeight: 600, cursor: enviando || !lead || !destino ? 'not-allowed' : 'pointer'}}>
              {enviando ? 'Traspasando...' : 'Traspasar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const InicioPage = ({ leads, onSelectLead, selectedLead, onUpdateLead, comercialNombre, comerciales = [], comercialActual = null, onAbrirDimoPhone = null, dimoVinculado = true, onTareaCreada = null }) => {
  const [showTraspaso, setShowTraspaso] = useState(false);
  // Traspasar leads: SOLO Adriana (Recepción, comerciales.id 17). Los admins
  // traspasan desde el CRM Admin o el Panel Recepción; los comerciales no.
  const puedeTraspasar = comercialActual?.id === 17;
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroOrigen, setFiltroOrigen] = useState('todos');
  const [ocultarFinalizados, setOcultarFinalizados] = useState(false);
  const [ocultarAgendados, setOcultarAgendados] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  // Obtener orígenes únicos de los leads
  const origenesUnicos = useMemo(() => {
    const origenes = new Set();
    leads.forEach(lead => {
      if (lead.origen) origenes.add(lead.origen);
    });
    return ['todos', ...Array.from(origenes).sort()];
  }, [leads]);

  const estadosCount = useMemo(() => {
    const counts = { todos: leads.length };
    Object.keys(ESTADOS).forEach(estado => {
      counts[estado] = leads.filter(l => l.estado === estado).length;
    });
    return counts;
  }, [leads]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (filtroEstado !== 'todos' && lead.estado !== filtroEstado) return false;
      if (filtroOrigen !== 'todos' && lead.origen !== filtroOrigen) return false;
      if (ocultarFinalizados && (lead.estado === 'Cancelado' || lead.estado === 'Vendido')) return false;
      if (ocultarAgendados && estaAgendadoFuturo(lead)) return false;
      if (busqueda) {
        const search = busqueda.toLowerCase();
        const matchName = `${lead.nombre} ${lead.apellidos}`.toLowerCase().includes(search);
        const matchRazon = lead.razonSocial?.toLowerCase().includes(search);
        const matchTel = lead.telefono?.includes(search) || lead.movil?.includes(search);
        const matchMarca = lead.marca?.toLowerCase().includes(search);
        if (!matchName && !matchRazon && !matchTel && !matchMarca) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.fechaEntrada || b.fechaCreacion) - new Date(a.fechaEntrada || a.fechaCreacion));
  }, [leads, filtroEstado, filtroOrigen, ocultarFinalizados, ocultarAgendados, busqueda]);

  const handleQuickUpdateEstado = async (leadId, nuevoEstado) => {
    // Validación: no permitir cambiar a "Citado" desde el dropdown rápido
    if (nuevoEstado === 'Citado') {
      alert('Para cambiar a "Citado" debes indicar fecha y hora. Usa el panel de gestión del lead.');
      return;
    }

    const lead = leads.find(l => l.id === leadId);
    // "Por qué": al cancelar pedimos el motivo (queda registrado quién/cuándo/motivo)
    let motivo = null;
    if (nuevoEstado === 'Cancelado') {
      // Motivo OBLIGATORIO (petición Adriana 26/08): se insiste hasta que se escriba algo
      let r = window.prompt('Motivo de la cancelación (obligatorio — por qué se cancela):', '');
      while (r !== null && !r.trim()) {
        r = window.prompt('El motivo es obligatorio para cancelar. Escribe por qué se cancela:', '');
      }
      if (r === null) return; // se cerró el diálogo → no cambiamos el estado
      motivo = r.trim();
    }
    const now = new Date().toISOString();
    const historialPrev = Array.isArray(lead?.historial) ? lead.historial : [];
    const updates = {
      estado: nuevoEstado,
      fechaModificacion: now,
      ...(motivo ? { motivoNoCompra: motivo } : {}),
      historial: [
        ...historialPrev,
        {
          fecha: now,
          accion: `Cambio de estado a ${nuevoEstado}${motivo ? ` — Motivo: ${motivo}` : ''}`,
          usuario: comercialNombre,
          estado: nuevoEstado,
          ...(motivo ? { motivo } : {}),
        },
      ],
    };
    // FIX: solo aplicar optimistic update si el PATCH realmente persistió.
    // Si fallaba en silencio (RLS, id incorrecto, red), antes el lead "volvía"
    // a Pendiente al siguiente refetch del realtime.
    const result = await updateLeadInSupabase(leadId, updates);
    if (result && result.success === false) {
      alert('No se pudo guardar el cambio de estado. Inténtalo de nuevo.');
      return;
    }
    onUpdateLead(leadId, updates);
  };

  return (
    <div style={{display: 'flex', height: 'calc(100vh - 110px)'}}>
      {/* Sidebar de estados: panel claro, como el resto de la suite */}
      <div style={{width: '220px', background: C.card, padding: '16px', borderRight: `1px solid ${C.line}`, overflowY: 'auto', flexShrink: 0}}>
        <div style={{marginBottom: '16px'}}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar..."
            style={{...campo, width: '100%', outline: 'none'}}
          />
        </div>

        <div style={{...etiquetaCifra, marginBottom: '10px'}}>Estados</div>
        
        <div
          onClick={() => setFiltroEstado('todos')}
          className="fg-nav"
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            cursor: 'pointer',
            marginBottom: '6px',
            background: filtroEstado === 'todos' ? GRAD : 'transparent',
            color: filtroEstado === 'todos' ? '#fff' : C.page,
            boxShadow: filtroEstado === 'todos' ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
            fontSize: '13px',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 700
          }}
        >
          <span>Todos</span>
          <span style={filtroEstado === 'todos'
            ? {borderRadius: 999, padding: '1px 7px', fontSize: '9.5px', fontWeight: 800, background: 'rgba(255,255,255,0.22)', color: '#fff'}
            : {borderRadius: 999, padding: '1px 7px', fontSize: '9.5px', fontWeight: 800, background: `${C.brand}12`, color: C.brand, border: `1px solid ${C.brand}2b`}}>{ocultarFinalizados ? (estadosCount.todos - (estadosCount['Cancelado'] || 0) - (estadosCount['Vendido'] || 0)) : estadosCount.todos}</span>
        </div>

        {Object.entries(ESTADOS).map(([estado, config]) => (
          estadosCount[estado] > 0 && (
            <div
              key={estado}
              onClick={() => setFiltroEstado(estado)}
              className="fg-nav"
              style={{
                padding: '7px 12px',
                borderRadius: 10,
                cursor: 'pointer',
                marginBottom: '4px',
                background: filtroEstado === estado ? `${config.color}18` : 'transparent',
                border: filtroEstado === estado ? `1px solid ${config.color}44` : '1px solid transparent',
                color: ocultarFinalizados && (estado === 'Cancelado' || estado === 'Vendido')
                  ? C.rojo
                  : (filtroEstado === estado ? config.color : C.page),
                fontSize: '12.5px',
                fontWeight: 700,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                opacity: ocultarFinalizados && (estado === 'Cancelado' || estado === 'Vendido') ? 0.7 : 1
              }}
            >
              <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{width: '8px', height: '8px', borderRadius: '50%', background: config.color}}></span>
                {estado}
              </span>
              <span style={{borderRadius: 999, padding: '1px 7px', fontSize: '9.5px', fontWeight: 800, background: `${config.color}18`, color: config.color, border: `1px solid ${config.color}44`}}>{estadosCount[estado]}</span>
            </div>
          )
        ))}

        {/* Mi móvil — Dimo Phone: instalar la app y vincular el teléfono */}
        <div style={{marginTop: '18px', paddingTop: '14px', borderTop: `1px solid ${C.line}`}}>
          <div style={{...etiquetaCifra, marginBottom: '10px'}}>Mi móvil</div>
          <button
            onClick={() => onAbrirDimoPhone && onAbrirDimoPhone()}
            className="fg-nav"
            style={{
              ...botonSecundario,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
              borderColor: dimoVinculado ? C.line : `${C.naranja}66`,
              background: dimoVinculado ? undefined : `${C.naranja}0f`
            }}
            title="Descargar la app Dimo Phone y vincular tu móvil con el CRM"
          >
            <span style={{fontSize: '15px'}}>📱</span>
            <span style={{flex: 1}}>Dimo Phone</span>
            <span style={{
              width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
              background: dimoVinculado ? C.verde : C.naranja
            }} />
          </button>
          <div style={{fontSize: '11px', color: C.muted, marginTop: '6px', lineHeight: 1.4}}>
            {dimoVinculado
              ? 'Móvil vinculado · las llamadas se registran solas'
              : 'Sin vincular · descarga la app y vincúlalo'}
          </div>
        </div>

        {/* Acciones rápidas — traspaso solo para Recepción/admins */}
        {puedeTraspasar && (
        <div style={{marginTop: '18px', paddingTop: '14px', borderTop: `1px solid ${C.line}`}}>
          <div style={{...etiquetaCifra, marginBottom: '10px'}}>Acciones</div>
          <button
            onClick={() => setShowTraspaso(true)}
            className="fg-nav"
            style={{
              ...botonSecundario,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            title="Traspasar un lead tuyo a otro comercial"
          >
            <span style={{fontSize: '15px'}}>↔</span>
            <span>Traspasar lead</span>
          </button>
        </div>
        )}
      </div>

      {/* Lista de leads - Vista de lista */}
      <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg}}>
        {/* Header de la lista */}
        <div style={{padding: '12px 20px', background: C.card, borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
          <h2 style={{margin: 0, color: C.ink, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '10px'}}>
            {filtroEstado === 'todos' ? 'Todos los Leads' : filtroEstado}
            <span style={{color: C.muted, fontSize: '12.5px', fontWeight: 700}}>({filteredLeads.length})</span>
          </h2>

          {/* Filtro de Origen/Plataforma */}
          <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
            <button
              onClick={() => setOcultarFinalizados(!ocultarFinalizados)}
              className="fg-nav"
              title={ocultarFinalizados ? 'Mostrando solo leads activos' : 'Click para ocultar Cancelados y Vendidos'}
              style={{
                ...botonSecundario,
                padding: '7px 12px',
                border: ocultarFinalizados ? `1px solid ${C.verde}55` : `1px solid ${C.line}`,
                background: ocultarFinalizados ? `${C.verde}14` : '#fff',
                color: ocultarFinalizados ? C.verde : C.page,
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{fontSize: '14px'}}>{ocultarFinalizados ? '✅' : '⊘'}</span>
              No mostrar Cancelados/Vendidos
            </button>
            <button
              onClick={() => setOcultarAgendados(!ocultarAgendados)}
              className="fg-nav"
              title={ocultarAgendados ? 'Ocultando leads agendados a un día futuro' : 'Mostrando también los agendados a futuro'}
              style={{
                ...botonSecundario,
                padding: '7px 12px',
                border: ocultarAgendados ? `1px solid ${C.naranja}55` : `1px solid ${C.line}`,
                background: ocultarAgendados ? `${C.naranja}14` : '#fff',
                color: ocultarAgendados ? C.naranja : C.page,
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{fontSize: '14px'}}>{ocultarAgendados ? '🗓️' : '⊘'}</span>
              Ocultar agendados a futuro
            </button>
            <span style={{fontSize: '12px', color: C.muted, fontWeight: 700}}>Origen:</span>
            <select
              value={filtroOrigen}
              onChange={(e) => setFiltroOrigen(e.target.value)}
              style={{
                ...campo,
                background: filtroOrigen !== 'todos' ? `${C.brand}0d` : '#fff',
                color: C.page,
                fontWeight: 700,
                cursor: 'pointer',
                minWidth: '160px',
                outline: 'none'
              }}
            >
              {origenesUnicos.map(origen => (
                <option key={origen} value={origen}>
                  {origen === 'todos' ? '🌐 Todas las plataformas' : origen}
                </option>
              ))}
            </select>
            {filtroOrigen !== 'todos' && (
              <button
                onClick={() => setFiltroOrigen('todos')}
                style={{
                  ...botonPrimario,
                  padding: '7px 12px',
                  background: C.rojo,
                  boxShadow: '0 3px 10px rgba(210,59,52,.28)',
                  fontSize: '12px'
                }}
              >
                ✕ Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Cabecera de columnas */}
        <div style={{padding: '9px 20px', background: '#f7f8fa', borderBottom: `1px solid ${C.line}`, display: 'grid', gridTemplateColumns: '100px 1.3fr 130px 1.2fr 1.2fr 140px', gap: '16px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', color: C.muted, textTransform: 'uppercase'}}>
          <span>Hora/Estado</span>
          <span>Cliente</span>
          <span style={{textAlign: 'center'}}>Contactar</span>
          <span>Vehículo</span>
          <span>Lead Info</span>
          <span>Cambiar Estado</span>
        </div>

        {/* Lista scrolleable */}
        <div style={{flex: 1, overflowY: 'auto'}}>
          {filteredLeads.map(lead => (
            <LeadRow 
              key={lead.id} 
              lead={lead} 
              onClick={onSelectLead}
              onUpdateEstado={handleQuickUpdateEstado}
              isSelected={selectedLead?.id === lead.id}
              comercialNombre={comercialNombre}
            />
          ))}

          {filteredLeads.length === 0 && (
            <EmptyState icon="📭" title="No hay leads que mostrar" hint="Prueba a cambiar los filtros" />
          )}
        </div>
      </div>

      {/* Modal emergente de detalle */}
      {selectedLead && (
        <FichaLeadCrm
          lead={selectedLead}
          onClose={() => onSelectLead(null)}
          onUpdate={onUpdateLead}
          comercialNombre={comercialNombre}
          lista={filteredLeads}
          onNavegar={(l) => onSelectLead(l)}
          onTareaCreada={onTareaCreada}
        />
      )}
      {showTraspaso && puedeTraspasar && (
        <TraspasoLeadComercialModal
          misLeads={leads}
          comerciales={comerciales}
          comercialActual={comercialActual}
          onClose={() => setShowTraspaso(false)}
          onTraspasar={(leadId, updates) => onUpdateLead(leadId, updates)}
        />
      )}
    </div>
  );
};

// ==================== CLIENTES PAGE ====================
const ClientesPage = ({ leads }) => {
  const [busqueda, setBusqueda] = useState('');

  const clientes = useMemo(() => {
    const clientesMap = {};
    leads.forEach(lead => {
      const key = lead.telefono || lead.movil || lead.email || lead.leadId;
      if (!clientesMap[key]) {
        clientesMap[key] = {
          id: key,
          nombre: lead.nombre,
          apellidos: lead.apellidos,
          razonSocial: lead.razonSocial,
          tipoCliente: lead.tipoCliente,
          telefono: lead.telefono,
          movil: lead.movil,
          email: lead.email,
          cp: lead.cp,
          provincia: lead.provincia,
          leads: [],
          ultimaInteraccion: null
        };
      }
      clientesMap[key].leads.push(lead);
      const fecha = lead.fechaModificacion || lead.fechaCreacion;
      if (fecha && (!clientesMap[key].ultimaInteraccion || new Date(fecha) > new Date(clientesMap[key].ultimaInteraccion))) {
        clientesMap[key].ultimaInteraccion = fecha;
      }
    });
    return Object.values(clientesMap).sort((a, b) => 
      new Date(b.ultimaInteraccion) - new Date(a.ultimaInteraccion)
    );
  }, [leads]);

  const filteredClientes = useMemo(() => {
    if (!busqueda) return clientes;
    const search = busqueda.toLowerCase();
    return clientes.filter(c => 
      `${c.nombre} ${c.apellidos}`.toLowerCase().includes(search) ||
      c.razonSocial?.toLowerCase().includes(search) ||
      c.telefono?.includes(search) ||
      c.movil?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  }, [clientes, busqueda]);

  return (
    <div className="fg-fade" style={{padding: '16px 20px', height: 'calc(100vh - 110px)', overflowY: 'auto', background: C.bg}}>
      <div style={{marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
        <h2 style={{margin: 0, color: C.ink, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.2px'}}>
          👥 Clientes
          <span style={{color: C.muted, fontSize: '12.5px', fontWeight: 700, marginLeft: '10px'}}>({clientes.length})</span>
        </h2>
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar cliente..."
          style={{...campo, width: '280px', outline: 'none'}}
        />
      </div>

      <div className="fg-card" style={{...tarjeta, overflow: 'hidden'}}>
        <table style={{width: '100%', borderCollapse: 'collapse'}}>
          <thead>
            <tr>
              <th style={celdaTitulo}>Cliente</th>
              <th style={celdaTitulo}>Contacto</th>
              <th style={celdaTitulo}>Ubicación</th>
              <th style={{...celdaTitulo, textAlign: 'center'}}>Leads</th>
              <th style={celdaTitulo}>Última Interacción</th>
            </tr>
          </thead>
          <tbody>
            {filteredClientes.map((cliente, idx) => {
              const nombreCompleto = cliente.razonSocial || `${cliente.nombre} ${cliente.apellidos}`.trim() || 'Sin nombre';
              return (
                <tr key={cliente.id} style={{background: '#fff', borderBottom: SEPARADOR_FILA}}>
                  <td style={{padding: '10px 12px'}}>
                    <div style={{color: C.ink, fontSize: '12.5px', fontWeight: 700}}>{nombreCompleto}</div>
                    <div style={{color: C.muted, fontSize: '11px', marginTop: '2px'}}>{cliente.tipoCliente}</div>
                  </td>
                  <td style={{padding: '10px 12px'}}>
                    <div style={{color: C.page, fontSize: '12.5px'}}>{cliente.telefono || cliente.movil || '-'}</div>
                    <div style={{color: C.brand, fontSize: '11px', marginTop: '2px'}}>{cliente.email || '-'}</div>
                  </td>
                  <td style={{padding: '10px 12px', color: C.muted, fontSize: '12.5px'}}>
                    {cliente.provincia || '-'} {cliente.cp && `(${cliente.cp})`}
                  </td>
                  <td style={{padding: '10px 12px', textAlign: 'center'}}>
                    <span style={pildora(C.brand)}>
                      {cliente.leads.length}
                    </span>
                  </td>
                  <td style={{padding: '10px 12px', color: C.muted, fontSize: '12.5px'}}>
                    {formatDateTime(cliente.ultimaInteraccion)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredClientes.length === 0 && (
        <EmptyState icon="👥" title="No hay clientes que mostrar" />
      )}
    </div>
  );
};

// ==================== FLOATING BUTTONS ====================
// ==================== CHAT INTERNO COMERCIAL ↔ ADMIN ====================
const fmtChatHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
  const sameDay = d.toDateString() === hoy.toDateString();
  const isYest = d.toDateString() === ayer.toDateString();
  if (sameDay) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (isYest) return 'Ayer ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const useChatUnread = (email) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!email) return undefined;
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/chat_mensajes?destinatario_email=eq.${encodeURIComponent(email)}&leido=eq.false&select=id`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } }
        );
        if (r.ok && alive) {
          const data = await r.json();
          setCount(Array.isArray(data) ? data.length : 0);
        }
      } catch (_) { /* silencio */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [email]);
  return count;
};

const ChatComercialPanel = ({ comercial }) => {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [adminDestino, setAdminDestino] = useState({ email: '', nombre: '' });
  const messagesEndRef = useRef(null);

  const myEmail = comercial?.email || '';
  const myNombre = comercial?.nombre || '';

  // Cargar admin para envíos (el más reciente que ha hablado conmigo, o cualquier admin activo)
  useEffect(() => {
    if (!myEmail) return;
    (async () => {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/chat_mensajes?destinatario_email=eq.${encodeURIComponent(myEmail)}&emisor_tipo=eq.admin&order=fecha_envio.desc&limit=1&select=emisor_email,emisor_nombre`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } }
        );
        if (r.ok) {
          const d = await r.json();
          if (d.length > 0) { setAdminDestino({ email: d[0].emisor_email, nombre: d[0].emisor_nombre || 'Administración' }); return; }
        }
        // Fallback: primer admin activo
        const ar = await fetch(`${SUPABASE_URL}/rest/v1/administradores?activo=eq.true&select=email,nombre&limit=1`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } });
        if (ar.ok) {
          const ad = await ar.json();
          if (ad.length > 0) setAdminDestino({ email: ad[0].email, nombre: ad[0].nombre || 'Administración' });
        }
      } catch (_) { /* silencio */ }
    })();
  }, [myEmail]);

  const cargar = async () => {
    if (!myEmail) return;
    try {
      const url = `${SUPABASE_URL}/rest/v1/chat_mensajes?or=(emisor_email.eq.${encodeURIComponent(myEmail)},destinatario_email.eq.${encodeURIComponent(myEmail)})&order=fecha_envio.asc&limit=500`;
      const r = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } });
      if (r.ok) setMensajes(await r.json());
    } catch (_) { /* silencio */ }
  };

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail]);

  // Auto-marcar como leídos los mensajes recibidos al abrir
  useEffect(() => {
    if (!myEmail) return;
    const noLeidos = mensajes.filter(m => m.destinatario_email === myEmail && !m.leido);
    if (noLeidos.length === 0) return;
    Promise.all(noLeidos.map(m =>
      fetch(`${SUPABASE_URL}/rest/v1/chat_mensajes?id=eq.${m.id}`, {
        method: 'PATCH',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ leido: true, fecha_leido: new Date().toISOString() })
      })
    )).then(cargar).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensajes.length]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes.length]);

  const enviar = async () => {
    if (!texto.trim() || enviando || !myEmail) return;
    if (!adminDestino.email) {
      window.alert('No se ha encontrado un administrador activo al que enviar el mensaje.');
      return;
    }
    setEnviando(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/chat_mensajes`, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emisor_tipo: 'comercial',
          emisor_email: myEmail,
          emisor_nombre: myNombre,
          destinatario_tipo: 'admin',
          destinatario_email: adminDestino.email,
          destinatario_nombre: adminDestino.nombre,
          mensaje: texto.trim()
        })
      });
      setTexto('');
      await cargar();
    } catch (e) {
      console.error(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', fontFamily: 'inherit'}}>
      <div style={{flex: 1, overflowY: 'auto', padding: '12px', background: '#f4f5f7'}}>
        {mensajes.length === 0 ? (
          <div style={{textAlign: 'center', color: '#9aa4af', fontSize: '13px', padding: '40px 20px'}}>
            <div style={{fontSize: '40px', marginBottom: '8px'}}>💬</div>
            <div>Aún no tienes mensajes.</div>
            <div style={{fontSize: '12px', marginTop: '4px'}}>Si la administración te envía algo, aparecerá aquí.</div>
          </div>
        ) : mensajes.map(m => {
          const isMine = m.emisor_email === myEmail;
          return (
            <div key={m.id} style={{display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: '8px'}}>
              <div style={{
                maxWidth: '80%',
                background: isMine ? '#005187' : '#fff',
                color: isMine ? '#fff' : '#2b333d',
                padding: '8px 12px',
                borderRadius: 12,
                fontSize: '13px',
                lineHeight: '1.4',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                border: isMine ? 'none' : '1px solid #e7e9ec',
                wordBreak: 'break-word'
              }}>
                {!isMine && <div style={{fontSize: '11px', fontWeight: 700, marginBottom: '2px', color: '#005187'}}>{m.emisor_nombre || 'Admin'}</div>}
                <div style={{whiteSpace: 'pre-wrap'}}>{m.mensaje}</div>
                <div style={{fontSize: '10px', opacity: 0.7, marginTop: '4px', textAlign: 'right'}}>
                  {fmtChatHora(m.fecha_envio)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <div style={{padding: '10px 12px', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '8px', background: '#fff'}}>
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          placeholder="Escribe un mensaje al admin..."
          rows={1}
          style={{flex: 1, border: `1.5px solid ${C.line}`, borderRadius: 10, padding: '10px 12px', fontSize: '13px', resize: 'none', fontFamily: 'inherit', minHeight: '40px', maxHeight: '120px'}}
        />
        <button
          onClick={enviar}
          disabled={enviando || !texto.trim()}
          style={{
            padding: '8px 18px',
            background: enviando || !texto.trim() ? '#9aa4af' : '#005187',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 600,
            cursor: enviando || !texto.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {enviando ? '...' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};

const FloatingButtons = ({ onOpenAsistente, onOpenTelegram, onOpenNuevoLead, telegramVinculado, onOpenChat, chatUnread = 0, onOpenDimoPhone, dimoVinculado = true }) => {
  const [showTooltip, setShowTooltip] = useState(null);
  
  const buttonStyle = {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    transition: 'transform 0.2s, box-shadow 0.2s'
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      zIndex: 1000
    }}>
      {/* Botón Nuevo Lead */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'nuevolead' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Crear Nuevo Lead
          </div>
        )}
        <button
          onClick={onOpenNuevoLead}
          onMouseEnter={() => setShowTooltip('nuevolead')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #228B22 0%, #2E8B57 100%)',
            color: 'white'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,139,34,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
        >
          ➕
        </button>
      </div>

      {/* Botón Chat (Mensajes con admin) — encima de Telegram */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'chat' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Mensajes con admin{chatUnread > 0 ? ` · ${chatUnread} sin leer` : ''}
          </div>
        )}
        <button
          onClick={onOpenChat}
          onMouseEnter={() => setShowTooltip('chat')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #e08c1e 0%, #e08c1e 100%)',
            color: 'white',
            position: 'relative'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(245,158,11,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
          aria-label="Mensajes con admin"
        >
          💬
          {chatUnread > 0 && (
            <div style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '20px',
              height: '20px',
              padding: '0 5px',
              background: '#d23b34',
              borderRadius: 10,
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              color: 'white',
              fontWeight: 'bold'
            }}>{chatUnread > 99 ? '99+' : chatUnread}</div>
          )}
        </button>
      </div>

      {/* Botón Telegram */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'telegram' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Configurar Telegram
            {!telegramVinculado && <span style={{color: C.naranja, marginLeft: '6px'}}>⚠️</span>}
          </div>
        )}
        <button
          onClick={onOpenTelegram}
          onMouseEnter={() => setShowTooltip('telegram')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #0088cc 0%, #0077b5 100%)',
            color: 'white',
            position: 'relative'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,136,204,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
          </svg>
          {!telegramVinculado && (
            <div style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '16px',
              height: '16px',
              background: '#d23b34',
              borderRadius: '50%',
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: 'white',
              fontWeight: 'bold'
            }}>!</div>
          )}
        </button>
      </div>

      {/* Botón Dimo Phone (vinculación del móvil) */}
      {onOpenDimoPhone && (
        <div style={{position: 'relative'}}>
          {showTooltip === 'dimophone' && (
            <div style={{
              position: 'absolute',
              right: '68px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#1f2a37',
              color: 'white',
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: '13px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              Dimo Phone · llamadas en el CRM
              {!dimoVinculado && <span style={{color: C.naranja, marginLeft: '6px'}}>⚠️</span>}
            </div>
          )}
          <button
            onClick={onOpenDimoPhone}
            onMouseEnter={() => setShowTooltip('dimophone')}
            onMouseLeave={() => setShowTooltip(null)}
            style={{
              ...buttonStyle,
              background: 'linear-gradient(135deg, #1f2a37 0%, #005187 100%)',
              color: 'white',
              position: 'relative'
            }}
            onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(30,42,58,0.4)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
            aria-label="Dimo Phone"
          >
            📱
            {!dimoVinculado && (
              <div style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '16px',
                height: '16px',
                background: '#d23b34',
                borderRadius: '50%',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold'
              }}>!</div>
            )}
          </button>
        </div>
      )}

      {/* Botón Asistente IA */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'asistente' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Mi Asistente IA
          </div>
        )}
        <button
          onClick={onOpenAsistente}
          onMouseEnter={() => setShowTooltip('asistente')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #005187 0%, #0066a5 100%)',
            color: 'white'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,81,135,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
        >
          🤖
        </button>
      </div>
    </div>
  );
};

// ==================== MODAL WRAPPER PARA ASISTENTE Y TELEGRAM ====================
const FloatingModal = ({ isOpen, onClose, title, children }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Versión minimizada - barra flotante */}
      {isMinimized && (
        <div 
          onClick={() => setIsMinimized(false)}
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            background: GRAD,
            borderRadius: 12,
            boxShadow: '0 20px 60px rgba(15,42,70,0.35)',
            zIndex: 1001,
            cursor: 'pointer',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            animation: 'slideUp 0.2s ease-out'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.25)';
          }}
        >
          <span style={{fontSize: '20px'}}>🤖</span>
          <div>
            <div style={{color: 'white', fontSize: '14px', fontWeight: 600}}>{title}</div>
            <div style={{color: '#a5d4ff', fontSize: '11px'}}>Click para expandir</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{
              marginLeft: '8px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Versión expandida - se oculta pero NO se desmonta cuando está minimizado */}
      <div style={{
        position: 'fixed',
        bottom: '100px',
        right: '24px',
        width: '672px',
        height: '960px',
        maxHeight: 'calc(100vh - 140px)',
        maxWidth: 'calc(100vw - 48px)',
        background: '#ffffff',
        borderRadius: 14,
        boxShadow: '0 20px 60px rgba(15,42,70,0.35)',
        zIndex: 1001,
        display: isMinimized ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'slideUp 0.3s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: GRAD,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{fontSize: '15px', fontWeight: 600}}>{title}</span>
          <div style={{display: 'flex', gap: '8px'}}>
            {/* Botón Minimizar */}
            <button 
              onClick={() => setIsMinimized(true)}
              title="Minimizar"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              ─
            </button>
            {/* Botón Cerrar */}
            <button 
              onClick={onClose}
              title="Cerrar"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.8)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              ×
            </button>
          </div>
        </div>
        {/* Content */}
        <div style={{flex: 1, overflow: 'hidden'}}>
          {children}
        </div>
      </div>
      
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

// ==================== ASISTENTE IA PAGE ====================
const AsistentePage = ({ leads, comercial, isFloating = false, onRefreshLeads }) => {
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: `¡Hola ${comercial.nombre}! 👋 Soy tu asistente personal.\n\nPuedo ayudarte a:\n• Consultar leads y citas\n• ✨ **Crear nuevos leads** (solo para ti)\n• Cambiar estados\n• Agendar citas\n• Añadir notas\n• Redactar emails y WhatsApps\n\n¿En qué puedo ayudarte?` 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = React.useRef(null);

  // Las API keys se gestionan de forma segura a través de Edge Functions

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Plantillas de mensajes según estado del lead
  const generarMensaje = (lead, tipo, canal) => {
    const nombre = lead.nombre || lead.razonSocial?.split(' ')[0] || 'cliente';
    const nombreCompleto = lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Cliente';
    const vehiculo = `${lead.marca || ''} ${lead.modelo || ''}`.trim();
    const comercialNombre = comercial.nombre || 'tu asesor comercial';
    const centro = comercial.centro || 'Grupo Dimoauto';
    
    const plantillas = {
      // Primer contacto / Pendiente
      'Pendiente': {
        email: {
          asunto: `${nombre}, información sobre ${vehiculo} - ${centro}`,
          cuerpo: `Hola ${nombre},

Soy ${comercialNombre} de ${centro}.

He recibido tu interés en el ${vehiculo} y me encantaría poder ayudarte.

¿Te vendría bien que habláramos esta semana? Puedo explicarte todas las opciones disponibles, financiación y ofertas actuales.

¿Qué día te viene mejor?

Un saludo,
${comercialNombre}
${centro}`
        },
        whatsapp: `Hola ${nombre}! 👋

Soy ${comercialNombre} de ${centro}.

Vi que estás interesado en el ${vehiculo}. Me encantaría ayudarte y resolver cualquier duda.

¿Te viene bien que hablemos? 🚗`
      },
      
      // Seguimiento después de contacto
      'Atendido': {
        email: {
          asunto: `Seguimiento: ${vehiculo} - ${centro}`,
          cuerpo: `Hola ${nombre},

Gracias por tu tiempo en nuestra conversación.

Como comentamos, el ${vehiculo} es una excelente opción. Te recuerdo que tenemos condiciones especiales este mes.

¿Quieres que agendemos una visita al concesionario para verlo en persona?

Quedo a tu disposición.

Un saludo,
${comercialNombre}
${centro}`
        },
        whatsapp: `Hola ${nombre}! 😊

¿Qué tal? Te escribo para saber cómo lo llevas con la decisión del ${vehiculo}.

¿Te gustaría venir a verlo? Puedo reservarte una prueba de conducción 🚗

¿Qué día te vendría bien?`
      },
      
      // Recordatorio de cita
      'Citado': {
        email: {
          asunto: `Confirmación de cita - ${centro}`,
          cuerpo: `Hola ${nombre},

Te confirmo nuestra cita para ver el ${vehiculo}.

📍 Centro: ${centro}
👤 Te atenderá: ${comercialNombre}

Si necesitas cambiar la hora o tienes cualquier duda, no dudes en contactarme.

¡Te esperamos!

Un saludo,
${comercialNombre}`
        },
        whatsapp: `Hola ${nombre}! 👋

Te escribo para confirmar nuestra cita para ver el ${vehiculo}.

📍 ${centro}

¿Todo sigue en pie? Si necesitas cambiar algo, dime sin problema 😊`
      },
      
      // Post oferta
      'Ofertado': {
        email: {
          asunto: `Tu presupuesto del ${vehiculo} - ${centro}`,
          cuerpo: `Hola ${nombre},

Espero que hayas tenido tiempo de revisar el presupuesto que te preparé para el ${vehiculo}.

Recuerda que las condiciones actuales tienen validez limitada, así que si tienes cualquier duda o quieres que revisemos algo, estoy a tu disposición.

¿Hablamos esta semana?

Un saludo,
${comercialNombre}
${centro}`
        },
        whatsapp: `Hola ${nombre}! 😊

¿Pudiste revisar el presupuesto del ${vehiculo}?

Si tienes alguna duda o quieres que ajustemos algo, cuéntame 👍

Las condiciones actuales son muy buenas, no querría que las perdieras 🚗`
      },
      
      // Felicitación por compra
      'Vendido': {
        email: {
          asunto: `¡Enhorabuena por tu nuevo ${vehiculo}! - ${centro}`,
          cuerpo: `Hola ${nombre},

¡Enhorabuena por tu nuevo ${vehiculo}! 🎉

Ha sido un placer atenderte. Recuerda que para cualquier cosa que necesites, aquí estoy.

Te agradecería mucho si pudieras dejarnos una reseña en Google, nos ayuda muchísimo:
[Enlace a reseña]

¡Disfruta de tu nuevo coche!

Un abrazo,
${comercialNombre}
${centro}`
        },
        whatsapp: `¡Hola ${nombre}! 🎉

¡Enhorabuena por tu nuevo ${vehiculo}! Ha sido un placer ayudarte.

Cuando lo tengas, ¿me mandas una foto? 📸

Para cualquier cosa, aquí me tienes 👋`
      }
    };
    
    // Estado por defecto si no existe plantilla específica
    const estadoKey = plantillas[lead.estado] ? lead.estado : 'Pendiente';
    const plantilla = plantillas[estadoKey];
    
    return canal === 'email' ? plantilla.email : plantilla.whatsapp;
  };

  // Buscar lead por nombre o teléfono
  const findLead = (query) => {
    const q = query.toLowerCase();
    return leads.find(l => {
      const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.toLowerCase();
      const razon = (l.razonSocial || '').toLowerCase();
      const tel = l.telefono || l.movil || '';
      return nombre.includes(q) || razon.includes(q) || tel.includes(q);
    });
  };

  // Ejecutar acción en Supabase
  const executeAction = async (action) => {
    try {
      let response;
      const now = new Date().toISOString();
      
      
      switch (action.funcion) {
        case 'CAMBIAR_ESTADO': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              estado_cita: action.params.nuevo_estado,
              fecha_modificacion: now
            })
          });
          
          
          if (response.ok || response.status === 204) {
            if (onRefreshLeads) onRefreshLeads();
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Estado actualizado\n\n👤 ${nombreLead}\n📊 Nuevo estado: ${action.params.nuevo_estado}` };
          } else {
            const errorText = await response.text();
            secureLog.error('apiResponse', errorText);
            return { success: false, msg: `❌ Error al actualizar: ${response.status}` };
          }
        }
        
        case 'AGENDAR_CITA': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          // Validar que tenemos fecha
          if (!action.params.fecha) {
            return { success: false, msg: `❌ Necesito una fecha para agendar la cita.\n\nEjemplo: "Agenda cita con ${action.params.nombre_lead} para mañana a las 10:00"` };
          }
          
          const leadId = lead.id || lead.lead_id;
          let fechaCita = action.params.fecha;
          
          // Añadir hora si se proporciona, si no usar 09:00 por defecto
          if (action.params.hora) {
            fechaCita = `${action.params.fecha}T${action.params.hora}:00`;
          } else {
            fechaCita = `${action.params.fecha}T09:00:00`;
          }
          
          // Validar que la fecha es válida
          const fechaObj = new Date(fechaCita);
          if (isNaN(fechaObj.getTime())) {
            return { success: false, msg: `❌ La fecha "${action.params.fecha}" no es válida.\n\nUsa formato: YYYY-MM-DD (ej: 2025-01-20)` };
          }
          
          
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              estado_cita: 'Citado',
              fecha_agendada: fechaCita,
              tipo_agenda: 'cita',
              fecha_modificacion: now
            })
          });
          
          
          if (response.ok || response.status === 204) {
            if (onRefreshLeads) onRefreshLeads();
            const fechaFormateada = fechaObj.toLocaleString('es-ES', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Cita agendada\n\n👤 ${nombreLead}\n📅 ${fechaFormateada}\n📊 Estado: Citado` };
          } else {
            const errorText = await response.text();
            secureLog.error('agendandoCita', errorText);
            return { success: false, msg: `❌ Error al agendar cita: ${response.status}` };
          }
        }
        
        case 'AGREGAR_NOTA': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          const ahora = new Date().toLocaleString('es-ES');
          let observaciones = lead.observaciones || '';
          if (observaciones) observaciones += '\n\n';
          observaciones += `[${ahora}] ${action.params.nota}`;
          
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              observaciones: observaciones,
              fecha_modificacion: now
            })
          });
          
          if (response.ok || response.status === 204) {
            if (onRefreshLeads) onRefreshLeads();
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Nota añadida\n\n👤 ${nombreLead}\n📝 ${action.params.nota}` };
          } else {
            return { success: false, msg: `❌ Error al añadir nota: ${response.status}` };
          }
        }
        
        case 'CANCELAR_LEAD': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              estado_cita: 'Cancelada Comercial',
              motivo_no_compra: action.params.motivo || 'Sin especificar',
              fecha_modificacion: now
            })
          });
          
          if (response.ok || response.status === 204) {
            if (onRefreshLeads) onRefreshLeads();
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Lead cancelado\n\n👤 ${nombreLead}\n❌ Motivo: ${action.params.motivo || 'Sin especificar'}` };
          } else {
            return { success: false, msg: `❌ Error al cancelar: ${response.status}` };
          }
        }
        
        case 'BUSCAR_LEAD': {
          const q = (action.params.query || '').toLowerCase();
          const encontrados = leads.filter(l => {
            const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.toLowerCase();
            const razon = (l.razonSocial || '').toLowerCase();
            const tel = l.telefono || l.movil || '';
            const marca = (l.marca || '').toLowerCase();
            const modelo = (l.modelo || '').toLowerCase();
            return nombre.includes(q) || razon.includes(q) || tel.includes(q) || marca.includes(q) || modelo.includes(q);
          }).slice(0, 5);
          
          if (encontrados.length === 0) {
            return { success: true, msg: `🔍 No encontré leads con "${action.params.query}"` };
          }
          
          let msg = `🔍 Encontré ${encontrados.length} lead${encontrados.length > 1 ? 's' : ''}:\n\n`;
          encontrados.forEach((l, i) => {
            const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.razonSocial || 'Sin nombre';
            msg += `${i + 1}. ${nombre}\n`;
            msg += `   🚗 ${l.marca || ''} ${l.modelo || ''}\n`;
            msg += `   📞 ${l.telefono || l.movil || 'Sin tel'}\n`;
            msg += `   📊 ${l.estado_cita || l.estado || 'Sin estado'}\n\n`;
          });
          return { success: true, msg };
        }
        
        case 'REDACTAR_MENSAJE': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const canal = (action.params.canal || 'whatsapp').toLowerCase();
          const nombreLead = lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Cliente';
          const telefono = lead.movil || lead.telefono || '';
          const email = lead.email || '';
          const mensaje = generarMensaje(lead, action.params.tipo, canal);
          
          if (canal === 'email') {
            if (!email) {
              return { success: false, msg: `❌ ${nombreLead} no tiene email registrado` };
            }
            return { 
              success: true, 
              msg: `✉️ **Email para ${nombreLead}**\n\n**Asunto:** ${mensaje.asunto}\n\n**Mensaje:**\n${mensaje.cuerpo}`,
              actionButton: {
                type: 'email',
                email: email,
                subject: mensaje.asunto,
                body: mensaje.cuerpo,
                label: `📧 Enviar Email a ${nombreLead}`
              }
            };
          } else {
            if (!telefono) {
              return { success: false, msg: `❌ ${nombreLead} no tiene teléfono registrado` };
            }
            return { 
              success: true, 
              msg: `💬 **WhatsApp para ${nombreLead}**\n\n${mensaje}`,
              actionButton: {
                type: 'whatsapp',
                phone: telefono,
                text: mensaje,
                label: `💬 Enviar WhatsApp a ${nombreLead}`
              }
            };
          }
        }
        
        case 'CREAR_LEAD': {
          // Validar datos mínimos
          if (!action.params.nombre && !action.params.apellidos) {
            return { success: false, msg: '❌ Necesito al menos el nombre del cliente' };
          }
          if (!action.params.telefono && !action.params.movil) {
            return { success: false, msg: '❌ Necesito al menos un teléfono de contacto' };
          }
          
          // Crear el lead asignado al comercial actual
          const nuevoLead = {
            lead_id: `lead-${Date.now()}`,
            lead_tipo: action.params.tipo || 'Lead Válido',
            origen: action.params.origen || 'Visita Espontánea',
            estado_cita: 'Pendiente',
            usuario_alta: comercial?.nombre || 'Sistema',
            fecha_creacion: now,
            vendedor_nombre: comercial?.nombre || '',
            centro: comercial?.centro || '',
            nombre: action.params.nombre || '',
            apellidos: action.params.apellidos || '',
            tipo_cliente: action.params.tipo_cliente || 'Particular',
            telefono: action.params.telefono || '',
            movil: action.params.movil || '',
            email: action.params.email || '',
            marca: action.params.marca || '',
            modelo: action.params.modelo || '',
            tipo_venta: action.params.tipo_venta || 'VN',
            observaciones: action.params.observaciones || ''
          };
          
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(nuevoLead)
          });
          
          if (response.ok) {
            if (onRefreshLeads) onRefreshLeads();
            const nombreCompleto = `${action.params.nombre || ''} ${action.params.apellidos || ''}`.trim();
            let msg = `✅ Lead creado correctamente\n\n`;
            msg += `👤 ${nombreCompleto}\n`;
            if (action.params.telefono || action.params.movil) {
              msg += `📞 ${action.params.telefono || action.params.movil}\n`;
            }
            if (action.params.marca || action.params.modelo) {
              msg += `🚗 ${action.params.marca || ''} ${action.params.modelo || ''}\n`;
            }
            msg += `📊 Estado: Pendiente\n`;
            msg += `👨‍💼 Asignado a: ${comercial?.nombre}`;
            return { success: true, msg };
          } else {
            const errorText = await response.text();
            secureLog.error('creandoLead', errorText);
            return { success: false, msg: `❌ Error al crear el lead: ${response.status}` };
          }
        }
        
        default:
          return { success: false, msg: `❌ Función "${action.funcion}" no reconocida` };
      }
      
      return { success: false, msg: '❌ Error al ejecutar la acción' };
    } catch (error) {
      secureLog.error('ejecutandoAccion', error);
      return { success: false, msg: `❌ Error de conexión: ${error.message}` };
    }
  };

  // Construir contexto de leads para el asistente
  const buildLeadsContext = () => {
    const pendientes = leads.filter(l => (l.estado_cita || l.estado) === 'Pendiente').length;
    const enCurso = leads.filter(l => (l.estado_cita || l.estado) === 'En curso').length;
    const citados = leads.filter(l => (l.estado_cita || l.estado) === 'Citado').length;
    const vendidos = leads.filter(l => (l.estado_cita || l.estado) === 'Vendido').length;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);
    
    const citasHoy = leads.filter(l => {
      const fecha = l.fecha_agendada || l.citaAgendada;
      if (!fecha) return false;
      const cita = new Date(fecha);
      return cita >= hoy && cita < manana;
    });
    
    const leadsResumen = leads.slice(0, 20).map(l => ({
      id: l.id,
      nombre: `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.razonSocial || 'Sin nombre',
      telefono: l.telefono || l.movil || 'Sin tel',
      estado: l.estado_cita || l.estado,
      marca: l.marca || '',
      modelo: l.modelo || '',
      cita: l.fecha_agendada || l.citaAgendada || null
    }));

    return `
COMERCIAL: ${comercial.nombre}
Centro: ${comercial.centro || 'No asignado'}

RESUMEN:
- Total: ${leads.length}
- Pendientes: ${pendientes}
- En curso: ${enCurso}
- Citados: ${citados}
- Vendidos: ${vendidos}
- Citas hoy: ${citasHoy.length}

CITAS HOY:
${citasHoy.length > 0 ? citasHoy.map(c => `- ${c.nombre || c.razonSocial || ''} (${c.marca || ''} ${c.modelo || ''}) Tel: ${c.telefono || c.movil || 'N/A'}`).join('\n') : 'Ninguna'}

LEADS RECIENTES:
${JSON.stringify(leadsResumen)}`;
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Construir el system prompt
      const systemPrompt = `Eres el asistente personal de ventas de ${comercial.nombre}, un comercial de Grupo Dimoauto (concesionario de coches). Ayudas a gestionar leads.

FECHA Y HORA ACTUAL:
- Hoy es: ${new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
- Fecha ISO: ${new Date().toISOString().split('T')[0]}
- Dia de la semana: ${['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'][new Date().getDay()]}
- Numero del dia: ${new Date().getDay()} (0=domingo, 1=lunes, 2=martes, 3=miercoles, 4=jueves, 5=viernes, 6=sabado)

FUNCIONES DISPONIBLES (responde SOLO con JSON cuando el usuario pida una accion):

1. AGENDAR_CITA - Programar cita con fecha y hora (TAMBIEN cambia estado a Citado automaticamente)
   IMPORTANTE: SOLO usa esta funcion si el usuario proporciona una FECHA CONCRETA
   Si el usuario pide "cita" o "llamar" pero NO dice cuando, PREGUNTA: "¿Para qué día y hora quieres la cita con [nombre]?"
   JSON: {"funcion": "AGENDAR_CITA", "params": {"nombre_lead": "...", "fecha": "YYYY-MM-DD", "hora": "HH:MM"}}
   
   CALCULO DE FECHAS (MUY IMPORTANTE - calcula con precision):
   Hoy es ${new Date().toLocaleDateString('es-ES', { weekday: 'long' })} ${new Date().getDate()}/${new Date().getMonth()+1}/${new Date().getFullYear()}
   
   Para calcular dias de la semana, suma dias a la fecha de hoy:
   - Si hoy es lunes (1) y piden "viernes" -> sumar 4 dias
   - Si hoy es martes (2) y piden "viernes" -> sumar 3 dias
   - Si hoy es miercoles (3) y piden "viernes" -> sumar 2 dias
   - Si hoy es jueves (4) y piden "viernes" -> sumar 1 dia
   - Si hoy es viernes (5) y piden "viernes" -> es hoy o sumar 7 dias (proximo viernes)
   
   Ejemplo concreto de HOY: Si hoy es ${new Date().toLocaleDateString('es-ES', { weekday: 'long' })} ${new Date().getDate()}:
   - "mañana" = ${new Date(Date.now() + 86400000).toISOString().split('T')[0]}
   - "pasado mañana" = ${new Date(Date.now() + 172800000).toISOString().split('T')[0]}
   ${(() => {
     const hoy = new Date();
     const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
     let resultado = '';
     for (let i = 1; i <= 7; i++) {
       const fecha = new Date(hoy);
       fecha.setDate(hoy.getDate() + i);
       resultado += `   - "${diasSemana[fecha.getDay()]}" = ${fecha.toISOString().split('T')[0]}\n`;
     }
     return resultado;
   })()}
   
   Ejemplos:
   - "cita con Juan mañana a las 11" -> calcular fecha de mañana y usar AGENDAR_CITA
   - "cita con Maria el viernes a las 10" -> calcular fecha del proximo viernes segun la tabla de arriba
   - "pon cita con Pedro" (sin fecha) -> PREGUNTAR la fecha, NO usar JSON

2. CAMBIAR_ESTADO - Cambiar estado de un lead (solo si NO hay fecha/hora)
   Estados validos: Pendiente, En curso, Atendida, Citado, Ofertado, Vendido, Cancelada, Cancelada Comercial, Nurturing
   JSON: {"funcion": "CAMBIAR_ESTADO", "params": {"nombre_lead": "...", "nuevo_estado": "..."}}

3. AGREGAR_NOTA - Anadir observacion a un lead
   JSON: {"funcion": "AGREGAR_NOTA", "params": {"nombre_lead": "...", "nota": "..."}}

4. CANCELAR_LEAD - Cancelar lead con motivo
   Motivos: Sin interes, No contesta, Precio elevado, Compro competencia, Otro
   JSON: {"funcion": "CANCELAR_LEAD", "params": {"nombre_lead": "...", "motivo": "..."}}

5. BUSCAR_LEAD - Buscar lead por nombre/telefono/vehiculo
   JSON: {"funcion": "BUSCAR_LEAD", "params": {"query": "..."}}

6. REDACTAR_MENSAJE - Redactar email o WhatsApp para un lead (segun su estado)
   Usa cuando el usuario pida: escribir, redactar, enviar, mensaje, email, whatsapp, contactar
   JSON: {"funcion": "REDACTAR_MENSAJE", "params": {"nombre_lead": "...", "canal": "email|whatsapp"}}
   Ejemplos:
   - "redacta un email para Juan" -> {"funcion": "REDACTAR_MENSAJE", "params": {"nombre_lead": "Juan", "canal": "email"}}
   - "escribe un whatsapp a Maria" -> {"funcion": "REDACTAR_MENSAJE", "params": {"nombre_lead": "Maria", "canal": "whatsapp"}}
   - "contacta con Pedro por whatsapp" -> {"funcion": "REDACTAR_MENSAJE", "params": {"nombre_lead": "Pedro", "canal": "whatsapp"}}

7. CREAR_LEAD - Crear un nuevo lead (se asigna automaticamente a ti)
   Usa cuando el usuario pida: crear lead, nuevo lead, añadir cliente, registrar cliente, dar de alta
   IMPORTANTE: Solo puedes crear leads para ti mismo, no para otros vendedores
   Campos requeridos: nombre y telefono (minimo uno de cada)
   JSON: {"funcion": "CREAR_LEAD", "params": {"nombre": "...", "apellidos": "...", "telefono": "...", "movil": "...", "email": "...", "marca": "...", "modelo": "...", "origen": "...", "observaciones": "..."}}
   Origenes disponibles: Visita Espontanea, Llamada Entrante, Referido, Evento/Feria, Redes Sociales, Conocido/Familiar, Cliente Anterior, Otro
   Ejemplos:
   - "crea un lead para Juan Garcia telefono 666111222" -> {"funcion": "CREAR_LEAD", "params": {"nombre": "Juan", "apellidos": "Garcia", "telefono": "666111222"}}
   - "nuevo cliente Maria Lopez 655444333 interesada en Peugeot 3008" -> {"funcion": "CREAR_LEAD", "params": {"nombre": "Maria", "apellidos": "Lopez", "movil": "655444333", "marca": "Peugeot", "modelo": "3008"}}
   - "añade lead Pedro, movil 677888999, viene de visita espontanea" -> {"funcion": "CREAR_LEAD", "params": {"nombre": "Pedro", "movil": "677888999", "origen": "Visita Espontanea"}}

REGLAS IMPORTANTES:
- Si piden CITA sin fecha concreta -> PREGUNTA "¿Para qué día y hora?" (no uses JSON)
- Si piden CITA con fecha concreta -> usa AGENDAR_CITA con JSON y CALCULA BIEN LA FECHA usando la tabla de arriba
- Para CONSULTAS normales, responde en texto (sin JSON)
- Para ACCIONES con datos completos, responde SOLO el JSON (sin explicacion)
- Se MUY conciso (max 3-4 lineas)
- Usa emojis

${buildLeadsContext()}`;

      // Preparar mensajes para la Edge Function
      const chatMessages = [
        ...messages.slice(-6).map(m => ({
          role: m.role,
          content: m.content
        })),
        { role: 'user', content: userMessage }
      ];

      // Llamar a la Edge Function de OpenAI
      const response = await fetch(`${SUPABASE_URL}/functions/v1/dimogpt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'chat',
          payload: {
            messages: chatMessages,
            systemPrompt: systemPrompt
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error en la API');
      }

      const data = await response.json();
      let respuesta = data.content || 'Lo siento, no pude procesar tu solicitud.';
      
      
      // Intentar parsear como JSON (es una acción)
      try {
        let jsonStr = respuesta.trim();
        if (jsonStr.startsWith('```json')) jsonStr = jsonStr.replace(/```json\n?/, '').replace(/```$/, '');
        if (jsonStr.startsWith('```')) jsonStr = jsonStr.replace(/```\n?/, '').replace(/```$/, '');
        
        const action = JSON.parse(jsonStr);
        
        if (action.funcion) {
          // Ejecutar la acción
          setMessages(prev => [...prev, { role: 'assistant', content: '⏳ Ejecutando...' }]);
          const resultado = await executeAction(action);
          // Reemplazar el mensaje "Ejecutando..." con el resultado (incluye actionButton si existe)
          setMessages(prev => [...prev.slice(0, -1), { 
            role: 'assistant', 
            content: resultado.msg,
            actionButton: resultado.actionButton || null
          }]);
          return;
        }
      } catch (e) {
        // No es JSON, es respuesta normal
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: respuesta }]);
    } catch (error) {
      secureLog.error('errorCompleto', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `⚠️ Error: ${error.message}\n\nInténtalo de nuevo.` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sugerencias = [
    '¿Cómo voy?',
    '¿Citas de hoy?',
    'Crear nuevo lead',
    'Redacta WhatsApp'
  ];

  const containerStyle = isFloating ? {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff'
  } : {
    padding: '24px',
    height: 'calc(100vh - 130px)',
    display: 'flex',
    flexDirection: 'column',
    background: '#f4f5f7'
  };

  return (
    <div style={containerStyle}>
      {!isFloating && (
        <div style={{marginBottom: '16px'}}>
          <h2 style={{margin: 0, color: '#1f2a37', fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px'}}>
            🤖 Mi Asistente IA
            <span style={{fontSize: '12px', fontWeight: 400, color: '#8a929c', background: '#e7e9ec', padding: '4px 10px', borderRadius: 12}}>Beta</span>
          </h2>
          <p style={{margin: '8px 0 0 0', color: '#8a929c', fontSize: '13px'}}>Tu asistente personal para gestionar leads y citas</p>
        </div>
      )}

      {/* Chat Container */}
      <div style={{flex: 1, background: '#ffffff', borderRadius: isFloating ? '0' : '12px', border: isFloating ? 'none' : '1px solid #e7e9ec', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: isFloating ? 'none' : '0 1px 3px rgba(0,0,0,0.1)'}}>
        {/* Messages */}
        <div style={{flex: 1, overflowY: 'auto', padding: isFloating ? '16px' : '20px'}}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{marginBottom: '12px', display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'}}>
              <div style={{
                maxWidth: '85%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: msg.role === 'user' ? '#005187' : '#f4f5f7',
                color: msg.role === 'user' ? '#ffffff' : '#1f2a37',
                fontSize: '13px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap'
              }}>
                {msg.content}
                {/* Botón de acción (Email o WhatsApp) */}
                {msg.actionButton && (
                  <div style={{marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e7e9ec'}}>
                    {msg.actionButton.type === 'email' ? (
                      <a
                        href={`mailto:${msg.actionButton.email}?subject=${encodeURIComponent(msg.actionButton.subject)}&body=${encodeURIComponent(msg.actionButton.body)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #005187 0%, #1a9cd4 100%)',
                          color: 'white',
                          borderRadius: 10,
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontWeight: 600,
                          boxShadow: '0 3px 10px rgba(0,81,135,.28)'
                        }}
                        className="fg-card"
                      >
                        {msg.actionButton.label}
                      </a>
                    ) : (
                      <a
                        href={`https://wa.me/${(msg.actionButton.phone || '').replace(/\D/g, '').replace(/^(?!34)/, '34')}?text=${encodeURIComponent(msg.actionButton.text)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                          color: 'white',
                          borderRadius: 10,
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontWeight: 600,
                          boxShadow: '0 3px 10px rgba(46,158,91,.28)'
                        }}
                        className="fg-card"
                      >
                        {msg.actionButton.label}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div style={{display: 'flex', justifyContent: 'flex-start', marginBottom: '12px'}}>
              <div style={{padding: '10px 14px', borderRadius: '14px 14px 14px 4px', background: '#f4f5f7', color: '#8a929c', fontSize: '13px'}}>
                <span style={{animation: 'pulse 1s infinite'}}>●</span> Pensando...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Sugerencias */}
        {messages.length <= 1 && (
          <div style={{padding: '8px 16px', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
            {sugerencias.map((sug, idx) => (
              <button
                key={idx}
                onClick={() => setInput(sug)}
                style={{padding: '6px 12px', background: '#f4f5f7', border: '1px solid #e7e9ec', borderRadius: 14, fontSize: '11px', color: '#2b333d', cursor: 'pointer'}}
              >
                {sug}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{padding: '12px 16px', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '10px'}}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Escribe tu pregunta..."
            disabled={isLoading}
            style={{flex: 1, padding: '10px 14px', borderRadius: 999, border: `1.5px solid ${C.line}`, background: '#f4f5f7', fontSize: '13px', outline: 'none'}}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            style={{padding: '10px 18px', background: isLoading ? '#9aa4af' : '#005187', color: '#ffffff', border: 'none', borderRadius: 999, fontSize: '13px', fontWeight: 600, cursor: isLoading ? 'wait' : 'pointer'}}
          >
            {isLoading ? '...' : '➤'}
          </button>
        </div>
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
};

// ==================== TELEGRAM CONFIG PAGE ====================
// ==================== DIMO PHONE — VINCULACIÓN DEL COMERCIAL ====================
// Cada comercial ve solo SU estado de vinculación y su propio QR si aún no ha vinculado.
const DIMO_DOMINIOS = ['dimoauto.com', 'jpcarrion.com', 'maam.es'];
const DIMO_RE_EMAIL = new RegExp('^[^@\\s]+@(' + DIMO_DOMINIOS.join('|').replace(/\./g, '\\.') + ')$', 'i');

let dimoQrLibPromise = null;
const dimoCargarLibQR = () => {
  if (window.QRCode) return Promise.resolve();
  if (!dimoQrLibPromise) {
    dimoQrLibPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
      s.onload = resolve;
      s.onerror = () => { dimoQrLibPromise = null; reject(new Error('Sin conexión a la librería QR')); };
      document.head.appendChild(s);
    });
  }
  return dimoQrLibPromise;
};

const dimoNuevoToken = () => {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a).map(b => b.toString(16).padStart(2, '0')).join('');
};

const dimoBuildLink = (tk, email, nombre) =>
  `dimophone://pair?token=${encodeURIComponent(tk)}&email=${encodeURIComponent(email)}&nombre=${encodeURIComponent(nombre)}`;

const dimoHace = (f) => {
  if (!f) return null;
  const min = Math.round((Date.now() - new Date(f).getTime()) / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} día${Math.round(h / 24) > 1 ? 's' : ''}`;
};

// El instalador se sirve desde el propio dominio del CRM (public/DimoPhone.apk),
// así que el comercial lo descarga sin que nadie le pase nada por WhatsApp.
const DIMO_APK_PATH = '/DimoPhone.apk';
const dimoApkUrl = () => `${window.location.origin}${DIMO_APK_PATH}`;

// QR pintado sobre la librería que ya carga dimoCargarLibQR. Si cdnjs no
// responde, avisa en vez de dejar un hueco en blanco.
const DimoQR = ({ texto, size = 170 }) => {
  const ref = useRef(null);
  const [fallo, setFallo] = useState(false);
  useEffect(() => {
    if (!texto || !ref.current) return undefined;
    let vivo = true;
    dimoCargarLibQR().then(() => {
      if (!vivo || !ref.current) return;
      ref.current.innerHTML = '';
      // eslint-disable-next-line no-undef
      new window.QRCode(ref.current, { text: texto, width: size, height: size, correctLevel: window.QRCode.CorrectLevel.M });
      setFallo(false);
    }).catch(() => { if (vivo) setFallo(true); });
    return () => { vivo = false; };
  }, [texto, size]);
  if (fallo) {
    return (
      <div style={{ fontSize: 12, color: C.naranja, background: `${C.naranja}14`, border: '1px solid #f5dfae', borderRadius: 10, padding: '9px 12px' }}>
        No se ha podido dibujar el QR (sin conexión con cdnjs). Usa el botón de descarga.
      </div>
    );
  }
  return <div ref={ref} style={{ display: 'inline-block', background: '#fff', padding: 10, borderRadius: 12, border: '1px solid #e7e9ec', lineHeight: 0 }} />;
};

// Paso previo a vincular: instalar la app. Se enseña mientras no haya móvil
// vinculado; una vez vinculado sobra y desaparece.
const DimoDescargaApp = ({ compacto = false, mini = false }) => {
  // mini = ya tiene un código pendiente, o sea que la app ya se la ha bajado:
  // el paso de instalar se queda en una línea para no comerse la pantalla.
  if (mini) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: '#f4f5f7', border: '1px solid #e7e9ec', borderRadius: 10,
        padding: '8px 12px', marginBottom: 10,
      }}>
        <span style={{ fontSize: 12, color: '#8a929c', flex: 1, minWidth: 150 }}>
          ¿Aún no tienes la app instalada?
        </span>
        <a href={DIMO_APK_PATH} download="DimoPhone.apk" style={{
          padding: '7px 13px', borderRadius: 8, background: '#005187', color: '#fff',
          fontSize: 12.5, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap',
        }}>⬇ Descargar APK</a>
      </div>
    );
  }
  return (
    <div style={{ background: '#f4f5f7', border: '1px solid #e7e9ec', borderRadius: 12, padding: '11px 13px', marginBottom: 11, textAlign: 'center' }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.brand, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
        1 · Instala la app en tu móvil
      </div>
      {!compacto && <DimoQR texto={dimoApkUrl()} size={118} />}
      <div style={{ fontSize: 12, color: '#8a929c', margin: compacto ? '0 0 9px' : '7px 0 9px', lineHeight: 1.4 }}>
        {compacto
          ? 'Pulsa para descargar el instalador en este móvil.'
          : 'Escanea el QR con la cámara del móvil, o pulsa el botón desde el propio móvil.'}
      </div>
      <a href={DIMO_APK_PATH} download="DimoPhone.apk" style={{
        display: 'block', padding: '10px', borderRadius: 10, background: '#005187', color: '#fff',
        fontSize: 13.5, fontWeight: 700, textDecoration: 'none',
      }}>⬇ Descargar Dimo Phone (APK)</a>
      <div style={{ fontSize: 11, color: '#8a929c', marginTop: 7, lineHeight: 1.4 }}>
        Android puede pedirte permitir «instalar apps desconocidas»: acéptalo. Después
        concede el permiso de <b>registro de llamadas</b>.
      </div>
    </div>
  );
};

// ── Aviso automático: los 5 primeros días desde el primer login ────────────
// La fecha de arranque se guarda por comercial en el navegador la primera vez
// que se le enseñaría, así que cada uno tiene su propia ventana de 5 días.
const DIMO_AVISO_DIAS = 5;
const dimoAvisoKey = (email) => `dimophone_aviso_inicio_${(email || '').toLowerCase()}`;

const useDimoAviso = (comercial, vinculado, listo) => {
  const [mostrar, setMostrar] = useState(false);
  useEffect(() => {
    const email = comercial?.email;
    if (!email || !listo || vinculado) return undefined;  // ya lo tiene: no se le molesta
    let inicio;
    try {
      const k = dimoAvisoKey(email);
      inicio = window.localStorage.getItem(k);
      if (!inicio) { inicio = new Date().toISOString(); window.localStorage.setItem(k, inicio); }
    } catch (e) { inicio = new Date().toISOString(); }
    const dias = (Date.now() - new Date(inicio).getTime()) / 86400000;
    if (!(dias >= 0 && dias <= DIMO_AVISO_DIAS)) return undefined;
    const t = setTimeout(() => setMostrar(true), 1200);
    return () => clearTimeout(t);
  }, [comercial, vinculado, listo]);
  return [mostrar, () => setMostrar(false)];
};

const DimoPhoneAviso = ({ onAbrir, onCerrar, movil = false }) => (
  <div style={{
    position: 'fixed',
    // En escritorio abajo a la IZQUIERDA: a la derecha están los botones
    // flotantes y el aviso de tareas.
    ...(movil ? { left: 12, right: 12, bottom: 86 } : { left: 20, bottom: 20, maxWidth: 330 }),
    background: '#fff', borderRadius: 14, border: `2px solid ${C.brand}`,
    boxShadow: '0 12px 32px rgba(0,0,0,0.22)', padding: '16px 18px', zIndex: 1200,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span style={{ fontSize: 26 }}>📱</span>
      <strong style={{ color: C.brand, fontSize: 15 }}>Vincula tu móvil al CRM</strong>
      <button onClick={onCerrar} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, color: '#9aa4af', cursor: 'pointer', lineHeight: 1 }}>×</button>
    </div>
    <div style={{ fontSize: 13, color: '#8a929c', lineHeight: 1.5, marginBottom: 12 }}>
      Instala <b>Dimo Phone</b> y vincula tu móvil: tus llamadas se registran solas en la
      ficha de cada cliente, sin tener que apuntar nada.
    </div>
    {!movil && (
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <DimoQR texto={dimoApkUrl()} size={112} />
        <div style={{ fontSize: 11.5, color: '#9aa4af', marginTop: 6 }}>Escanea para descargar la app</div>
      </div>
    )}
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      <button onClick={onCerrar} style={{ padding: '8px 14px', borderRadius: 9, border: '1px solid #e7e9ec', background: '#fff', color: '#2b333d', fontSize: 13, cursor: 'pointer' }}>Ahora no</button>
      <button onClick={onAbrir} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: C.brand, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Vincular mi móvil</button>
    </div>
  </div>
);

// Hook ligero: ¿tiene el comercial un móvil vinculado? (para el aviso del botón flotante)
const useDimoVinculado = (email) => {
  const [vinculado, setVinculado] = useState(true); // true por defecto: sin aviso hasta saberlo
  useEffect(() => {
    if (!email) return undefined;
    let vivo = true;
    const check = () => {
      fetch(`${SUPABASE_URL}/rest/v1/dimo_phone_dispositivos?user_email=eq.${encodeURIComponent(email)}&estado=eq.vinculado&select=id&limit=1`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      }).then(r => r.ok ? r.json() : null)
        .then(d => { if (vivo && Array.isArray(d)) setVinculado(d.length > 0); })
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 60000);
    return () => { vivo = false; clearInterval(t); };
  }, [email]);
  return vinculado;
};

const DimoPhoneComercialPanel = ({ comercial }) => {
  const [dispositivos, setDispositivos] = useState(null); // null = cargando
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);
  const qrRef = useRef(null);

  const email = (comercial?.email || '').trim();
  const nombreCompleto = `${comercial?.nombre || ''} ${comercial?.apellidos || ''}`.trim() || email;

  const cargar = useCallback(() => {
    if (!email) { setDispositivos([]); return; }
    const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` };
    fetch(`${SUPABASE_URL}/rest/v1/dimo_phone_dispositivos?user_email=eq.${encodeURIComponent(email)}&order=creado_en.desc&select=*`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setDispositivos(Array.isArray(d) ? d : []))
      .catch(() => setDispositivos([]));
  }, [email]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 8000); // detecta la vinculación en cuanto ocurre
    return () => clearInterval(t);
  }, [cargar]);

  const vinculado = (dispositivos || []).find(d => d.estado === 'vinculado') || null;
  const pendiente = !vinculado ? ((dispositivos || []).find(d => d.estado === 'pendiente') || null) : null;
  const link = pendiente ? dimoBuildLink(pendiente.token, pendiente.user_email, pendiente.user_nombre) : null;

  // Pinta el QR del código pendiente
  useEffect(() => {
    if (!link || !qrRef.current) return undefined;
    let vivo = true;
    dimoCargarLibQR().then(() => {
      if (!vivo || !qrRef.current) return;
      qrRef.current.innerHTML = '';
      new window.QRCode(qrRef.current, { text: link, width: 150, height: 150, correctLevel: window.QRCode.CorrectLevel.M });
    }).catch(() => {});
    return () => { vivo = false; };
  }, [link]);

  const generarCodigo = async () => {
    setError('');
    if (!email) { setError('Tu usuario no tiene email configurado. Pide a administración que lo complete.'); return; }
    if (!DIMO_RE_EMAIL.test(email)) { setError(`Tu email (${email}) no es corporativo (@${DIMO_DOMINIOS.join(', @')}). Pide a administración que lo corrija.`); return; }
    if (!nombreCompleto || nombreCompleto.length < 3) { setError('Tu usuario no tiene nombre configurado. Pide a administración que lo complete.'); return; }
    setGenerando(true);
    try {
      const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };
      // Revocar códigos pendientes anteriores
      await fetch(`${SUPABASE_URL}/rest/v1/dimo_phone_dispositivos?user_email=eq.${encodeURIComponent(email)}&estado=eq.pendiente`, {
        method: 'PATCH', headers, body: JSON.stringify({ estado: 'revocado' }),
      }).catch(() => {});
      const r = await fetch(`${SUPABASE_URL}/rest/v1/dimo_phone_dispositivos`, {
        method: 'POST', headers: { ...headers, Prefer: 'return=minimal' },
        body: JSON.stringify({ user_email: email, user_nombre: nombreCompleto, token: dimoNuevoToken(), estado: 'pendiente' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      cargar();
    } catch (e) {
      setError('No se pudo generar el código. Inténtalo de nuevo o avisa a administración.');
    } finally {
      setGenerando(false);
    }
  };

  const copiarCodigo = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    }).catch(() => {});
  };

  const caja = { background: '#f4f5f7', border: '1px solid #e7e9ec', borderRadius: 12, padding: '16px', textAlign: 'center' };

  return (
    // maxHeight + overflow: la ventana flotante del hub tiene alto fijo y corta
    // lo que sobra, así que el scroll tiene que vivir aquí dentro.
    <div style={{ fontFamily: 'inherit', maxHeight: '100%', overflowY: 'auto', padding: '2px 2px 6px', boxSizing: 'border-box' }}>
      <p style={{ fontSize: 12.5, color: '#8a929c', marginTop: 0, marginBottom: 10, lineHeight: 1.45 }}>
        <b>Dimo Phone</b> registra automáticamente tus llamadas de trabajo en el CRM y las asocia a tus leads.
      </p>

      {dispositivos === null && (
        <div style={{ ...caja, color: '#8a929c', fontSize: 13 }}>Comprobando tu estado de vinculación…</div>
      )}

      {dispositivos !== null && vinculado && (
        <div style={{ ...caja, background: `${C.verde}0d`, border: `1px solid ${C.verde}55` }}>
          <div style={{ fontSize: 34, marginBottom: 6 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.verde }}>Tu móvil está vinculado</div>
          <div style={{ fontSize: 13, color: '#2b333d', marginTop: 8, lineHeight: 1.6 }}>
            {vinculado.dispositivo && <div>📱 {vinculado.dispositivo}</div>}
            {vinculado.vinculado_en && <div>Vinculado el {new Date(vinculado.vinculado_en).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>}
            <div>
              Última sincronización: {vinculado.ultima_sync
                ? `${dimoHace(vinculado.ultima_sync)}`
                : 'todavía ninguna'}
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#8a929c', marginTop: 10 }}>
            Si cambias de móvil, revoca este y genera un código nuevo desde aquí mismo.
          </div>
        </div>
      )}

      {dispositivos !== null && !vinculado && <DimoDescargaApp mini={!!pendiente} />}

      {dispositivos !== null && !vinculado && pendiente && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.naranja, background: `${C.naranja}14`, border: '1px solid #f5dfae', borderRadius: 10, padding: '7px 12px', marginBottom: 10 }}>
            ⏳ Tienes un código pendiente de vincular
          </div>
          <div ref={qrRef} style={{ display: 'inline-block', background: '#fff', padding: 10, borderRadius: 12, border: '1px solid #e7e9ec' }} />
          <div style={{ fontSize: 12, color: '#8a929c', margin: '8px 0', lineHeight: 1.45 }}>
            Escanea el QR con la cámara del móvil donde tengas instalado <b>Dimo Phone</b>…
            <br />o, si estás leyendo esto desde ese móvil, pulsa el botón:
          </div>
          <a href={link} style={{
            display: 'block', padding: '13px', borderRadius: 10, background: '#005187', color: '#fff',
            fontSize: 14.5, fontWeight: 600, textDecoration: 'none', marginBottom: 10,
          }}>📲 Vincular este móvil</a>
          <button onClick={copiarCodigo} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#2b333d' }}>
            {copiado ? '¡Copiado!' : 'Copiar código para pegarlo en la app'}
          </button>
          <button onClick={generarCodigo} disabled={generando} style={{ marginTop: 10, background: 'none', border: 'none', color: '#8a929c', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            {generando ? 'Generando…' : 'El código no funciona · generar uno nuevo'}
          </button>
        </div>
      )}

      {dispositivos !== null && !vinculado && !pendiente && (
        <div style={caja}>
          <div style={{ fontSize: 34, marginBottom: 6 }}>📵</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2a37' }}>Aún no has vinculado tu móvil</div>
          <div style={{ fontSize: 13, color: '#8a929c', marginTop: 6, lineHeight: 1.5 }}>
            Genera tu código, instala <b>Dimo Phone</b> en tu móvil de trabajo y escanea el QR.
          </div>
          <button onClick={generarCodigo} disabled={generando} style={{
            marginTop: 14, width: '100%', padding: '13px', borderRadius: 10, border: 'none',
            background: generando ? '#9aa4af' : '#005187', color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: generando ? 'default' : 'pointer',
          }}>
            {generando ? 'Generando…' : 'Generar mi código de vinculación'}
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 10, fontSize: 13, background: `${C.rojo}0d`, border: `1px solid ${C.rojo}55`, color: '#d23b34' }}>
          {error}
        </div>
      )}
    </div>
  );
};

const TelegramConfigPage = ({ comercial, onUpdateComercial, isFloating = false }) => {
  const [telegramConfig, setTelegramConfig] = useState({
    activo: comercial.telegram_activo || false,
    horaResumen: comercial.telegram_hora_resumen || '09:00',
    chatId: comercial.telegram_chat_id || null,
    codigoVinculacion: comercial.telegram_codigo_vinculacion || null,
    notifCitas: comercial.telegram_notif_citas !== false, // default true
    notifMinutos: comercial.telegram_notif_minutos || 5
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const TELEGRAM_BOT_URL = 'https://t.me/Dimoautoasistenteleadsbot';

  // Generar código de vinculación único
  const generarCodigoVinculacion = async () => {
    setGeneratingCode(true);
    const codigo = `DIMO-${comercial.id}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    
    try {
      const { error } = await supabase.from('comerciales').update({
        telegram_codigo_vinculacion: codigo
      }).eq('id', comercial.id);
      
      if (error) throw error;
      
      setTelegramConfig(prev => ({ ...prev, codigoVinculacion: codigo }));
      if (onUpdateComercial) {
        onUpdateComercial({ ...comercial, telegram_codigo_vinculacion: codigo });
      }
    } catch (error) {
      secureLog.error('generandoCodigo', error);
      alert('Error al generar código. Inténtalo de nuevo.');
    } finally {
      setGeneratingCode(false);
    }
  };

  // Guardar configuración
  const guardarConfiguracion = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from('comerciales').update({
        telegram_activo: telegramConfig.activo,
        telegram_hora_resumen: telegramConfig.horaResumen,
        telegram_resumen_diario: telegramConfig.activo,
        telegram_notif_citas: telegramConfig.notifCitas,
        telegram_notif_minutos: telegramConfig.notifMinutos
      }).eq('id', comercial.id);
      
      if (error) throw error;
      
      if (onUpdateComercial) {
        onUpdateComercial({ 
          ...comercial, 
          telegram_activo: telegramConfig.activo,
          telegram_hora_resumen: telegramConfig.horaResumen,
          telegram_resumen_diario: telegramConfig.activo,
          telegram_notif_citas: telegramConfig.notifCitas,
          telegram_notif_minutos: telegramConfig.notifMinutos
        });
      }
      alert('✅ Configuración guardada');
    } catch (error) {
      secureLog.error('guardando', error);
      alert('Error al guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard.writeText(telegramConfig.codigoVinculacion);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const vinculado = !!telegramConfig.chatId;

  const containerStyle = isFloating ? {
    height: '100%',
    overflowY: 'auto',
    background: '#ffffff'
  } : {
    padding: '24px',
    height: 'calc(100vh - 130px)',
    overflowY: 'auto',
    background: '#f4f5f7'
  };

  return (
    <div style={containerStyle}>
      <div style={{maxWidth: isFloating ? '100%' : '600px', margin: '0 auto', padding: isFloating ? '16px' : '0'}}>
        {!isFloating && (
          <>
            <h2 style={{margin: '0 0 8px 0', color: '#1f2a37', fontSize: '20px', fontWeight: 700}}>
              📱 Configurar Telegram
            </h2>
            <p style={{margin: '0 0 24px 0', color: '#8a929c', fontSize: '13px'}}>
              Recibe recordatorios y consulta tus leads desde Telegram
            </p>
          </>
        )}

        {/* Descripción introductoria */}
        <div style={{background: `${C.brand}0d`, borderRadius: 10, padding: '14px 16px', marginBottom: '16px', border: `1px solid ${C.brand}33`}}>
          <div style={{fontSize: '13px', color: '#1e40af', lineHeight: '1.5'}}>
            📲 <strong>Vincula tu Telegram</strong> para recibir notificaciones en tu móvil: recordatorios de citas, leads pendientes y resúmenes diarios sin abrir la aplicación.
          </div>
        </div>

        {/* Estado de vinculación */}
        <div style={{background: vinculado ? `${C.verde}14` : `${C.naranja}14`, borderRadius: 10, padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px'}}>
          <span style={{fontSize: '24px'}}>{vinculado ? '✅' : '⏳'}</span>
          <div>
            <div style={{fontSize: '14px', fontWeight: 600, color: vinculado ? C.verde : C.naranja}}>
              {vinculado ? 'Telegram vinculado' : 'No vinculado'}
            </div>
            <div style={{fontSize: '12px', color: vinculado ? C.verde : '#a16207'}}>
              {vinculado ? 'Listo para recibir notificaciones' : 'Sigue los pasos para vincular'}
            </div>
          </div>
        </div>

        {!vinculado && (
          <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #e7e9ec'}}>
            <div style={{fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '14px'}}>Pasos para vincular:</div>
            
            {/* Paso 1 */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '14px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0}}>1</div>
              <div>
                <div style={{fontSize: '12px', fontWeight: 500, color: '#1f2a37', marginBottom: '6px'}}>Abre el bot en Telegram</div>
                <a 
                  href={TELEGRAM_BOT_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#0088cc', color: 'white', borderRadius: 10, textDecoration: 'none', fontSize: '12px', fontWeight: 500}}
                >
                  📱 Abrir Bot
                </a>
              </div>
            </div>

            {/* Paso 2 */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '14px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0}}>2</div>
              <div style={{fontSize: '12px', color: '#2b333d'}}>
                Pulsa <strong>"Iniciar"</strong> en el bot
              </div>
            </div>

            {/* Paso 3 */}
            <div style={{display: 'flex', gap: '10px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0}}>3</div>
              <div style={{flex: 1}}>
                <div style={{fontSize: '12px', fontWeight: 500, color: '#1f2a37', marginBottom: '8px'}}>Envía este código:</div>
                {telegramConfig.codigoVinculacion ? (
                  <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                    <code style={{flex: 1, padding: '10px 12px', background: '#1f2a37', color: '#2e9e5b', borderRadius: 10, fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px'}}>
                      {telegramConfig.codigoVinculacion}
                    </code>
                    <button 
                      onClick={copiarCodigo}
                      style={{padding: '10px 12px', background: copied ? '#2e9e5b' : '#005187', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: '11px', fontWeight: 500}}
                    >
                      {copied ? '✓' : '📋'}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={generarCodigoVinculacion}
                    disabled={generatingCode}
                    style={{padding: '10px 16px', background: generatingCode ? '#9aa4af' : '#005187', color: 'white', border: 'none', borderRadius: 10, cursor: generatingCode ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 500, width: '100%'}}
                  >
                    {generatingCode ? '⏳ Generando...' : '🔑 Generar código'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Configuración de notificaciones */}
        <div style={{background: '#ffffff', borderRadius: 10, border: '1px solid #e7e9ec', padding: '16px', marginBottom: '16px', opacity: vinculado ? 1 : 0.5}}>
          <div style={{fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '14px'}}>🔔 Notificaciones</div>

          {/* Toggle recordatorios */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px'}}>
            <div>
              <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Resumen diario</div>
              <div style={{fontSize: '11px', color: '#8a929c'}}>Leads pendientes y citas del día</div>
            </div>
            <button
              onClick={() => setTelegramConfig(prev => ({ ...prev, activo: !prev.activo }))}
              disabled={!vinculado}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: 12,
                border: 'none',
                background: telegramConfig.activo ? '#2e9e5b' : '#e7e9ec',
                cursor: vinculado ? 'pointer' : 'not-allowed',
                position: 'relative'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '3px',
                left: telegramConfig.activo ? '25px' : '3px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }} />
            </button>
          </div>

          {/* Hora del resumen */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
              <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Hora del resumen</div>
            </div>
            <input
              type="time"
              value={telegramConfig.horaResumen}
              onChange={(e) => setTelegramConfig(prev => ({ ...prev, horaResumen: e.target.value }))}
              disabled={!vinculado || !telegramConfig.activo}
              style={{padding: '6px 10px', borderRadius: 10, border: `1.5px solid ${C.line}`, fontSize: '13px', background: (!vinculado || !telegramConfig.activo) ? '#f4f5f7' : 'white'}}
            />
          </div>
        </div>

        {/* Sección Notificaciones de Citas */}
        <div style={{marginBottom: '16px', padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
          <div style={{fontSize: '13px', color: '#8a929c', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            🔔 Notificaciones de Citas
          </div>

          {/* Toggle notificaciones de citas */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px'}}>
            <div>
              <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Recordatorios de citas</div>
              <div style={{fontSize: '11px', color: '#8a929c'}}>Recibe alertas antes de tus citas</div>
            </div>
            <button
              onClick={() => setTelegramConfig(prev => ({ ...prev, notifCitas: !prev.notifCitas }))}
              disabled={!vinculado}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: 12,
                border: 'none',
                background: telegramConfig.notifCitas ? '#2e9e5b' : '#e7e9ec',
                cursor: vinculado ? 'pointer' : 'not-allowed',
                position: 'relative'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '3px',
                left: telegramConfig.notifCitas ? '25px' : '3px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }} />
            </button>
          </div>

          {/* Selector de minutos de antelación */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
              <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Avisar con antelación</div>
              <div style={{fontSize: '11px', color: '#8a929c'}}>Minutos antes de la cita</div>
            </div>
            <select
              value={telegramConfig.notifMinutos}
              onChange={(e) => setTelegramConfig(prev => ({ ...prev, notifMinutos: parseInt(e.target.value) }))}
              disabled={!vinculado || !telegramConfig.notifCitas}
              style={{
                padding: '6px 12px',
                borderRadius: 10,
                border: `1.5px solid ${C.line}`,
                fontSize: '13px',
                background: (!vinculado || !telegramConfig.notifCitas) ? '#f4f5f7' : 'white',
                cursor: (!vinculado || !telegramConfig.notifCitas) ? 'not-allowed' : 'pointer'
              }}
            >
              <option value={0}>En el momento</option>
              <option value={5}>5 minutos</option>
              <option value={10}>10 minutos</option>
              <option value={15}>15 minutos</option>
              <option value={30}>30 minutos</option>
              <option value={60}>1 hora</option>
            </select>
          </div>
        </div>

        {/* Botón guardar */}
        <button
          onClick={guardarConfiguracion}
          disabled={saving || !vinculado}
          style={{
            width: '100%',
            padding: '12px 20px',
            background: saving ? '#9aa4af' : '#005187',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontSize: '14px',
            fontWeight: 600,
            cursor: (saving || !vinculado) ? 'not-allowed' : 'pointer',
            opacity: vinculado ? 1 : 0.5
          }}
        >
          {saving ? '⏳ Guardando...' : '💾 Guardar'}
        </button>

        {/* Info de funciones disponibles */}
        <div style={{marginTop: '16px', padding: '14px 16px', background: `${C.verde}0d`, borderRadius: 10, border: '1px solid #d3ecdd'}}>
          <div style={{fontSize: '13px', color: C.verde, fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            💡 Funciones disponibles en Telegram
          </div>
          <div style={{fontSize: '12px', color: C.verde, lineHeight: '1.8'}}>
            <div style={{marginBottom: '4px'}}>✅ Resumen diario automático de leads y citas</div>
            <div style={{marginBottom: '4px'}}>✅ Consultas con IA: "¿Cuántos leads tengo?" "¿Qué citas tengo hoy?"</div>
            <div style={{marginBottom: '4px'}}>✅ Gestión de leads: cambiar estado, agendar citas, añadir notas</div>
            <div style={{marginBottom: '4px'}}>✅ <strong>Crear leads:</strong> "Crea lead Juan García 666111222"</div>
            <div style={{marginBottom: '4px'}}>✅ <strong>Contactar:</strong> "WhatsApp a María" (genera enlace)</div>
            <div style={{marginBottom: '4px'}}>✅ 🔔 Recordatorios de citas (configurable)</div>
            <div>✅ Alertas de leads sin contactar (+2 días)</div>
          </div>
          
          {/* Botón descargar guía */}
          <a 
            href="/Guia_Uso_Asistente_Telegram_Dimoauto.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '14px',
              padding: '10px 16px',
              background: `linear-gradient(135deg, ${C.verde} 0%, ${C.verde} 100%)`,
              color: 'white',
              borderRadius: 10,
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: '0 2px 4px rgba(22, 101, 52, 0.2)',
              transition: 'all 0.2s ease'
            }}
          >
            📄 Descargar Guía de Uso (PDF)
          </a>
        </div>
      </div>
    </div>
  );
};

// ==================== MODAL NUEVO LEAD COMERCIAL ====================
const NuevoLeadModalComercial = ({ onClose, onAddLead, comercial }) => {
  const [formData, setFormData] = useState({
    tipo: 'Lead Válido',
    origen: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    movil: '',
    email: '',
    marca: '',
    modelo: '',
    matricula: '',
    tipoCliente: 'Particular',
    tipoVenta: 'VN',
    observaciones: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validación básica
    if (!formData.nombre.trim() && !formData.apellidos.trim()) {
      setError('Introduce al menos el nombre o apellidos del cliente');
      return;
    }
    if (!formData.origen) {
      setError('Selecciona el origen del lead');
      return;
    }
    // Validar campos obligatorios para duplicados
    if (!formData.movil.trim()) {
      setError('El campo Móvil es obligatorio');
      return;
    }
    if (!formData.marca) {
      setError('El campo Marca es obligatorio');
      return;
    }

    setSaving(true);
    try {
      secureLog.dev('Iniciando validación de duplicados...');
      
      // Verificar si ya existe un lead con el mismo móvil y marca
      const { exists, lead: existingLead } = await checkDuplicateLead(formData.movil, formData.marca);
      
      if (exists) {
        const fechaCreacion = existingLead.fecha_creacion ? new Date(existingLead.fecha_creacion).toLocaleDateString('es-ES') : 'N/A';
        setError(`⚠️ LEAD DUPLICADO (MÓVIL + MARCA)\n\n` +
          `Ya existe un lead con este móvil (${formData.movil}) para la marca ${formData.marca}.\n\n` +
          `👤 Cliente: ${existingLead.nombre || ''} ${existingLead.apellidos || ''}\n` +
          `📱 Vendedor: ${existingLead.vendedor_nombre || 'Sin asignar'}\n` +
          `📊 Estado: ${existingLead.estado_cita || 'Pendiente'}\n` +
          `📅 Creado: ${fechaCreacion}`);
        setSaving(false);
        return;
      }

      // Verificar si ya existe un lead con el mismo email y marca
      if (formData.email && formData.marca) {
        const { exists: emailExists, lead: emailLead } = await checkDuplicateByEmail(formData.email, formData.marca);
        
        if (emailExists) {
          const fechaCreacion = emailLead.fecha_creacion ? new Date(emailLead.fecha_creacion).toLocaleDateString('es-ES') : 'N/A';
          setError(`⚠️ LEAD DUPLICADO (EMAIL + MARCA)\n\n` +
            `Ya existe un lead con este email (${formData.email}) para la marca ${formData.marca}.\n\n` +
            `👤 Cliente: ${emailLead.nombre || ''} ${emailLead.apellidos || ''}\n` +
            `📧 Email: ${emailLead.email || ''}\n` +
            `📱 Vendedor: ${emailLead.vendedor_nombre || 'Sin asignar'}\n` +
            `📊 Estado: ${emailLead.estado_cita || 'Pendiente'}\n` +
            `📅 Creado: ${fechaCreacion}`);
          setSaving(false);
          return;
        }
      }
      
      secureLog.dev('No hay duplicados, creando lead...');

      // Preparar el lead para Supabase - asignado automáticamente al comercial actual
      const supabaseLead = {
        lead_id: `lead-${Date.now()}`,
        lead_tipo: formData.tipo,
        origen: formData.origen,
        estado_cita: 'Pendiente',
        usuario_alta: comercial?.nombre || 'Sistema',
        fecha_creacion: new Date().toISOString(),
        vendedor_nombre: comercial?.nombre || '',
        centro: comercial?.centro || '',
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        tipo_cliente: formData.tipoCliente,
        telefono: formData.telefono,
        movil: formData.movil,
        email: formData.email,
        marca: formData.marca,
        modelo: formData.modelo,
        matricula: formData.matricula || null,
        tipo_venta: formData.tipoVenta,
        observaciones: formData.observaciones
      };

      const result = await insertLead(supabaseLead);
      
      if (result.error) {
        throw new Error('Error al guardar el lead');
      }

      if (onAddLead) {
        onAddLead(supabaseLead);
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error al guardar el lead');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #e7e9ec',
    fontSize: '13px',
    background: 'white',
    color: '#1f2a37',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#2b333d',
    marginBottom: '6px'
  };

  const sectionStyle = {
    background: '#f4f5f7',
    borderRadius: 10,
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid #e7e9ec'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15,28,43,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: 14,
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(15,42,70,0.35)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: GRAD,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{fontSize: '16px', fontWeight: 700}}>➕ Nuevo Lead</div>
            <div style={{fontSize: '12px', opacity: 0.9, marginTop: '2px'}}>
              Asignado a: {comercial?.nombre} • {comercial?.centro}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{flex: 1, overflowY: 'auto', padding: '20px'}}>
          {saved && (
            <div style={{
              background: `${C.verde}14`,
              color: C.verde,
              padding: '12px 16px',
              borderRadius: 10,
              marginBottom: '16px',
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '14px'
            }}>
              ✅ Lead guardado correctamente
            </div>
          )}

          {error && (
            <div style={{
              background: `${C.rojo}0d`,
              color: '#d23b34',
              padding: '12px 16px',
              borderRadius: 10,
              marginBottom: '16px',
              fontSize: '13px'
            }}>
              ❌ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Información del Lead */}
            <div style={sectionStyle}>
              <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', fontWeight: 700}}>
                📋 Información del Lead
              </h4>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px'}}>
                <div>
                  <label style={labelStyle}>Tipo de Lead</label>
                  <select
                    style={inputStyle}
                    value={formData.tipo}
                    onChange={e => setFormData({...formData, tipo: e.target.value})}
                  >
                    {TIPOS_LEAD.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Origen *</label>
                  <select
                    style={inputStyle}
                    value={formData.origen}
                    onChange={e => setFormData({...formData, origen: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar origen...</option>
                    {ORIGENES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Tipo Venta</label>
                  <select
                    style={inputStyle}
                    value={formData.tipoVenta}
                    onChange={e => setFormData({...formData, tipoVenta: e.target.value})}
                  >
                    {TIPOS_VENTA.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Datos del Cliente */}
            <div style={sectionStyle}>
              <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', fontWeight: 700}}>
                👤 Datos del Cliente
              </h4>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                <div>
                  <label style={labelStyle}>Nombre *</label>
                  <input
                    style={inputStyle}
                    value={formData.nombre}
                    onChange={e => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Nombre"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Apellidos</label>
                  <input
                    style={inputStyle}
                    value={formData.apellidos}
                    onChange={e => setFormData({...formData, apellidos: e.target.value})}
                    placeholder="Apellidos"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Teléfono</label>
                  <input
                    style={inputStyle}
                    value={formData.telefono}
                    onChange={e => setFormData({...formData, telefono: e.target.value})}
                    placeholder="Teléfono fijo"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Móvil *</label>
                  <input
                    style={inputStyle}
                    value={formData.movil}
                    onChange={e => setFormData({...formData, movil: e.target.value})}
                    placeholder="Teléfono móvil"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    style={inputStyle}
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Tipo Cliente</label>
                  <select
                    style={inputStyle}
                    value={formData.tipoCliente}
                    onChange={e => setFormData({...formData, tipoCliente: e.target.value})}
                  >
                    {TIPOS_CLIENTE.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Vehículo de Interés */}
            <div style={sectionStyle}>
              <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', fontWeight: 700}}>
                🚗 Vehículo de Interés
              </h4>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                <div>
                  <label style={labelStyle}>Marca *</label>
                  <select
                    style={inputStyle}
                    value={formData.marca}
                    onChange={e => setFormData({...formData, marca: e.target.value})}
                  >
                    <option value="">Seleccionar...</option>
                    {MARCAS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Modelo</label>
                  <input
                    style={inputStyle}
                    value={formData.modelo}
                    onChange={e => setFormData({...formData, modelo: e.target.value})}
                    placeholder="Modelo del vehículo"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Matrícula</label>
                  <input
                    style={inputStyle}
                    value={formData.matricula}
                    onChange={e => setFormData({...formData, matricula: e.target.value.toUpperCase()})}
                    placeholder="Ej: 1234ABC"
                  />
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div style={sectionStyle}>
              <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', fontWeight: 700}}>
                📝 Observaciones
              </h4>
              <textarea
                style={{...inputStyle, minHeight: '80px', resize: 'vertical'}}
                value={formData.observaciones}
                onChange={e => setFormData({...formData, observaciones: e.target.value})}
                placeholder="Notas adicionales sobre el cliente o su interés..."
              />
            </div>

            {/* Botones */}
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px'}}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  background: '#8a929c',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  background: saving ? '#9aa4af' : '#228B22',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {saving ? '⏳ Guardando...' : '💾 Guardar Lead'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Loading Screen - Estilo azul con logo Dimoauto
// Pantalla de carga: en vez de un spinner, el esqueleto de lo que va a salir
// (cabecera, pestañas, cifras y lista), con el shimmer del sistema.
const LoadingScreen = () => (
  <div style={{minHeight: '100vh', background: C.bg, padding: '16px 20px'}}>
    <div className="fg-shimmer" style={{height: 46, borderRadius: 12, marginBottom: 12}} />
    <div className="fg-shimmer" style={{height: 38, borderRadius: 12, marginBottom: 14, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto'}} />
    <div style={{display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap'}}>
      {[0, 1, 2, 3].map(i => <div key={i} className="fg-shimmer" style={{height: 74, borderRadius: 12, flex: '1 1 165px'}} />)}
    </div>
    {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="fg-shimmer" style={{height: 62, borderRadius: 12, marginBottom: 8}} />)}
  </div>
);

// ==================== APP PRINCIPAL CON DETECCIÓN DE DISPOSITIVO ====================
export default function App() {
  const isMobile = useIsMobile();
  const [comercial, setComercial] = useState(null);
  const [comerciales, setComerciales] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('inicio');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAsistente, setShowAsistente] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatPopupShown, setChatPopupShown] = useState(false);
  const chatUnreadCount = useChatUnread(comercial?.email || '');
  // Dimo Phone click-to-call: recordar quién ha iniciado sesión
  useEffect(() => { dimoSetUsuario(comercial); }, [comercial]);
  const [showTelegram, setShowTelegram] = useState(false);
  const [showDimoPhone, setShowDimoPhone] = useState(false);
  const dimoVinculado = useDimoVinculado(comercial?.email || '');
  const [dimoAviso, cerrarDimoAviso] = useDimoAviso(comercial, dimoVinculado, !!comercial?.email);
  const [showNuevoLeadModal, setShowNuevoLeadModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const comercialesData = await fetchComerciales();
      if (comercialesData && comercialesData.length > 0) {
        setComerciales(comercialesData);
      }

      const leadsRows = await fetchLeads();
      if (leadsRows) {
        const leadsData = transformSupabaseLeads(leadsRows);
        setLeads(leadsData);
      } else {
        throw new Error('No se pudieron cargar los leads');
      }
    } catch (err) {
      secureLog.error('error', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Detectar si el usuario viene de un enlace de recuperación de contraseña
    // Soporta flujo implícito (#access_token) y PKCE (?code)
    const checkRecoveryToken = async () => {
      // Flujo implícito: #access_token=xxx&type=recovery
      const hash = window.location.hash;
      if (hash && hash.includes('access_token') && hash.includes('type=recovery')) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('access_token');
        if (token) {
          setRecoveryToken(token);
          setShowResetPasswordModal(true);
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
      }
      // Flujo PKCE: ?code=xxx (Supabase moderno)
      const search = window.location.search;
      if (search) {
        const params = new URLSearchParams(search);
        const code = params.get('code');
        if (code) {
          try {
            const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`, {
              method: 'POST',
              headers: {
                'apikey': SUPABASE_ANON_KEY,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ auth_code: code })
            });
            if (response.ok) {
              const data = await response.json();
              if (data.access_token) {
                setRecoveryToken(data.access_token);
                setShowResetPasswordModal(true);
                window.history.replaceState(null, '', window.location.pathname);
              }
            }
          } catch (err) { /* silently fail */ }
        }
      }
    };
    
    checkRecoveryToken();
    
    const checkSession = async () => {
      // ❌ AUTO-LOGIN DESACTIVADO POR SEGURIDAD
      // Ya NO verificamos sesión automáticamente
      // El usuario DEBE hacer clic en "Iniciar sesión" cada vez
      
      // Solo cargar datos de configuración (sin auto-login)
    };
    
    checkSession();
    loadData();
    
    // Real-time subscription
    let ws = null;
    let reconnectTimeout = null;
    
    const connectRealtime = () => {
      const wsUrl = SUPABASE_URL.replace('https://', 'wss://') + '/realtime/v1/websocket?apikey=' + SUPABASE_ANON_KEY + '&vsn=1.0.0';
      
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        secureLog.dev('Real-time conectado');
        const subscribeMsg = {
          topic: 'realtime:public:leads',
          event: 'phx_join',
          payload: {
            config: {
              broadcast: { self: false },
              presence: { key: '' },
              postgres_changes: [{ event: '*', schema: 'public', table: 'leads' }]
            }
          },
          ref: '1'
        };
        ws.send(JSON.stringify(subscribeMsg));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'phx_reply' || data.event === 'presence_state') return;

          // Soportar formato viejo (payload.type/record) y nuevo (payload.data.type/record)
          const changeType = data.payload?.type || data.payload?.data?.type;
          const newRecord = data.payload?.record || data.payload?.data?.record;
          const oldRecord = data.payload?.old_record || data.payload?.data?.old_record;

          if (data.event === 'postgres_changes' || changeType === 'INSERT' || changeType === 'UPDATE' || changeType === 'DELETE') {
            secureLog.dev('Cambio detectado en leads:', changeType || 'UPDATE');

            // FIX: update quirúrgico desde el payload en vez de loadData() global.
            // El refetch global causaba race condition con el optimistic update:
            // si otro lead se modificaba y disparaba loadData() mientras el PATCH
            // del comercial todavía no era visible al lector, el setLeads del
            // refetch sobreescribía el optimistic update con el estado viejo y el
            // lead "volvía" a Pendiente.
            try {
              if ((changeType === 'UPDATE' || changeType === 'INSERT') && newRecord && newRecord.id != null) {
                const transformed = transformSupabaseLeads([newRecord])[0];
                setLeads(prev => {
                  const idx = prev.findIndex(l => l.id === newRecord.id);
                  if (idx === -1) {
                    // INSERT: añadir si no existe
                    return changeType === 'INSERT' ? [transformed, ...prev] : prev;
                  }
                  // UPDATE: reemplazar la fila concreta sin tocar las demás
                  const next = prev.slice();
                  next[idx] = transformed;
                  return next;
                });
                return;
              }
              if (changeType === 'DELETE' && oldRecord && oldRecord.id != null) {
                setLeads(prev => prev.filter(l => l.id !== oldRecord.id));
                return;
              }
            } catch (mergeErr) {
              secureLog.warn('Realtime merge falló, usando refetch', mergeErr);
            }

            // Fallback: si el payload no trae record (cambio de schema, evento
            // ambiguo), refetch completo como antes.
            loadData();
          }
        } catch (e) {}
      };
      
      ws.onerror = (error) => secureLog.warn('Real-time', error);
      
      ws.onclose = () => {
        secureLog.dev('Real-time desconectado');
        reconnectTimeout = setTimeout(() => connectRealtime(), 5000);
      };
      
      const heartbeatInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: Date.now().toString() }));
        }
      }, 30000);
      
      return () => clearInterval(heartbeatInterval);
    };
    
    connectRealtime();
    
    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, []);

  const misLeads = useMemo(() => {
    if (!comercial) return [];
    const norm = (s) => (s || '').toString().toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const nombreComercial = norm(comercial.nombre);
    if (!nombreComercial) return [];
    const out = leads.filter(lead => {
      const vendedor = norm(lead.vendedor);
      if (!vendedor) return false;
      return vendedor === nombreComercial || vendedor.includes(nombreComercial) || nombreComercial.includes(vendedor);
    });
    if (out.length === 0 && leads.length > 0 && process.env.NODE_ENV === 'development') {
      const distinct = [...new Set(leads.map(l => l.vendedor).filter(Boolean))].slice(0, 8);
      console.warn('[misLeads] 0 leads para comercial=' + comercial.nombre + ' aunque hay ' + leads.length + ' cargados. Vendedores distintos:', distinct);
    }
    return out;
  }, [leads, comercial]);

  const clientesCount = useMemo(() => {
    const unique = new Set();
    misLeads.forEach(lead => {
      const key = lead.telefono || lead.movil || lead.email || lead.leadId;
      unique.add(key);
    });
    return unique.size;
  }, [misLeads]);

  const citasCount = useMemo(() => {
    return misLeads.filter(lead => lead.citaAgendada && new Date(lead.citaAgendada) >= new Date()).length;
  }, [misLeads]);

  // El numerito de "Leads" cuenta SOLO los pendientes, no los ya gestionados
  const leadsPendientesCount = useMemo(
    () => misLeads.filter(l => (l.estado || 'Pendiente') === 'Pendiente' && !estaAgendadoFuturo(l)).length,
    [misLeads],
  );

  // Tareas del comercial (lead_tareas — compartidas con el Panel de Adriana)
  const [misTareas, setMisTareas] = useState([]);
  const cargarMisTareas = useCallback(() => {
    if (!comercial?.nombre) return;
    getTareasDeComercial(comercial.nombre)
      .then(ts => setMisTareas(Array.isArray(ts) ? ts : []))
      .catch(() => {});
  }, [comercial?.nombre]);
  useEffect(() => {
    cargarMisTareas();
    const t = setInterval(cargarMisTareas, 120000);
    return () => clearInterval(t);
  }, [cargarMisTareas]);
  const tareasPendientesCount = useMemo(() => misTareas.filter(t => !t.hecha).length, [misTareas]);

  const handleUpdateLead = (leadId, updates) => {
    setLeads(prev => prev.map(lead => 
      lead.id === leadId ? { ...lead, ...updates } : lead
    ));
    if (selectedLead && selectedLead.id === leadId) {
      setSelectedLead(prev => ({ ...prev, ...updates }));
    }
  };

  const handleLogout = async () => {
    await supabaseAuth.signOut();
    setComercial(null);
    setActiveTab('inicio');
  };

  // ============ VERSIÓN MÓVIL ============
  if (isMobile) {
    return (
      <MobileApp
        comercial={comercial}
        comerciales={comerciales}
        leads={leads}
        isLoading={isLoading}
        error={error}
        onLogin={setComercial}
        onLogout={handleLogout}
        onUpdateLead={handleUpdateLead}
        onRefresh={loadData}
      />
    );
  }

  // ============ VERSIÓN DESKTOP (Original) ============
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!comercial) {
    return <LoginScreen onLogin={setComercial} comerciales={comerciales} error={null} />;
  }

  return (
    <div style={{minHeight: '100vh', background: '#f4f5f7', fontFamily: 'inherit'}}>
      <Header comercial={comercial} onLogout={handleLogout} />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} leadsCount={leadsPendientesCount} clientesCount={clientesCount} citasCount={citasCount} tareasCount={tareasPendientesCount} />
      
      {activeTab === 'inicio' && (
        <InicioPage 
          leads={misLeads} 
          onSelectLead={setSelectedLead}
          selectedLead={selectedLead}
          onUpdateLead={handleUpdateLead}
          comercialNombre={comercial.nombre}
          comerciales={comerciales}
          comercialActual={comercial}
          onAbrirDimoPhone={() => { setShowDimoPhone(true); cerrarDimoAviso(); }}
          dimoVinculado={dimoVinculado}
          onTareaCreada={cargarMisTareas}
        />
      )}
      
      {activeTab === 'agenda' && (
        <AgendaPage leads={misLeads} onSelectLead={setSelectedLead} comercial={comercial} onUpdateComercial={setComercial}
          tareas={misTareas}
          onTareaHecha={async (t) => { try { await actualizarTareaCrm(t.id, { hecha: true }); cargarMisTareas(); } catch { alert('No se pudo marcar la tarea.'); } }}
          onAbrirLeadId={(id) => {
            const l = leads.find(x => x.id === id);
            if (l) setSelectedLead(l);
            else alert('Ese lead no está en tu listado (puede ser de otro comercial).');
          }} />
      )}

      {activeTab === 'tareas' && (
        <TareasComercialPage
          comercial={comercial}
          comerciales={comerciales}
          tareas={misTareas}
          onRecargar={cargarMisTareas}
          onAbrirLead={(id) => {
            const l = leads.find(x => x.id === id);
            if (l) setSelectedLead(l);
            else alert('Ese lead no está en tu listado (puede ser de otro comercial).');
          }}
        />
      )}

      {activeTab === 'clientes' && (
        <ClientesPage leads={misLeads} />
      )}

      {selectedLead && activeTab !== 'inicio' && (
        <FichaLeadCrm
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
          comercialNombre={comercial.nombre}
          lista={misLeads}
          onNavegar={(l) => setSelectedLead(l)}
          onTareaCreada={cargarMisTareas}
        />
      )}

      <FloatingButtons
        onOpenAsistente={() => setShowAsistente(true)}
        onOpenTelegram={() => setShowTelegram(true)}
        onOpenNuevoLead={() => setShowNuevoLeadModal(true)}
        telegramVinculado={!!comercial.telegram_chat_id}
        onOpenChat={() => { setShowChat(true); setChatPopupShown(true); }}
        chatUnread={chatUnreadCount}
        onOpenDimoPhone={() => setShowDimoPhone(true)}
        dimoVinculado={dimoVinculado}
      />

      <FloatingModal 
        isOpen={showChat} 
        onClose={() => setShowChat(false)} 
        title="💬 Mensajes con Administración"
      >
        <ChatComercialPanel comercial={comercial} />
      </FloatingModal>

      {/* Popup automático al cargar si hay mensajes sin leer */}
      {!chatPopupShown && chatUnreadCount > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#fff',
          padding: '16px 20px',
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          zIndex: 2000,
          border: '2px solid #e08c1e',
          maxWidth: '340px',
          fontFamily: 'inherit'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
            <span style={{fontSize: '28px'}}>📬</span>
            <strong style={{color: '#005187', fontSize: '15px'}}>
              Tienes {chatUnreadCount} mensaje{chatUnreadCount > 1 ? 's' : ''} sin leer
            </strong>
          </div>
          <div style={{fontSize: '13px', color: '#8a929c', marginBottom: '14px', lineHeight: 1.4}}>
            La administración te ha enviado {chatUnreadCount > 1 ? 'mensajes' : 'un mensaje'}. Pulsa <strong>Abrir chat</strong> para verlo.
          </div>
          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
            <button onClick={() => setChatPopupShown(true)} style={{padding: '7px 14px', border: '1px solid #e7e9ec', background: '#fff', borderRadius: 10, fontSize: '13px', cursor: 'pointer', color: '#2b333d'}}>Cerrar</button>
            <button onClick={() => { setShowChat(true); setChatPopupShown(true); }} style={{padding: '7px 14px', border: 'none', background: '#005187', color: '#fff', borderRadius: 10, fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}>Abrir chat</button>
          </div>
        </div>
      )}

      <FloatingModal 
        isOpen={showAsistente} 
        onClose={() => setShowAsistente(false)} 
        title="🤖 Mi Asistente IA"
      >
        <AsistentePage leads={misLeads} comercial={comercial} isFloating={true} onRefreshLeads={async () => {
          const leadsRows = await fetchLeads();
          if (leadsRows) {
            const leadsData = transformSupabaseLeads(leadsRows);
            setLeads(leadsData);
          }
        }} />
      </FloatingModal>

      <FloatingModal 
        isOpen={showTelegram} 
        onClose={() => setShowTelegram(false)} 
        title="📱 Configurar Telegram"
      >
        <TelegramConfigPage
          comercial={comercial}
          onUpdateComercial={(updated) => setComercial(updated)}
          isFloating={true}
        />
      </FloatingModal>

      <FloatingModal
        isOpen={showDimoPhone}
        onClose={() => setShowDimoPhone(false)}
        title="📱 Dimo Phone"
      >
        <DimoPhoneComercialPanel comercial={comercial} />
      </FloatingModal>

      {dimoAviso && !showDimoPhone && (
        <DimoPhoneAviso
          onAbrir={() => { cerrarDimoAviso(); setShowDimoPhone(true); }}
          onCerrar={cerrarDimoAviso}
        />
      )}

      {showNuevoLeadModal && (
        <NuevoLeadModalComercial
          comercial={comercial}
          onClose={() => setShowNuevoLeadModal(false)}
          onAddLead={async () => {
            // Refrescar los leads después de añadir uno nuevo
            const leadsRows = await fetchLeads();
            if (leadsRows) {
              const leadsData = transformSupabaseLeads(leadsRows);
              setLeads(leadsData);
            }
          }}
        />
      )}

      {/* Modal para restablecer contraseña cuando el usuario viene del email de recuperación */}
      {showResetPasswordModal && (
        <ResetPasswordModal 
          token={recoveryToken} 
          onClose={() => {
            setShowResetPasswordModal(false);
            setRecoveryToken(null);
          }}
        />
      )}
    </div>
  );
}

// ==================== MODAL RESTABLECER CONTRASEÑA ====================
const ResetPasswordModal = ({ token, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    
    // Validar contraseñas
    if (newPassword !== confirmPassword) {
      setMessage('error:Las contraseñas no coinciden');
      return;
    }
    
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setMessage(`error:${validation.message}`);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        setMessage('success:✅ Contraseña actualizada correctamente. Ya puedes iniciar sesión.');
        setTimeout(() => {
          onClose();
        }, 2500);
      } else {
        const errorData = await response.json();
        if (errorData.error_description?.includes('expired') || errorData.msg?.includes('expired')) {
          setMessage('error:El enlace ha expirado. Solicita uno nuevo desde el login.');
        } else {
          setMessage('error:No se pudo actualizar la contraseña. Intenta solicitar un nuevo enlace.');
        }
      }
    } catch (error) {
      setMessage('error:Error de conexión. Intenta de nuevo más tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 10,
    border: '1px solid #e7e9ec',
    fontSize: '14px',
    background: '#f4f5f7',
    color: '#1f2a37',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#8a929c',
    marginBottom: '8px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15,28,43,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: 14,
        padding: '32px',
        maxWidth: '440px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(15,42,70,0.35)'
      }}>
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <span style={{fontSize: '48px'}}>🔐</span>
          <h2 style={{margin: '16px 0 8px 0', color: '#1f2a37', fontSize: '22px'}}>
            Crear nueva contraseña
          </h2>
          <p style={{margin: 0, color: '#8a929c', fontSize: '14px'}}>
            Introduce tu nueva contraseña para restablecer el acceso
          </p>
        </div>

        <form onSubmit={handleResetPassword}>
          <div style={{marginBottom: '20px'}}>
            <label style={labelStyle}>Nueva contraseña</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
            />
            <div style={{fontSize: '11px', color: '#888', marginTop: '6px'}}>
              Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número
            </div>
          </div>

          <div style={{marginBottom: '24px'}}>
            <label style={labelStyle}>Confirmar contraseña</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
            />
          </div>

          {message && (
            <div style={{
              background: message.startsWith('success:') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
              border: `1px solid ${message.startsWith('success:') ? '#2e9e5b' : '#d23b34'}`,
              borderRadius: 10,
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '13px',
              color: message.startsWith('success:') ? C.verde : '#d23b34'
            }}>
              {message.replace('success:', '').replace('error:', '')}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '14px',
              background: isLoading ? '#9aa4af' : 'linear-gradient(135deg, #005187, #1a9cd4)',
              border: 'none',
              borderRadius: 10,
              color: 'white',
              fontSize: '15px',
              fontWeight: 600,
              cursor: isLoading ? 'wait' : 'pointer',
              marginBottom: '12px'
            }}
          >
            {isLoading ? '⏳ Actualizando...' : '✓ Guardar nueva contraseña'}
          </button>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              background: '#f4f5f7',
              border: 'none',
              borderRadius: 10,
              color: '#8a929c',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
        </form>
      </div>
    </div>
  );
};